// Canonical print-portal vocabulary.
//
// This file is the single source of truth for the attribute keys, option
// values, and customer-facing labels used across:
//   - the customer form schema (portal SchemaForm renders)
//   - the Quote.line.config blob (portal API persists)
//   - the Nexus ERP pricing engine (Service / ServiceAttribute / Option
//     records — Nexus's seed mirrors these names verbatim)
//
// Design rules:
//   1. snake_case for all machine identifiers (keys + option values).
//   2. One canonical KEY per concept; labels are customer-friendly and
//      may differ across surfaces, but the key is fixed.
//   3. No mapping/translation layer between the portal config payload and
//      the Nexus pricing request. The form emits exactly what the engine
//      reads.
//
// Cross-references:
//   - Nexus seed: nexus_erp/packages/backend/prisma/seeds/pricingCatalog.data.js
//   - Portal form seed: packages/backend/prisma/seed/customer-form.ts
//   - Pricing contract (wire shape): v2_analysis/v2_PRICING_API_CONTRACT.md
// ── Product types (replaces product_family) ────────────────────────────
// Ordered books-first, then flat, then media, then custom packaging, then the two
// non-print entries — the configurator picker reads in this sequence.
// `custom_packaging` is packaging ordered AS the product, a stock catalog carton
// or a custom-made box; packaging a kit ships in is an `assembly` item's packing
// answer instead. `fulfillment` is a quote-level
// order service only (see ORDER_SERVICES below): it is a thing we do WITH a
// finished product, never a product. `assembly` is the item being bought when it
// IS the kitting/assembly work — the one assembly flow (ADR-020); each assembly
// item holds its own kit. `something_else` captures nothing and routes the job to
// sales to scope.
//
// `perfect_bound_book` is the paperback (glued-spine, perfect-bound) book — its key
// is RETAINED (it is the historical perfect-bound Service) and only its label
// changed to "Paperback Book". `hard_bound_book` is a NEW, separately-priced
// product type (rigid case binding) — the split of the old
// perfect-bound-with-a-hardcover-toggle into two first-class families. Its
// spec/attribute rules mirror the paperback's; the one difference is a longer
// production turnaround (see TURNAROUND_DAYS_BY_PRODUCT_TYPE). Ordered
// portal-first add with a NEW Nexus Service that mirrors perfect_bound.
export const PRODUCT_TYPES = [
    'perfect_bound_book',
    'hard_bound_book',
    'saddle_stitch_book',
    'coil_or_wire_o_book',
    'loose_leaf',
    'single_sheet',
    'labels',
    'media_replication',
    'custom_packaging',
    'assembly',
    'something_else',
];
export const PRODUCT_TYPE_LABELS = {
    perfect_bound_book: 'Paperback Book',
    hard_bound_book: 'Hard-bound Book',
    saddle_stitch_book: 'Saddle-stitched Book',
    coil_or_wire_o_book: 'Coil or Wire-O Book',
    loose_leaf: 'Loose-leaf Binder Content',
    single_sheet: 'Single Sheet (Flyer, Brochure, Postcard)',
    labels: 'Labels & Stickers',
    media_replication: 'Media Replication',
    custom_packaging: 'Custom Packaging',
    assembly: 'Assembly',
    something_else: 'Something else',
};
// Convenience: which product types are "books" for conditional-show rules.
export const BOOK_PRODUCT_TYPES = [
    'saddle_stitch_book',
    'perfect_bound_book',
    'hard_bound_book',
    'coil_or_wire_o_book',
    'loose_leaf',
];
// ── Attribute keys (the only `config` keys the engine reads) ───────────
// All values that flow through quote.line.config and into Nexus's
// pricing engine MUST use one of these keys.
export const ATTRIBUTE_KEYS = {
    product_type: 'product_type',
    // ── Flat-family spec keys (single_sheet, labels) ──────────────────────
    // These remain SINGLE-value picks for flat work. From PrintQuoteV1 v3 the
    // BOOK families no longer use them — books split into the cover_* /
    // interior_* keys below (the configurator's "Cover" and "Inside pages"
    // steps). The keys stay in the vocabulary because flat work still prices
    // on them; books simply stop emitting them.
    finished_size: 'finished_size',
    print_sides: 'print_sides',
    print_color: 'print_color',
    // print_ink collapses print_color + print_sides on FLAT products (single_sheet,
    // labels) into ONE printer-notation value (e.g. 4/4), priced per imaged side
    // (the impression model — a color side costs more than a black one, and each
    // side is an impression). The two keys above are retained append-only for
    // pinned quotes; Wide-Format is Nexus-only and keeps print_sides there.
    print_ink: 'print_ink',
    page_count: 'page_count',
    interior_paper: 'interior_paper',
    cover_paper: 'cover_paper',
    cover_finish: 'cover_finish',
    // Flat-work lamination (single_sheet on a 10pt+ card stock). The customer-
    // facing counterpart to cover_finish for flat pieces — same value space
    // (the collapsed finish+sides domain, COVER_FINISHES / LAMINATION_FINISHES:
    // none | gloss_2s | gloss_1s | matte_2s | matte_1s | soft_touch_2s |
    // soft_touch_1s) but a DISTINCT key, because a FormField is unique per
    // (schema, key) and cover_finish is book-cover-only. Nexus mirrors this as a
    // `lamination` ServiceAttribute on the single_sheet Service, one option row
    // per value (ordered, portal-first add — author here, then sync Nexus).
    lamination: 'lamination',
    // ── Cover / interior spec split (book families) — PrintQuoteV1 v3 ─────
    // The cover ("outside") and interior ("guts") of a book are specced
    // independently. paper is ALREADY split (interior_paper / cover_paper);
    // v3 extends the split to size, color, and sides. Each variant REUSES the
    // existing option-value catalog VERBATIM (no new option values minted) —
    // only the KEYS are new:
    //   *_finished_size  → FINISHED_SIZES values (book_* entries)
    //   cover_ink        → COVER_INKS values (see below). The cover's color +
    //                       sidedness are ONE printer-notation dimension (4/4,
    //                       4/1, ...), replacing the old cover_color +
    //                       cover_print_sides pair, so the cover charge scales
    //                       per imaged side and 4/1 (mixed-sided) is expressible.
    //   interior_color   → PRINT_COLORS values. The INTERIOR (manuscript) also
    //                       offers 'mixed' — a body part color, part black & white
    //                       (mostly text with a few color pages). Nexus's
    //                       interior_color ServiceAttribute must accept 'mixed'
    //                       for every book Service. The cover is NOT 'mixed' — a
    //                       mixed-sided cover is 4/1 via cover_ink.
    //   interior_print_sides → PRINT_SIDES values
    // Nexus hand-off (portal-first, then Nexus): these are NEW ServiceAttribute
    // keys Nexus must add to its book Services, reading the SAME option values.
    // Per-section bleed (cover_bleed / interior_bleed) is captured in the form
    // but is NOT a priced ServiceAttribute (mirrors has_bleed) — see the
    // "undertrim derived from bleed" note below.
    cover_finished_size: 'cover_finished_size',
    interior_finished_size: 'interior_finished_size',
    // cover_ink collapses the legacy cover_color + cover_print_sides pair into ONE
    // printer-notation dimension (COVER_INKS, e.g. 4/4), priced per imaged side.
    cover_ink: 'cover_ink',
    // interior_ink collapses the legacy interior_color + interior_print_sides pair
    // (2026-07-23) into ONE value (INTERIOR_INKS: 4_4/4_0/1_1/1_0 + `mixed`). Book
    // interiors are spec'd in the same N/M notation as the cover; `mixed` is the
    // interior-only 6th value whose color-page count rides on
    // interior_color_page_count. Unlike cover_ink it is a Nexus GANGING INPUT, not a
    // priced delta (interior prices via sheet-ups) — Nexus decodes it to (color,
    // sides). New book quotes emit interior_ink; the pair below is retained
    // append-only for pinned/cloned pre-collapse quotes.
    interior_ink: 'interior_ink',
    interior_color: 'interior_color',
    interior_print_sides: 'interior_print_sides',
    // Count of interior PAGES that print in color, captured ONLY when
    // interior_ink = 'mixed' (a body part color, part B&W). It is the price driver
    // Nexus needs to split the interior impression rate — color pages at the
    // color-click rate, the remainder at the mono rate — instead of guessing a
    // mixed book's color proportion (see the interior_color_page_count seed field
    // + Nexus hand-off). Grain is PAGES, not signatures: customers think in pages,
    // page_count is already captured in pages, and Nexus derives signatures/forms
    // from the page count on its side. NEXUS HAND-OFF (portal-first → Nexus
    // mirror): add this as a ServiceAttribute on every book Service, but it is
    // CONDITIONALLY required (only when interior_ink='mixed') — hence it is in
    // PRODUCT_TYPE_ATTRIBUTES below but deliberately NOT in REQUIRED_ATTRIBUTES.
    interior_color_page_count: 'interior_color_page_count',
    // LEGACY (append-only, no new emitters): retained only so pre-collapse pinned
    // quotes carrying config.cover_color / cover_print_sides read + label back
    // cleanly (cf. cover_binding_style). New book quotes emit cover_ink instead.
    cover_color: 'cover_color',
    cover_print_sides: 'cover_print_sides',
    // `binding` is the two-value spine pick (coil | wire_o), used by
    // coil_or_wire_o_book only. Post-press operations (folding, scoring,
    // three_hole_drilling, corner_stapling, shrinkwrapping) are NOT binding
    // values — they are standalone finishing Services emitted as their own
    // items[] entries (see FINISHING_OPS below).
    binding: 'binding',
    // NOTE: there is no `add_ons` attribute. Specialty finishes
    // (die_cutting/uv_coating/pms_custom_color/embossing/foil_stamping) are
    // finishing Services like the rest. Finishing selections live in
    // `config.finishing` (portal-only line state, NOT an engine-read key)
    // and expand to items[] entries.
    media_type: 'media_type',
    media_packaging: 'media_packaging',
    // What decoration a disc face / USB body carries: none, printed, or
    // laser-etched. Replaces the `media_art_printed` boolean, which could not
    // express etching at all — the old help text lumped "laser-etched / printed"
    // into one flag, so an etched job priced as in-house printing. Etching is an
    // OUT-OF-HOUSE operation (FB-0075), which is why it is an alternative to
    // printing rather than a co-selection, and why it also emits the
    // `laser_etching` Pattern B project (see LASER_ETCHING_FAMILY_KEY).
    media_decoration: 'media_decoration',
    // Sidedness of PRINTED decoration. A DISTINCT key from `print_sides`, not a
    // reuse of it: Nexus's `print_sides` attribute already carries wide-format
    // option deltas, and its options are shared across every Service the
    // attribute is assigned to — so a shared key would make a USB order inherit a
    // wide-format price. Same VALUE domain though (PRINT_SIDES), so Nexus reads
    // one sides shape everywhere.
    media_print_sides: 'media_print_sides',
    // HOW the printed art is put on an OPTICAL disc face — silkscreen or offset
    // litho. A disc is decorated by one of two presses, not by a sidedness: the
    // label side is the only printable face (the other side is the data side), so
    // `media_print_sides` is a USB-BODY question and this is the disc's. They are
    // mutually exclusive by media_type and never both offered.
    media_print_method: 'media_print_method',
    // Custom packaging (CUSTOM_PACKAGING_SOURCES below). The style and board
    // option values are the ones Nexus's historical "Custom Corrugated Box"
    // Service holds under box_style / board_grade; the print pair replaces its
    // `printed_box` boolean, which could say neither how many colors nor which
    // faces.
    custom_packaging_source: 'custom_packaging_source',
    custom_packaging_dimensions: 'custom_packaging_dimensions',
    custom_packaging_style: 'custom_packaging_style',
    custom_packaging_board: 'custom_packaging_board',
    custom_packaging_print_color: 'custom_packaging_print_color',
    custom_packaging_print_sides: 'custom_packaging_print_sides',
    kit_type: 'kit_type',
    // Fulfillment intent-capture keys, kept for BACK-COMPAT ONLY: a v1/v2-pinned
    // quote may still carry them in a line's `config` (the old `fulfillment`
    // product type). The quote-level fulfillment section deleted its own
    // fulfillment_type / recipient_count / storage_duration on 2026-08-20 — see
    // FulfillmentSnapshot. Nothing writes these today; they exist so an archived
    // line still renders with a label rather than a raw key.
    fulfillment_type: 'fulfillment_type',
    shipping_service: 'shipping_service',
    recipient_count: 'recipient_count',
    storage_duration: 'storage_duration',
};
// Customer-facing labels per attribute. Single surface today; admin
// surfaces may want their own label maps later.
export const ATTRIBUTE_LABELS = {
    product_type: "What you're making",
    finished_size: 'Finished size',
    page_count: 'Inside pages',
    print_sides: 'Sides',
    print_color: 'Print color',
    print_ink: 'Ink',
    interior_paper: 'Interior paper',
    cover_paper: 'Cover paper',
    // FB-0074: both lamination keys read "Lamination" to the customer. They never
    // co-occur — cover_finish is book-cover-only, lamination is flat-work-only —
    // so one label over two keys is unambiguous on any single surface. Nexus
    // mirrors this display name on the cover_finish ServiceAttribute; the KEY is
    // unchanged (renaming it would collide with the flat-work attribute).
    cover_finish: 'Lamination',
    lamination: 'Lamination',
    cover_finished_size: 'Cover size',
    interior_finished_size: 'Inside-pages size',
    cover_ink: 'Cover ink',
    interior_ink: 'Inside-pages ink',
    cover_color: 'Cover color',
    interior_color: 'Inside-pages color',
    cover_print_sides: 'Cover sides',
    interior_print_sides: 'Inside-pages sides',
    interior_color_page_count: 'Color pages',
    binding: 'Binding',
    media_type: 'Media type',
    media_packaging: 'Packaging',
    media_decoration: 'Art on the disc or drive',
    media_print_sides: 'Printed sides',
    media_print_method: 'Printing method',
    custom_packaging_source: 'Stock or custom-made',
    custom_packaging_dimensions: 'Inside size',
    custom_packaging_style: 'Style',
    custom_packaging_board: 'Board',
    custom_packaging_print_color: 'Printing',
    custom_packaging_print_sides: 'Printed where',
    kit_type: 'Kit type',
    fulfillment_type: 'What you need',
    shipping_service: 'Service level',
    recipient_count: 'Recipients',
    storage_duration: 'Storage length',
};
// Which product types require which attributes. Mirrors the
// FAMILY_ATTRIBUTES table on the Nexus side.
export const PRODUCT_TYPE_ATTRIBUTES = {
    single_sheet: [
        'finished_size', 'print_ink', 'interior_paper',
        // Optional surface finish, gated to a 10pt+ card stock (see the
        // `lamination` field's conditional_show in the seed). Mirrored as a Nexus
        // ServiceAttribute; absent from REQUIRED_ATTRIBUTES because it's opt-in.
        // Labels never carry it — the 10pt/14pt card stocks are single_sheet-only.
        'lamination',
    ],
    // No interior_paper: a label's finished_size value is the stocked sheet's
    // material SKU (LABEL_STOCK_SHEETS), so the size already names the stock.
    labels: [
        'finished_size', 'print_ink',
    ],
    // ── Book families (PrintQuoteV1 v3 cover/interior split) ──────────────
    // Books no longer carry the single-value finished_size / print_sides /
    // print_color. Each is split into a cover_* + interior_* pair so the
    // cover ("outside") and interior ("guts") are specced independently
    // (the configurator's "Cover" + "Inside pages" steps). interior_paper /
    // cover_paper were already separate. NEW keys → Nexus FAMILY_ATTRIBUTES
    // mirror update (portal-first; documented in the v3 seed hand-off header).
    saddle_stitch_book: [
        // No interior_color_page_count: saddle can't isolate color below a signature,
        // so a mixed interior prices as full color and the count is never captured
        // for this family (see the seed field's conditional_show). Every other book
        // family carries it.
        'cover_finished_size', 'interior_finished_size', 'page_count',
        'cover_ink', 'interior_ink',
        'interior_paper', 'cover_paper', 'cover_finish',
    ],
    perfect_bound_book: [
        'cover_finished_size', 'interior_finished_size', 'page_count',
        'cover_ink', 'interior_ink', 'interior_color_page_count',
        'interior_paper', 'cover_paper', 'cover_finish',
    ],
    // Hard-bound (case-bound) mirrors the paperback's attribute set exactly — the
    // only difference is the production turnaround (Nexus prices it as its own
    // Service). Kept in lockstep with perfect_bound_book above.
    hard_bound_book: [
        'cover_finished_size', 'interior_finished_size', 'page_count',
        'cover_ink', 'interior_ink', 'interior_color_page_count',
        'interior_paper', 'cover_paper', 'cover_finish',
    ],
    // binding (coil | wire_o) is assigned to this Service ONLY — it is the
    // spine disambiguator. Other families' binding is implied by the
    // product_type itself, so they carry no `binding` attribute.
    coil_or_wire_o_book: [
        'cover_finished_size', 'interior_finished_size', 'page_count',
        'cover_ink', 'interior_ink', 'interior_color_page_count',
        'interior_paper', 'cover_paper', 'cover_finish', 'binding',
    ],
    // Loose-leaf / drilled is self-cover by nature (the binder is the cover),
    // so it carries no separate cover_* specs — only the interior ("guts") set.
    loose_leaf: [
        'interior_finished_size', 'page_count', 'interior_ink',
        'interior_color_page_count', 'interior_paper',
    ],
    // media_decoration / media_print_sides are both PRICED attributes Nexus must
    // mirror on the media Services, and neither is required (decoration defaults
    // to 'none', sides to 'single_sided'), so they stay out of REQUIRED_ATTRIBUTES
    // below. `media_capacity` is captured by the form but has never been listed
    // here — an older gap, tracked in the Nexus hand-off rather than widened here.
    media_replication: [
        'media_type', 'media_packaging', 'media_decoration',
        // The two decoration follow-ups are media_type-exclusive: sides is the USB
        // body's, method is the optical disc's. Both are listed because the family
        // covers both carriers; only one is ever captured on a given line.
        'media_print_sides', 'media_print_method',
    ],
    // The style / board / printing axes are asked only on the `custom` path.
    // Stock packaging is named by its supplier and catalog number, which are
    // capture keys rather than attributes (CUSTOM_PACKAGING_STOCK_SUPPLIER_KEY and
    // its sibling).
    custom_packaging: [
        'custom_packaging_source', 'custom_packaging_dimensions', 'custom_packaging_style', 'custom_packaging_board',
        'custom_packaging_print_color', 'custom_packaging_print_sides',
    ],
    // An assembly ITEM is kitting/assembly labour, not a printed piece: its spec
    // is the kit snapshot (contents rows, build steps, packing details) captured
    // as quote-scope schema fields, so it has no priced print attribute of its
    // own. `something_else` captures nothing at all — sales scopes it off-form.
    assembly: [],
    something_else: [],
    // fulfillment is a quote-level order service (see ORDER_SERVICES), never a
    // product type. The kit_type / fulfillment_type / shipping_service /
    // recipient_count / storage_duration keys stay in ATTRIBUTE_KEYS for
    // back-compat with v1/v2-pinned quotes that still carry them in line config,
    // but no current product type requires them.
};
// ── Option values per attribute ────────────────────────────────────────
export const PRINT_SIDES = ['single_sided', 'double_sided'];
export const PRINT_SIDES_LABELS = {
    single_sided: 'Single-sided',
    double_sided: 'Double-sided',
};
// `mixed` = a body that is part color, part black & white (e.g. a manuscript
// that is mostly text with a few color photo pages). It is offered on the
// INTERIOR (manuscript) color field only; the cover stays Full color / B&W. The
// older "color cover, B&W interior" reading of `mixed` is now expressed by the
// per-section split (cover_color=color + interior_color=black_and_white), so the
// value is repurposed rather than re-minted (no new option value).
export const PRINT_COLORS = ['black_and_white', 'color', 'mixed'];
export const PRINT_COLOR_LABELS = {
    black_and_white: 'Black & white',
    color: 'Full color',
    mixed: 'Mixed: color & black & white pages',
};
// Cover ink — the cover's print spec as ONE printer-notation dimension: the
// front/back ink level ("N/M"). Ink level per side: 4 = full process color,
// 1 = black only, 0 = blank (no back). This COLLAPSES the legacy cover_color
// (color | black_and_white) + cover_print_sides (single | double) pair into one
// dimension so (a) the cover's print charge scales per imaged side — 4/4 is ~2x
// the ink of 4/0 — instead of two flat, additive deltas, and (b) mixed-sided
// covers (4/1: color outside, black inside) are expressible at all. Front (the
// outside face) is always imaged (4 or 1); the back (inside face) may be blank
// (0). Interior is unaffected — it keeps
// interior_color + interior_print_sides (priced via sheet-ups, not deltas).
//
// Nexus mirror (portal-first, then Nexus): a single `cover_ink` ServiceAttribute
// on the cover-bearing book Services, priced by a PER-SIDE rate pair
// (process-side + black-side) with the 5 combos COMPUTED as rate(front) +
// rate(back) — so the admin edits two numbers, not five configs. Ganging derives
// the cover sides factor from the ink value (4_4,4_1,1_1 -> 2; 4_0,1_0 -> 1).
//
// WORDING — "outside / inside", never "front / back". On a cover, "front" is
// ambiguous in a way it isn't on a flat sheet: it can mean the FRONT COVER (a
// panel of the outside face) or the FRONT OF THE SHEET (the whole outside face,
// back cover + spine + front cover). The N/M code's `front` means the latter, so
// the customer-facing copy says outside/inside and leaves no room to misread it.
// This holds on every binding, including coil/wire-o where nothing folds (the
// outside and inside faces of a flat cover sheet). The VALUES are untouched, so
// the wire contract and Nexus's option rows are unaffected — copy only.
// The flat-work twin (PRINT_INK_LABELS below) deliberately KEEPS front/back: a
// flyer has no outside.
export const COVER_INKS = ['4_4', '4_1', '4_0', '1_1', '1_0'];
export const COVER_INK_LABELS = {
    '4_4': '4/4: Full color, outside and inside',
    '4_1': '4/1: Full color outside, black inside',
    '4_0': '4/0: Full color outside, blank inside',
    '1_1': '1/1: Black, outside and inside',
    '1_0': '1/0: Black outside, blank inside',
};
// Map the LEGACY (color, sides) pair to the collapsed cover_ink value, per the
// old→new table in v2_VOCAB_RENAME_NEXUS.md. A pure function of already-captured
// data (never a synthesized-at-read intent), so it's safe to use to migrate a
// pinned/cloned pre-collapse quote forward AND to seed cover_ink from an uploaded
// cover's DETECTED color + sidedness (identical value domains). `4_1` has no
// legacy equivalent (the pair couldn't express a mixed-sided cover), so it is
// never produced here — the customer opts into 4/1 explicitly. Returns undefined
// when either input is missing/unrecognized, so callers no-op rather than guess.
export function coverInkFromLegacy(color, sides) {
    if (color == null || sides == null)
        return undefined;
    const doubleSided = sides === 'double_sided';
    if (color === 'color')
        return doubleSided ? '4_4' : '4_0';
    if (color === 'black_and_white')
        return doubleSided ? '1_1' : '1_0';
    return undefined; // 'mixed' is a manuscript-only color — never a cover ink
}
// Print ink — the FLAT-work (single_sheet, labels) counterpart to cover_ink,
// collapsing the legacy flat `print_color` + `print_sides` pair into ONE
// printer-notation value priced per imaged side (2026-07-22, same collapse as
// the cover). A flat sheet's front/back ink levels are the SAME five combos as a
// cover's, so the VALUE DOMAIN is shared verbatim — aliased rather than re-minted.
// `print_ink` is its own ServiceAttribute on the flat Services in Nexus
// (single_sheet / labels).
//
// The LABELS, however, are no longer shared: the cover moved to outside/inside
// wording (see COVER_INK_LABELS) because "front" is ambiguous on a folded wrap.
// A flat sheet has no outside — a flyer's two faces ARE its front and back — so
// flat work keeps the front/back copy. Same five values, two vocabularies.
export const PRINT_INKS = COVER_INKS;
export const PRINT_INK_LABELS = {
    '4_4': '4/4: Full color, both sides',
    '4_1': '4/1: Full color front, black back',
    '4_0': '4/0: Full color, front only',
    '1_1': '1/1: Black, both sides',
    '1_0': '1/0: Black, front only',
};
// (color, sides) → ink is a pure value-domain map — identical for the cover and
// the flat sheet — so the flat legacy backfill (a pinned/cloned pre-collapse
// single_sheet/labels quote carrying print_color + print_sides) reuses the same
// function. Flat `print_color` is binary (color | black_and_white), which
// coverInkFromLegacy already handles; 'mixed' never occurs on flat work.
export const printInkFromLegacy = coverInkFromLegacy;
// Interior ink — the BOOK-INTERIOR ("guts") counterpart, collapsing the legacy
// `interior_color` + `interior_print_sides` pair into ONE value (2026-07-23).
// Book interiors are spec'd in the SAME industry N/M notation as the cover/flat
// sheet (a novel is 1/1, a photo book 4/4, a single-sided workbook 1/0) — so the
// interior joins the collapse for consistency and correct impression pricing.
// Two differences from cover_ink:
//   1. NO `4_1`. A uniform book interior never prints color on one face of every
//      leaf and black on the other; that per-side split is a single-sheet/cover
//      thing. The physically-real interior set is 4_4 / 4_0 / 1_1 / 1_0.
//   2. A 6th value `mixed` the cover can't have — a body part color, part black
//      & white (e.g. mostly text with a few color photo pages). It is NOT an N/M
//      code (color distributes across PAGES, not front/back); the color-page
//      count rides on `interior_color_page_count` so Nexus prices color pages at
//      the color rate and the rest at mono (see that seed field). `mixed` is
//      double-sided by convention (the near-universal case for mixed books).
// Nexus mirror: interior_ink is a GANGING INPUT, not a priced delta — the
// interior prices via sheet-ups + impressions (PRINT_PRICING_MODEL.md), so Nexus
// DECODES interior_ink → (color, sides) at the ganging seam rather than summing a
// per-config delta. See the interior_ink section in v2_VOCAB_RENAME_NEXUS.md.
export const INTERIOR_INKS = ['4_4', '4_0', '1_1', '1_0', 'mixed'];
export const INTERIOR_INK_LABELS = {
    '4_4': 'Full color, both sides',
    '4_0': 'Full color, one side',
    '1_1': 'Black & white, both sides',
    '1_0': 'Black & white, one side',
    mixed: 'Mixed: some color, some black & white',
};
// (interior_color, interior_print_sides) → interior_ink. Pure map over
// already-captured values (never a synthesized intent), so it safely migrates a
// pinned/cloned pre-collapse book quote forward and seeds interior_ink from a
// detected manuscript's color + sidedness. `mixed` maps straight through (its
// page-count rides on interior_color_page_count). Undefined when unrecognized.
export function interiorInkFromLegacy(color, sides) {
    if (color === 'mixed')
        return 'mixed';
    if (color == null || sides == null)
        return undefined;
    const doubleSided = sides === 'double_sided';
    if (color === 'color')
        return doubleSided ? '4_4' : '4_0';
    if (color === 'black_and_white')
        return doubleSided ? '1_1' : '1_0';
    return undefined;
}
// interior_ink → its sidedness (for the page-count-doubling derivation) and its
// color (for the color-aware paper default). `mixed` is double-sided by
// convention and its own color. Undefined when unrecognized, so callers no-op.
export function interiorInkSides(value) {
    if (value === '4_0' || value === '1_0')
        return 'single_sided';
    if (value === '4_4' || value === '1_1' || value === 'mixed')
        return 'double_sided';
    return undefined;
}
export function interiorInkColor(value) {
    if (value === '4_4' || value === '4_0')
        return 'color';
    if (value === '1_1' || value === '1_0')
        return 'black_and_white';
    if (value === 'mixed')
        return 'mixed';
    return undefined;
}
// ── Lamination finish + sidedness, collapsed into ONE dimension ──────────────
// The same UX move the cover_ink / print_ink collapse made (2026-07): rather
// than a finish picker PLUS a separate sides control, the customer makes one
// choice from a friendly "<finish> — <sides>" list. Behind the scenes it's a
// SINGLE ServiceAttribute (cover_finish for books, lamination for flat work),
// so Nexus mirrors one attribute with these option rows + per-option deltas —
// not a finish attribute times a sides multiplier. `none` (no lamination) stays a
// first-class value. Value = `<finish>_<sides>` where sides is 2s (both) / 1s
// (front only); the compact code is the wire value, the label is what the
// customer reads. Added 2026-07 (print-manager session, item 3) — a portal-first
// vocabulary add: authored here, then Nexus mirrors these option rows.
export const COVER_FINISHES = [
    'none',
    'gloss_2s',
    'gloss_1s',
    'matte_2s',
    'matte_1s',
    'soft_touch_2s',
    'soft_touch_1s',
];
export const COVER_FINISH_LABELS = {
    none: 'No lamination',
    gloss_2s: 'Gloss, both sides',
    gloss_1s: 'Gloss, front only',
    matte_2s: 'Matte, both sides',
    matte_1s: 'Matte, front only',
    soft_touch_2s: 'Soft touch, both sides',
    soft_touch_1s: 'Soft touch, front only',
};
// Lamination is one dimension shared by the book cover (`cover_finish`) and flat
// work (`lamination`) — same value domain, two attribute keys (a FormField is
// unique per (schema, key)). Aliased rather than re-declared, exactly as
// print_ink aliases cover_ink.
export const LAMINATION_FINISHES = COVER_FINISHES;
export const LAMINATION_FINISH_LABELS = COVER_FINISH_LABELS;
// Legacy backfill: a pinned/cloned pre-collapse quote stored a BARE finish
// (`gloss` | `matte` | `soft_touch`) with no sidedness. The historical bare
// value was priced SINGLE-sided: the m2m cost catalog (tblLaminationCost)
// carries a distinct SINGLE8 and (higher) DOUBLE8 series, and the legacy
// lamination deltas both repos inherited equal SINGLE8 exactly
// (0.31/0.19/0.15/0.14/0.13 across the qty bands). So a bare finish maps to its
// 1-sided (front-only) variant — this is the price-preserving mapping: Nexus's
// collapse gives `_1s` those exact SINGLE8 deltas, so a re-migrated historical
// quote reprices to its original number (mapping to `_2s` would silently jump it
// to the higher DOUBLE8 cost). Must stay in lockstep with Nexus's config
// migration 20260722200000_lamination_finish_sides_collapse, which converts
// stored bare values → `_1s`. A value already in the collapsed domain (or
// `none`) passes through unchanged; undefined for anything unrecognizable so the
// caller can no-op.
export function coverFinishFromLegacy(value) {
    if (typeof value !== 'string')
        return undefined;
    if (COVER_FINISHES.includes(value))
        return value;
    switch (value) {
        case 'gloss':
            return 'gloss_1s';
        case 'matte':
            return 'matte_1s';
        case 'soft_touch':
            return 'soft_touch_1s';
        default:
            return undefined;
    }
}
export const laminationFinishFromLegacy = coverFinishFromLegacy;
// ── Cover binding style — LEGACY (superseded by the soft/hard family split) ──
//
// HISTORICAL: soft- vs hard-bound was once a single `cover_binding_style`
// (softcover | hardcover) field ON perfect_bound_book, and hardcover routed to
// a third-party-partner CANNOT_PRICE handoff. That has been SPLIT into two
// first-class product types — `perfect_bound_book` (Paperback, glued spine)
// and the new `hard_bound_book` (Hard-bound, case-bound, separately priced with
// a longer turnaround). The form field is retired and the portal no longer
// EMITS `cover_binding_style`; Nexus keys the guts undertrim on
// product_type === 'hard_bound_book' instead.
//
// This value space + labels are RETAINED (append-only, not deleted) only so any
// pre-split pinned quote that still carries `config.cover_binding_style` reads
// + labels back cleanly. Do not add new emitters — pick the product type.
export const COVER_BINDING_STYLES = ['softcover', 'hardcover'];
export const COVER_BINDING_STYLE_LABELS = {
    softcover: 'Paperback',
    hardcover: 'Hard-bound',
};
// ── Bleed (per section) ─────────────────────────────────────────────────
//
// Whether ink runs to the trimmed edge. R-AW-01 (v2_BUSINESS_RULES.md §16.2)
// makes bleed a first-class, EXPLICIT customer choice with NO default — the
// customer must say Y/N rather than have it inferred. PrintQuoteV1 v3 captures
// it PER SECTION on books (cover_bleed + interior_bleed) since a book's cover
// and guts can bleed independently; flat work keeps the single `has_bleed`.
// These values are shared verbatim by has_bleed, cover_bleed, interior_bleed.
//
// Bleed is NOT a priced ServiceAttribute (it doesn't appear in
// PRODUCT_TYPE_ATTRIBUTES, mirroring has_bleed). It informs the
// undertrim-from-bleed derivation below.
export const BLEED_CHOICES = ['yes', 'no'];
export const BLEED_CHOICE_LABELS = {
    yes: 'Yes: print edge-to-edge',
    no: 'No: keep a white border',
};
// ── Undertrim is DERIVED FROM BLEED in Nexus — never specced by the customer ─
//
// The old customer-facing `final_trim` (Standard / Undertrim 1/8" / 1/4")
// field was removed in PrintQuoteV1 v3 (deprecated, not deleted). Undertrim
// is a production decision, not a customer choice, and it is mutually
// exclusive with bleed:
//
//   bleed = false (no edge-to-edge ink)  ⇒  undertrim ALLOWED
//   bleed = true  (prints to the edge)   ⇒  undertrim DISABLED
//     (an edge-to-edge piece can't be trimmed in past the artwork without
//      cutting into the live image).
//
// This derivation runs entirely in NEXUS at pricing time off the bleed flag
// the portal forwards (has_bleed for flat; cover_bleed / interior_bleed per
// section for books). The PORTAL performs ZERO undertrim math and surfaces no
// undertrim control (CLAUDE.md §"Pricing logic lives in the ERP"). This is a
// portal-first vocabulary change with a documented Nexus follow-up (the v3
// seed header) — Nexus drops `final_trim` as a customer-supplied attribute and
// derives undertrim from the bleed flag instead.
// ── Hard-bound ⇒ the guts get an undertrim, DERIVED in Nexus ────────────
//
// A hard-bound (case-bound) book has its interior ("guts") block trimmed
// slightly UNDER the case size so the pages sit inside the cover boards. That
// undertrim is a PRODUCTION decision Nexus derives from
// product_type === 'hard_bound_book' (the family the portal forwards in config)
// — exactly like the undertrim-from-bleed derivation above. The PORTAL performs
// ZERO undertrim math and shows no undertrim control. With the soft/hard family
// split, Nexus keys this on the product type (NOT the retired
// cover_binding_style field) and prices hard_bound_book as its own Service.
// Department routing is Nexus-owned and UNCHANGED.
// `binding` — Pattern A attribute on coil_or_wire_o_book only. A two-value
// spine pick. Families with no spine carry no binding; perfect / saddle
// stitch are implied by product_type; post-press operations live in
// FINISHING_OPS; anything else goes in customer notes.
export const BINDINGS = ['coil', 'wire_o'];
export const BINDING_LABELS = {
    coil: 'Coil bound (spiral)',
    wire_o: 'Wire-O bound (twin-loop wire)',
};
export const FINISHING_CLASSES = {
    in_house: {
        label: 'Done in-house',
        help: 'Done on our own floor, usually no added lead time.',
        order: 1,
    },
    specialty: {
        label: 'Specialty finishing',
        help: 'May add lead time. We’ll confirm timing with you.',
        order: 2,
    },
};
const FLAT = 'single_sheet';
const PRINTED_TYPES = [
    'single_sheet', 'labels',
    'saddle_stitch_book', 'perfect_bound_book', 'hard_bound_book', 'coil_or_wire_o_book', 'loose_leaf',
];
const COVER_BOOKS = [
    'saddle_stitch_book', 'perfect_bound_book', 'hard_bound_book', 'coil_or_wire_o_book',
];
export const FINISHING_OPS = [
    // ── In-house: done on Argo's own floor ────────────────────────────────
    { value: 'folding', label: 'Folding', applies_to: [FLAT], popularity: 1, common: true, finishing_class: 'in_house' },
    { value: 'scoring', label: 'Scoring / creasing', applies_to: [FLAT], popularity: 2, common: true, finishing_class: 'in_house',
        help_text: 'A pressed crease along a fold line so the piece folds cleanly without cracking the paper.' },
    { value: 'three_hole_drilling', label: '3-hole drilling', applies_to: [FLAT, 'loose_leaf'], popularity: 3, common: true, finishing_class: 'in_house',
        help_text: 'Punches three holes along one edge so the sheets fit a standard 3-ring binder.' },
    { value: 'corner_stapling', label: 'Corner stapling', applies_to: [FLAT], popularity: 4, common: true, finishing_class: 'in_house' },
    { value: 'shrinkwrapping', label: 'Shrinkwrapping', applies_to: PRINTED_TYPES, popularity: 5, common: true, finishing_class: 'in_house' },
    // ── Specialty: may route to a finishing partner (jargon → defined inline) ─
    { value: 'die_cutting', label: 'Die-cutting', applies_to: [FLAT, ...COVER_BOOKS, 'loose_leaf'], popularity: 6, common: false, finishing_class: 'specialty',
        help_text: 'A custom-shaped cut (pockets, rounded folders, windows or unusual outlines) made with a steel die.' },
    { value: 'uv_coating', label: 'Spot UV coating', applies_to: [FLAT, 'labels', ...COVER_BOOKS], popularity: 7, common: false, finishing_class: 'specialty',
        help_text: 'A glossy clear coating applied to just part of the design (a logo or title) for contrast against a matte background.' },
    { value: 'pms_custom_color', label: 'PMS ink match', applies_to: PRINTED_TYPES, popularity: 8, common: false, finishing_class: 'specialty',
        help_text: 'Pantone (PMS) matching mixes a specific ink so an exact brand color prints the same on every run.' },
    { value: 'embossing', label: 'Embossing', applies_to: [FLAT, ...COVER_BOOKS], popularity: 9, common: false, finishing_class: 'specialty',
        help_text: 'Presses a raised (or recessed) design into the paper, felt by touch, with no added ink.' },
    { value: 'foil_stamping', label: 'Foil stamping', applies_to: [FLAT, ...COVER_BOOKS], popularity: 10, common: false, finishing_class: 'specialty',
        help_text: 'Presses a thin metallic or colored foil onto the paper for a shiny accent, common on covers and cards.' },
    // Laser etching (FB-0075). Specialty like the five above — it is performed OUT
    // OF HOUSE, so an etched order routes to a vendor-priced Service and comes back
    // as "talk to sales" rather than a self-serve price.
    //
    // It is the ONE op with no checkbox. The customer never picks it in a finishing
    // field: it is implied by `media_decoration = 'laser_etched'`, so it is absent
    // from the seed's own FINISHING_CATALOG (which is what builds the
    // `specialty_finishing` checklist) and reaches the expander through
    // readFinishingOps' media arm instead. A checkbox as well would let one drive
    // ask for printing AND etching, which is the mutual exclusion the enum exists
    // to express.
    { value: 'laser_etching', label: 'Laser etching', applies_to: ['media_replication'], popularity: 11, common: false, finishing_class: 'specialty',
        help_text: 'A laser burns your art into the drive body — no ink, and it will not rub off.' },
];
// The op a `media_decoration = 'laser_etched'` line emits. Named because the
// media reader in the finishing expander and the Nexus hand-off both refer to it
// and neither should re-spell the literal.
export const LASER_ETCHING_FAMILY_KEY = 'laser_etching';
// Canonical family_keys, for membership checks at the expansion seam.
export const FINISHING_FAMILY_KEYS = FINISHING_OPS.map((o) => o.value);
export const FINISHING_OP_LABELS = Object.fromEntries(FINISHING_OPS.map((o) => [o.value, o.label]));
// Line-level `config` key holding the customer's selected finishing
// operations as an array of FINISHING_OPS values. This is portal-only
// line state — the Nexus engine NEVER reads `config.finishing`. The
// pricing service strips it from the primary product line item and expands
// each selected op into its own items[] entry (Pattern B).
export const FINISHING_CONFIG_KEY = 'finishing';
// Finishing-section split (configurator UI cleanup). The customer-facing
// finishing controls are now THREE separate fields that all still expand to the
// SAME Pattern B family_keys — so pricing is unchanged (the portal owns the
// expansion; Nexus is untouched):
//   - config.finishing            → in-house ops (folding, scoring, drilling, stapling)
//   - config.shrinkwrap (boolean) → the standalone "Shrinkwrapping" toggle
//   - config.specialty_finishing  → the "Specialty Finishing" section
//                                    (die-cut, spot UV, PMS, emboss, foil)
// readFinishingOps (pricing) MERGES all three into one op list, so an existing
// quote that stored everything under config.finishing still prices (the merge
// reads it); the editor splits it on load (QuoteEdit.quoteToDrafts) so editing
// such a quote doesn't strand the shrinkwrap/specialty picks.
export const SHRINKWRAP_CONFIG_KEY = 'shrinkwrap';
export const SHRINKWRAP_FAMILY_KEY = 'shrinkwrapping';
// The pieces-per-bundle sub-detail on the shrinkwrapping op. Shrinkwrapping is
// billed PER BUNDLE, not per finished piece, so this value converts the line's
// piece count into a bundle count (see shrinkwrapBundleQuantity). It rides on the
// shrinkwrapping op's own project config (FINISHING_DETAIL_KEYS.shrinkwrapping).
export const SHRINKWRAP_BUNDLE_SIZE_KEY = 'shrinkwrap_bundle_size';
export const SPECIALTY_FINISHING_CONFIG_KEY = 'specialty_finishing';
// The specialty finishing op values (foil, emboss, spot UV, die-cut, PMS match).
// PRESENTATION / vocabulary only — used by the configurator to group finishing
// into its "specialty" subsection and by the editor (QuoteEdit.quoteToDrafts) to
// split a legacy combined config.finishing into the specialty_finishing field. It
// is NOT a pricability signal: the portal makes NO "specialty can't be priced"
// decision. The selected ops ride to Nexus as ordinary Pattern B projects; Nexus
// prices them or returns a CANNOT_PRICE outcome the portal renders in its own
// voice (see docs/ADR-013). There is deliberately no configHasSpecialtyFinishing
// helper anymore — the portal never routes on "is this specialty?".
export const SPECIALTY_FINISHING_KEYS = FINISHING_OPS.filter((o) => o.finishing_class === 'specialty').map((o) => o.value);
export const STANDALONE_FINISHING_OPS = [
    { trigger_key: 'perforate', family_key: 'perforation', label: 'Perforation', quantity_basis: { kind: 'per_piece' } },
    { trigger_key: 'rounded_corners', family_key: 'rounded_corners', label: 'Rounded Corners', quantity_basis: { kind: 'per_piece' } },
    { trigger_key: 'slot_punch', family_key: 'slot_punch', label: 'Slot Punch', quantity_basis: { kind: 'per_piece' } },
    { trigger_key: 'tabs_included', trigger_count_key: 'tabs_count', family_key: 'tabs', label: 'Tabs', quantity_basis: { kind: 'per_piece_times', count_key: 'tabs_count' } },
    { trigger_key: 'inserts_included', family_key: 'inserts', label: 'Inserts', quantity_basis: { kind: 'per_piece_times', count_key: 'inserts_count' } },
    { trigger_key: 'chipboard_backing', family_key: 'chipboard_backing', label: 'Chipboard Backing', quantity_basis: { kind: 'per_piece' } },
    { trigger_key: 'plastic_overlay', family_key: 'plastic_overlay', label: 'Plastic Cover', quantity_basis: { kind: 'per_piece' } },
    { trigger_key: 'variable_print', family_key: 'variable_data', label: 'Variable Data', quantity_basis: { kind: 'per_count', count_key: 'variable_print_records' } },
];
// ── Per-op sub-detail config keys (ride on the OP's OWN project) ────────────
//
// Keyed by Nexus family_key: the config keys that describe HOW an op is done and
// travel on that op's Pattern B project (never the primary product line — they're
// stripped from it by PORTAL_ONLY_CONFIG_KEYS). Two kinds, both first-class
// portal-mastered attributes Nexus mirrors 1:1 and renders as generic attribute
// sub-rows on the priced line:
//   - STRUCTURED counts/enums (perforation_count, slot_punch_*, tabs_*, the
//     variable-data record count); and
//   - FREE-TEXT prepress DIRECTION for the specialty ops (emboss/foil/die-cut/
//     spot-UV/PMS "what/where/how big") + variable-data "what changes / where".
//     These were previously pass-through on the primary line with no Nexus home;
//     routing them onto the op's project gives Nexus a place to mirror them and put
//     them on the quote — closing the "every selection lands in a bucket Nexus
//     reads" loop (CLAUDE.md §"The portal is a dumb form").
//
// Only keys tied to a specific finishing OP live here. Primary-line product
// attributes are NOT finishing sub-details and stay on the product line for Nexus
// to mirror as product ServiceAttributes: `sheet_orientation` (a single_sheet
// attribute) and the hardcover cover sub-flow (`endsheet_description`,
// `needs_endsheets`/`needs_embossing`/`embossing_description`) ride the primary/book
// line as before.
//
// A family_key absent here has no sub-details (rounded_corners, chipboard_backing,
// plastic_overlay, scoring, drilling, stapling, and the specialty ops with no
// direction fields).
//
// NEXUS HAND-OFF (portal-first) for `folding` + `shrinkwrapping`: these two
// in-house ops now carry their sub-details on the op's OWN Pattern B project
// (previously the fold_*/shrinkwrap_bundle_size keys rode the primary product
// line, where Nexus never read them — the "captured but transmitted in the wrong
// bucket" defect). The portal owns the shape; Nexus mirrors these as ordinary
// ServiceAttributes on the folding / shrinkwrapping Services. The fold_type /
// fold_orientation / fold_count / fold_direction vs Nexus `fold_pattern` vocabulary reconciliation
// is still Nexus's call to make on the RECEIVING shape — but the keys now arrive
// in a bucket Nexus reads.
export const FINISHING_DETAIL_KEYS = {
    // In-house ops — structured sub-details (portal-mastered; Nexus mirrors).
    folding: ['fold_type', 'fold_orientation', 'fold_count', 'fold_direction'],
    shrinkwrapping: [SHRINKWRAP_BUNDLE_SIZE_KEY],
    // Standalone ops — structured counts/enums.
    perforation: ['perforation_count'],
    slot_punch: ['slot_punch_location', 'slot_punch_count'],
    // `tabs_bank_size_other` is the describe-it box the 'custom' bank size reveals;
    // it rides the tabs op alongside the pick it qualifies, so the two never separate.
    // `tabs_method` is the INSERTION method (hand vs machine), not printed-vs-plain.
    tabs: [
        'tabs_count',
        'tabs_bank_size',
        'tabs_bank_size_other',
        'tabs_print_sides',
        'tabs_color',
        'tabs_method',
    ],
    // `inserts` is the row list, projected by insertsForTransmit before it rides
    // the op — never sent as stored. `inserts_count` is derived from those rows.
    inserts: ['inserts_count', 'inserts_method', 'inserts'],
    // Variable data — the record count (quantity basis) + free-text direction.
    variable_data: ['variable_print_records', 'variable_data_description', 'variable_data_location'],
    // Specialty ops — free-text prepress direction (portal-mastered; Nexus mirrors).
    embossing: ['emboss_description', 'emboss_location', 'emboss_size'],
    foil_stamping: ['foil_description', 'foil_location', 'foil_size'],
    die_cutting: ['die_cut_notes', 'die_cut_location', 'die_cut_size'],
    uv_coating: ['uv_notes', 'uv_location', 'uv_size'],
    pms_custom_color: ['pms_color', 'pms_location', 'pms_notes'],
};
// Every Pattern B finishing family_key the portal can emit — the multi_enum ops
// (FINISHING_FAMILY_KEYS) plus the standalone ops. Used at the snapshot-round-trip
// seam to recognize a priced finishing line by its `${parent}__${family_key}`
// draft_line_id (services/pricing/index.ts).
export const ALL_FINISHING_FAMILY_KEYS = [
    ...FINISHING_FAMILY_KEYS,
    ...STANDALONE_FINISHING_OPS.map((o) => o.family_key),
];
// Resolve the priced quantity for a standalone finishing op against a line's
// config + quantity, per the op's grounded basis. Always returns a positive whole
// number: when a count field is blank/invalid it falls back to the line quantity
// (a sane floor — Nexus re-prices at submit anyway), never 0 or NaN.
export function standaloneFinishingQuantity(op, lineQty, config) {
    const floor = Math.max(1, Math.round(Number.isFinite(lineQty) ? lineQty : 1));
    const readPositive = (key) => {
        const v = config[key];
        if (typeof v === 'number' && Number.isFinite(v) && v > 0)
            return Math.round(v);
        if (typeof v === 'string' && v.trim() !== '') {
            const n = Number(v);
            if (Number.isFinite(n) && n > 0)
                return Math.round(n);
        }
        return null;
    };
    const basis = op.quantity_basis;
    switch (basis.kind) {
        case 'per_piece':
            return floor;
        case 'per_piece_times': {
            const c = readPositive(basis.count_key);
            return c ? floor * c : floor;
        }
        case 'per_count': {
            const c = readPositive(basis.count_key);
            return c ?? floor;
        }
    }
}
// Resolve the priced quantity for the shrinkwrapping op. Shrinkwrapping is billed
// PER BUNDLE, not per finished piece: a 200-piece order at 25 pieces/bundle bills
// ceil(200 / 25) = 8 bundles. The pieces-per-bundle value rides on the op's project
// config as SHRINKWRAP_BUNDLE_SIZE_KEY (number or numeric string). When it's missing
// or < 2 (each piece wrapped on its own), fall back to the full line quantity — the
// per-piece behavior. Always returns a positive whole number (never 0 or NaN).
export function shrinkwrapBundleQuantity(config, lineQty) {
    const floor = Math.max(1, Math.round(Number.isFinite(lineQty) ? lineQty : 1));
    const raw = config[SHRINKWRAP_BUNDLE_SIZE_KEY];
    let bundleSize = null;
    if (typeof raw === 'number' && Number.isFinite(raw))
        bundleSize = raw;
    else if (typeof raw === 'string' && raw.trim() !== '') {
        const n = Number(raw);
        if (Number.isFinite(n))
            bundleSize = n;
    }
    if (bundleSize === null || bundleSize < 2)
        return floor;
    return Math.max(1, Math.ceil(floor / Math.round(bundleSize)));
}
// Per-op quantity resolvers for the multi_enum "simple" finishing ops (the ones
// readFinishingOps yields — folding, scoring, shrinkwrapping, the specialty ops).
// A family_key ABSENT here prices per-piece (= the line quantity) — the default.
// Only an op whose grounded Nexus basis ISN'T per-piece registers a resolver, so
// expandFinishingProjects stays free of family-specific branches. This is the
// data-layer hook for the multi_enum ops, mirroring how the standalone boolean ops
// carry their basis inline as StandaloneFinishingOp.quantity_basis. Keep in lockstep
// with Nexus's finishing-quantity resolver.
export const SIMPLE_FINISHING_QUANTITY = {
    // Shrinkwrapping bills per BUNDLE (ceil(pieces / pieces-per-bundle)), not per piece.
    [SHRINKWRAP_FAMILY_KEY]: shrinkwrapBundleQuantity,
};
// ── Expedite (rush turnaround) — Pattern B Service, quote-level ─────────
//
// Standard production turnaround, in calendar days. The configurator
// defaults "Need By" to today + this many days and treats any earlier
// Need-By as a rush order. This is a UI/intent constant — it decides
// WHETHER to emit the expedite intent, NOT what the rush costs. Pricing
// (the +10% uplift) is owned entirely by Nexus (CLAUDE.md §"Pricing logic
// lives in the ERP"). Single source of truth for the portal UI and the
// pricing-request builder (see util/turnaround.ts).
export const STANDARD_TURNAROUND_DAYS = 7;
// ── Per-product-type standard turnaround overrides ──────────────────────
//
// Most products share STANDARD_TURNAROUND_DAYS. A HARD-BOUND (case-bound) book
// runs through additional case-making + press + binding steps, so its standard
// production window is materially longer than the shop's ~1-week default. This
// map holds the overrides; a product type absent from it uses
// STANDARD_TURNAROUND_DAYS. Like STANDARD_TURNAROUND_DAYS, this decides ONLY the
// portal's DEFAULT Need-By date + the rush (expedite) threshold — the real lead
// time (and any rush price) is Nexus's (CLAUDE.md §"Pricing logic lives in the
// ERP"). A quote's window is the LONGEST among its lines' product types (see
// turnaroundDaysForProductTypes in util/turnaround.ts). Adjust the day count
// here if the shop's hard-bound lead time changes.
export const HARD_BOUND_TURNAROUND_DAYS = 21;
export const TURNAROUND_DAYS_BY_PRODUCT_TYPE = {
    hard_bound_book: HARD_BOUND_TURNAROUND_DAYS,
};
// Expedite is a single quote-level Pattern B Service (NOT a per-line
// finishing op). When a quote is expedited the portal appends ONE project
// with config { product_type: EXPEDITE_FAMILY_KEY }; Nexus returns it as
// its own lines[] row carrying the uplift it computed. The portal never
// computes or hardcodes the amount. Like FINISHING_OPS values, this string
// IS the canonical Nexus family_key — emitted verbatim, no translation.
export const EXPEDITE_FAMILY_KEY = 'expedite';
export const EXPEDITE_LABEL = 'Expedite';
// ── Physical proof (printed sample) — Pattern B Service, quote-level ────
//
// How the customer wants to review a proof before the full run. Authored PER
// LINE as the `proof_type` schema field — a MULTI-SELECT, so config.proof_type
// is a string[] (a line may want both 'digital' and 'physical'). A proof is now
// MANDATORY: 'digital' (an emailed PDF) is the required default and 'physical'
// (a mailed printed sample) is an optional paid upgrade. The portal reduces the
// whole quote to one intent — physical > digital > none (resolveQuoteProofType
// in util/proof.ts). Only 'physical' is a paid, quote-level Pattern B Service:
// when the quote resolves to 'physical' the portal appends ONE project with
// config { product_type: PHYSICAL_PROOF_FAMILY_KEY } and Nexus returns it as its
// own lines[] row carrying the amount it computed (the proof's own print +
// shipping). The portal never computes or hardcodes that amount. 'digital' emits
// NO project — a digital proof carries no separate charge today (its own pricing
// is still TBD — see the project-memory note). Like FINISHING_OPS values and
// EXPEDITE_FAMILY_KEY, this string IS the canonical Nexus family_key, emitted
// verbatim with no translation.
//
// 'none' is RETAINED here as a legacy/aggregation value only — it's no longer a
// form option (the customer-form seed dropped it) and submission is blocked when
// a quote resolves to it (assertProofSelected). Keeping it in the type lets
// pre-existing quotes that stored 'none' still read + resolve without error.
export const PROOF_TYPES = ['none', 'digital', 'physical'];
export const PROOF_TYPE_LABELS = {
    none: 'No proof',
    digital: 'Digital proof',
    physical: 'Physical proof',
};
export const PHYSICAL_PROOF_FAMILY_KEY = 'physical_proof';
export const PHYSICAL_PROOF_LABEL = 'Physical proof';
// Media replication covers optical discs and USB drives. The disc formats split
// on HOW the copy is made, which is a real cost difference and not a synonym:
// "replicated" discs (cd, dvd) are factory-pressed from a glass master, which
// carries a one-off mastering charge and only pays off on large runs;
// "duplicated" discs (cd_r, dvd_r) are burned individually with no master. That
// distinction is what Nexus's Disk Mastering service prices, so the two must stay
// separate values rather than collapsing to one "CD" option.
export const MEDIA_TYPES = ['cd', 'cd_r', 'dvd', 'dvd_r', 'usb', 'blu_ray'];
export const MEDIA_TYPE_LABELS = {
    cd: 'CD (replicated)',
    cd_r: 'CD-R (duplicated)',
    dvd: 'DVD (replicated)',
    dvd_r: 'DVD-R (duplicated)',
    usb: 'USB drive',
    blu_ray: 'Blu-ray',
};
// A jewel case is a disc carrier, and the seed hides it on a USB order with a
// per-option `hide_when` on media_type. `part_of_assembly` is the answer for a
// line that is a COMPONENT — the discs or drives go into a kit built for the
// order, so no per-piece carrier is bought for them at all.
//
// The five `usb_case_*` / `flashpack_case*` values are the three USB cases Argo
// stocks (FB-0077), each a priced per-unit carrier Nexus resolves to its own
// Material: the foam-insert case (STCKCASE036), the swivel-drive foam case
// (STCKCASE037) and the FlashPack super clear case (STCKCASE032). The FlashPack's
// optional printed wrap is folded into the value — none, one side (4/0) or both
// sides (4/4) — so the wrap is chosen in the same pick rather than by a field the
// pick would have to reveal. They carry no `hide_when`, so a disc order is
// offered them too.
//
// `stock_item` and `other` are the two ESCAPES, format-agnostic: a stocked
// carton and a described-by-hand carrier hold a drive and a disc equally. Each
// keeps its answer in a sibling config key — the stocked SKU in
// MEDIA_PACKAGING_STOCK_ITEM_KEY, the free text in MEDIA_PACKAGING_OTHER_KEY — so
// this list stays a fixed vocabulary Nexus mirrors as ServiceAttributeOption
// rows (R9) instead of one that grows a value per carton.
//
// `sleeve`, `mailer_box` and `bulk` are retired (FB-0077) and read back through
// RETIRED_OPTION_LABELS only.
export const MEDIA_PACKAGING = [
    'jewel_case',
    'usb_case_foam',
    'usb_case_foam_swivel',
    'flashpack_case',
    'flashpack_case_wrap_4_0',
    'flashpack_case_wrap_4_4',
    'part_of_assembly',
    'stock_item',
    'other',
];
export const MEDIA_PACKAGING_LABELS = {
    jewel_case: 'Jewel case',
    usb_case_foam: 'Clear USB case, foam insert',
    usb_case_foam_swivel: 'Clear swivel-USB case, foam insert',
    flashpack_case: 'FlashPack case, no wrap',
    flashpack_case_wrap_4_0: 'FlashPack case, full-color wrap, one side',
    flashpack_case_wrap_4_4: 'FlashPack case, full-color wrap, both sides',
    part_of_assembly: 'Part of an assembly',
    stock_item: 'Packaging we stock',
    other: 'Something else',
};
// The packaging picks that print the customer's art on a wrap. Picking one makes
// the `packaging` artwork upload REQUIRED: validateRoleSet's `packaging_art`
// flag, read through mediaPackagingCarriesArt by the price and submit gates
// alike. A 4/4 wrap takes ONE file holding both sides, which prepress splits by
// hand, so no second role exists for the inside face. Nexus runs its proof steps
// for the same two values.
export const MEDIA_PACKAGING_WRAPS = [
    'flashpack_case_wrap_4_0',
    'flashpack_case_wrap_4_4',
];
export function mediaPackagingCarriesArt(config) {
    if (typeof config !== 'object' || config === null || Array.isArray(config))
        return false;
    const picked = config[ATTRIBUTE_KEYS.media_packaging];
    return typeof picked === 'string' && MEDIA_PACKAGING_WRAPS.includes(picked);
}
// Where the two `media_packaging` escapes keep their answer. Both are ordinary
// line-config keys backed by their own seeded schema field, so each is scrubbed
// by scrubConfigForVisibility the moment the packaging pick moves off the option
// that revealed it — a stocked SKU cannot linger on a line that has since chosen
// a jewel case.
//
// The stock key holds a CompanyItem.item_id, the same opaque selection key the
// kit contents rows and the rework picker store (types/company-items.ts): `sku ||
// id` on the Nexus side, echoed back verbatim and never parsed here. It is read
// at the quote write boundary as a stock CLAIM and checked against the item
// master the picker offered from — see itemStockClaims in
// services/quotes/index.ts. A key the Company does not reach is refused, exactly
// as an assembly's `bom_id` is (defect D4).
//
// NEXUS HAND-OFF (portal-first → Nexus mirror, R9/R11): add `stock_item` and
// `other` as `media_packaging` ServiceAttributeOption rows on the media Services,
// both with a ZERO delta — the money for a stocked carton is the carton's own
// material cost, which Nexus resolves from the SKU, and `other` is unpriced by
// construction (it routes to sales). Neither sibling key is a priced attribute:
// they ride the primary line's config as capture, in the bucket Nexus already
// reads for this Service (R6), and are never stripped at transmit.
export const MEDIA_PACKAGING_STOCK_ITEM_KEY = 'media_packaging_stock_item';
export const MEDIA_PACKAGING_OTHER_KEY = 'media_packaging_other';
// What the picked item READ as, frozen at pick time — the same thing an assembly
// freezes in `bom_snapshot.name`, and for the same reason: `item_id` is an opaque
// key, and every surface that reads a saved pick back without mounting the picker
// (the right-rail summary, the review strip, a read-only mount of an approved
// quote) has nothing else to show. Only CompanyItemPicker can resolve a key to a
// name, and it is not mounted on any of them.
//
// A `_label` COMPANION, exactly like the SizeGrid's `<key>_custom`: not a schema
// field, written beside its parent, and deleted by scrubConfigForVisibility the
// moment the parent is hidden — so a stale name can never outlive the pick it
// described. Display only; the `item_id` remains the sole identifier anything
// resolves or checks ownership against.
export const MEDIA_PACKAGING_STOCK_ITEM_LABEL_KEY = `${MEDIA_PACKAGING_STOCK_ITEM_KEY}_label`;
// The stocked-packaging SKU a line config claims, or undefined. Read only when
// the packaging pick is actually `stock_item`: a value left behind by a config
// the scrub never reached (a historical import, a hand-edited row) names a
// carrier the customer is no longer asking for, and treating it as a live claim
// would put it in front of the ownership check — and on the wire — for a line
// that packages itself some other way.
export function readMediaPackagingStockItem(config) {
    if (typeof config !== 'object' || config === null || Array.isArray(config))
        return undefined;
    const record = config;
    if (record[ATTRIBUTE_KEYS.media_packaging] !== 'stock_item')
        return undefined;
    const claimed = record[MEDIA_PACKAGING_STOCK_ITEM_KEY];
    if (typeof claimed !== 'string')
        return undefined;
    const trimmed = claimed.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}
// The optical formats — every media_type that is a DISC rather than a drive.
// Named because two separate gates need the same membership: laser etching is a
// USB-BODY operation (you cannot etch a disc face), and a disc carrier is
// offered only for these. Derived by exclusion from MEDIA_TYPES so a new disc
// format joins both gates by adding one value, not by editing two lists.
export const OPTICAL_MEDIA_TYPES = MEDIA_TYPES.filter((t) => t !== 'usb');
// What art the disc face / USB body carries. Supersedes the `media_art_printed`
// BOOLEAN, which could express only "some art / no art": its help text read
// "Printed art on a disc face, or laser-etched / printed art on a USB body", so
// the two production paths shared one flag and an etched job was priced as
// in-house printing.
//
// The three values are mutually exclusive because laser etching is performed
// OUT OF HOUSE (FB-0075, reported 2026-08-05). It is therefore an ALTERNATIVE to
// in-house printing, never a co-selection, and it routes through the
// vendor-routed Specialty Finishing path — see LASER_ETCHING_FAMILY_KEY.
export const MEDIA_DECORATIONS = ['none', 'printed', 'laser_etched'];
export const MEDIA_DECORATION_LABELS = {
    none: 'No printed or etched art',
    printed: 'Printed art',
    laser_etched: 'Laser-etched art',
};
// How printed art is laid onto an OPTICAL disc face. A disc plant decorates by
// one of two presses, and the pick is driven by the ARTWORK, not by a count of
// faces: a disc has exactly one printable side (the opposite side carries the
// data), which is why `media_print_sides` is a USB-body question and this one
// is the disc's. The two are mutually exclusive by media_type.
//
// Silkscreen pulls each ink as its own solid screen — the standard for logos,
// type and flat spot color, and the cheaper set-up. Offset litho images a
// halftone, so it is what photographic art, gradients and fine detail need, at
// a higher plate/set-up cost. Ordered cheapest-first, like every other priced
// enum on this form.
export const MEDIA_PRINT_METHODS = ['silkscreen', 'offset'];
export const MEDIA_PRINT_METHOD_LABELS = {
    silkscreen: 'Silkscreen',
    offset: 'Offset (litho)',
};
export const MEDIA_DECORATION_CONFIG_KEY = 'media_decoration';
export const MEDIA_PRINT_SIDES_CONFIG_KEY = 'media_print_sides';
export const MEDIA_PRINT_METHOD_CONFIG_KEY = 'media_print_method';
// How the customer gets us the files that go ONTO the discs or drives (FB-0128,
// ADR-021 §5). Argo supplies no content, so every media line has to answer it.
// `upload` sends them browser-direct into the media content store (the
// recommended path); `link` is a share link the portal checks and, when it can,
// copies into that store; `mail_master` is a physical master drive or disc;
// `follow_up` lets a customer quote before the content exists; `ship_blank`
// (FB-0094) says on purpose that nothing goes onto them, so a blank order is an
// answer Nexus can read rather than a missing upload or a forgotten follow-up.
export const MEDIA_CONTENT_DELIVERIES = ['follow_up', 'link', 'mail_master', 'ship_blank', 'upload'];
export const MEDIA_CONTENT_DELIVERY_CONFIG_KEY = 'media_content_delivery';
export const MEDIA_CONTENT_LINK_CONFIG_KEY = 'media_content_link';
export const MEDIA_CONTENT_SIZE_GB_CONFIG_KEY = 'media_content_size_gb';
export const MEDIA_CAPACITY_CONFIG_KEY = 'media_capacity';
// Who supplies the USB drives a duplication line copies onto (FB-0211). Usually
// Argo buys them new; a rework (drives coming back to be reloaded with updated
// content) has the customer send their own. A separate answer from
// MEDIA_CONTENT_DELIVERIES rather than a sixth value on it, because the two are
// independent: a customer sending drives still has to get us the files by upload,
// link, master or follow-up, and Nexus skips the Duplicate step on
// `media_content_delivery = ship_blank` alone, so folding supply into that answer
// would either lose the content route or hide that the job still needs copying.
//
// The values are Nexus's ComponentSupplySource (COMPONENT_SUPPLY_SOURCES below),
// narrowed to the two a drive can take, so one supply shape crosses everywhere a
// kit row or a drive names its source. A drive Argo buys from an outside supplier
// is still `argo_purchased`. Capture only: whether a customer-supplied drive drops
// the blank-drive cost is Nexus's to decide (R2/R3).
export const MEDIA_SUPPLY_SOURCES = ['argo_purchased', 'partner_supplied'];
export const MEDIA_SUPPLY_SOURCE_LABELS = {
    argo_purchased: 'Argo supplies new drives',
    partner_supplied: "I'll send you my own drives",
};
export const MEDIA_SUPPLY_SOURCE_CONFIG_KEY = 'media_supply_source';
// Custom packaging (FB-0232): packaging ordered as the product itself. Two kinds
// have been ordered, and the first answer tells them apart: STOCK packaging
// bought off a supplier's catalog (recurring Uline shipping cartons) or CUSTOM
// packaging made to size (printed corrugated RSCs, kit boxes, tuck-front
// mailers). Packaging a kit ships in is not this product: it stays an `assembly`
// item's box_finish / overboxing answer or a kit-contents row with role
// `packaging`.
//
// The portal decides no price, plate fee or minimum for either (R2/R3). Nexus
// resolves the line's `product_type` against Service.family_key
// (`custom_packaging`) and answers with a price or CANNOT_PRICE.
export const CUSTOM_PACKAGING_SOURCES = ['custom', 'stock'];
export const CUSTOM_PACKAGING_SOURCE_LABELS = {
    custom: 'Custom-made',
    stock: 'Stock',
};
// Which stock item: the supplier and its catalog number as the customer has them
// written down ("Uline", "S-467"). Free text, because the carton is the
// supplier's, not an Argo stock item the CompanyItemPicker offers.
export const CUSTOM_PACKAGING_STOCK_SUPPLIER_KEY = 'custom_packaging_stock_supplier';
export const CUSTOM_PACKAGING_STOCK_CATALOG_NUMBER_KEY = 'custom_packaging_stock_catalog_number';
// The style and board option values are the ones Nexus's historical "Custom
// Corrugated Box" Service holds under its `box_style` / `board_grade`
// attributes, so re-keying those attributes keeps their option rows. `other`
// keeps its answer in the sibling `_other` key so the list stays a fixed
// vocabulary.
export const CUSTOM_PACKAGING_STYLES = ['die_cut', 'mailer', 'rsc', 'telescoping', 'tray', 'other'];
export const CUSTOM_PACKAGING_STYLE_LABELS = {
    die_cut: 'Die-cut, custom shape',
    mailer: 'Mailer with a tuck front',
    rsc: 'Regular slotted carton (RSC)',
    telescoping: 'Telescoping, separate lid',
    tray: 'Tray, open top',
    other: 'Something else',
};
export const CUSTOM_PACKAGING_STYLE_OTHER_KEY = 'custom_packaging_style_other';
export const CUSTOM_PACKAGING_BOARDS = ['b_flute', 'double_wall', 'e_flute', 'single_wall', 'white_kraft', 'other'];
export const CUSTOM_PACKAGING_BOARD_LABELS = {
    b_flute: 'B-flute corrugated',
    double_wall: 'Double-wall corrugated',
    e_flute: 'E-flute corrugated',
    single_wall: 'Single-wall corrugated',
    white_kraft: 'White kraft',
    other: 'Something else',
};
export const CUSTOM_PACKAGING_BOARD_OTHER_KEY = 'custom_packaging_board_other';
export const CUSTOM_PACKAGING_PRINT_COLORS = ['full_color', 'none', 'one_color'];
export const CUSTOM_PACKAGING_PRINT_COLOR_LABELS = {
    full_color: 'Full color',
    none: 'None',
    one_color: 'One color',
};
export const CUSTOM_PACKAGING_PRINT_SIDES = ['inside_and_outside', 'outside'];
export const CUSTOM_PACKAGING_PRINT_SIDES_LABELS = {
    inside_and_outside: 'Inside and outside',
    outside: 'Outside only',
};
// The custom_packaging_print_color answers that put the customer's art on it.
export const CUSTOM_PACKAGING_PRINTED_COLORS = ['one_color', 'full_color'];
// True when a custom packaging line is custom-made and prints. That makes the
// `custom_packaging_art` upload REQUIRED (validateRoleSet's `custom_packaging_art`
// flag) and puts the line in front of the proof question; both read this one
// predicate. Stock packaging never prints here, so a print color left behind on a
// line since switched to `stock` is not a print answer.
export function customPackagingCarriesArt(config) {
    if (typeof config !== 'object' || config === null || Array.isArray(config))
        return false;
    const record = config;
    if (record[ATTRIBUTE_KEYS.custom_packaging_source] !== 'custom')
        return false;
    const color = record[ATTRIBUTE_KEYS.custom_packaging_print_color];
    return typeof color === 'string' && CUSTOM_PACKAGING_PRINTED_COLORS.includes(color);
}
// The customer's reference to an earlier order of this packaging: the job or quote number on
// their last invoice. Free text, read by a person. Its own key rather than the
// `something_else_repeat_number` field, which Nexus reads by name into the
// unscoped-request Note and which only that family is offered.
export const CUSTOM_PACKAGING_REPEAT_NUMBER_KEY = 'custom_packaging_repeat_number';
// A share link must be https. Held as a pattern string because it rides the
// seeded `pattern` validation rule, which lineConfigIssues judges at the price
// gate and the SPA pre-flight — so a priced order never carries a `javascript:`
// or plain-http link to Nexus, where a rep opens it.
export const MEDIA_CONTENT_LINK_PATTERN = '^https://[^\\s]+$';
// How long content is kept in the media content store. The deletion itself is
// that account's lifecycle rule (erp_infrastructure, ADR-021 §1), counted from
// each blob's creation; this number is the copy that promises it, so the two
// change together.
export const MEDIA_CONTENT_RETENTION_DAYS = 90;
// What fraction of a USB drive's nominal (decimal) size is left for content once
// it is formatted — a 128 GB drive holds about 119 GB (ADR-021 §6).
export const USB_USABLE_FRACTION = 0.93;
// FAT32's per-file ceiling is one byte under 4 GiB, so content holding a file of
// this size or larger needs an exFAT-formatted drive.
export const FAT32_FILE_LIMIT_BYTES = 4 * 1024 * 1024 * 1024;
// What each `media_capacity` option holds for CONTENT, in decimal bytes — the
// unit the capacity is sold in and the unit a file browser reports content in.
// Keyed by the seeded option value, with the formats that option exists on (the
// same split the seed's per-option `hide_when` enforces).
export const MEDIA_CAPACITIES = [
    { value: '700mb', usable_bytes: 700e6, media_types: ['cd', 'cd_r'] },
    { value: '4_7gb', usable_bytes: 4.7e9, media_types: ['dvd', 'dvd_r'] },
    { value: '8_5gb', usable_bytes: 8.5e9, media_types: ['dvd', 'dvd_r'] },
    { value: '25gb', usable_bytes: 25e9, media_types: ['blu_ray'] },
    ...[1, 2, 4, 8, 16, 32, 64, 128].map((gb) => ({
        value: `${gb}gb`,
        usable_bytes: Math.floor(gb * 1e9 * USB_USABLE_FRACTION),
        media_types: ['usb'],
    })),
];
export function readMediaContentDelivery(config) {
    if (typeof config !== 'object' || config === null || Array.isArray(config))
        return undefined;
    const stored = config[MEDIA_CONTENT_DELIVERY_CONFIG_KEY];
    return typeof stored === 'string' && MEDIA_CONTENT_DELIVERIES.includes(stored)
        ? stored
        : undefined;
}
export function readMediaContentLink(config) {
    if (typeof config !== 'object' || config === null || Array.isArray(config))
        return null;
    const stored = config[MEDIA_CONTENT_LINK_CONFIG_KEY];
    return typeof stored === 'string' && stored.trim() !== '' ? stored.trim() : null;
}
// Which size the capacity advice judges, per delivery (ADR-021 §6): the session
// manifest on `upload` (so the warning lands before a byte is sent), the checked
// file on `link` with the declared size as the fallback, and the declared size
// on `mail_master` / `follow_up`. Null when nothing is known, and always null on
// `ship_blank`: there is no content to measure.
export function mediaContentBytesForCapacity(config, sizes = {}) {
    if (typeof config !== 'object' || config === null || Array.isArray(config))
        return null;
    const declaredGb = config[MEDIA_CONTENT_SIZE_GB_CONFIG_KEY];
    const declared = typeof declaredGb === 'number' && declaredGb > 0 ? declaredGb * 1e9 : null;
    switch (readMediaContentDelivery(config)) {
        case 'upload':
            return sizes.session_total_bytes ?? null;
        case 'link':
            return sizes.link_bytes ?? declared;
        case 'mail_master':
        case 'follow_up':
            return declared;
        case 'ship_blank':
        default:
            return null;
    }
}
// Whether the content fits the capacity the customer picked. A physical fact
// about the media, like the saddle-stitch page ceiling, so it is the portal's to
// judge (R3) — and it only ADVISES: it never blocks a price or a submit, because
// a customer may well mean to split content across several pieces.
//
// Pure over stored data (R26). Returns null when there is nothing to say: no
// capacity picked, no size known, or the content fits with no exFAT note.
export function mediaContentCapacityCheck(config, sizes = {}) {
    if (typeof config !== 'object' || config === null || Array.isArray(config))
        return null;
    const record = config;
    const capacity = MEDIA_CAPACITIES.find((c) => c.value === record[MEDIA_CAPACITY_CONFIG_KEY]);
    if (!capacity)
        return null;
    const contentBytes = mediaContentBytesForCapacity(config, sizes);
    if (contentBytes === null)
        return null;
    const onUsb = record[ATTRIBUTE_KEYS.media_type] === 'usb';
    const largestFile = sizes.session_largest_file_bytes ??
        (readMediaContentDelivery(config) === 'link' ? (sizes.link_bytes ?? null) : null);
    const exfatNote = onUsb && largestFile !== null && largestFile >= FAT32_FILE_LIMIT_BYTES;
    const overCapacity = contentBytes > capacity.usable_bytes;
    if (!overCapacity && !exfatNote)
        return null;
    const sameFormat = MEDIA_CAPACITIES.filter((c) => c.media_types.some((t) => capacity.media_types.includes(t)));
    const larger = overCapacity
        ? (sameFormat.find((c) => c.usable_bytes >= contentBytes) ?? null)
        : null;
    const largestUsb = Math.max(...MEDIA_CAPACITIES.filter((c) => c.media_types.includes('usb')).map((c) => c.usable_bytes));
    return {
        capacity_value: capacity.value,
        capacity_bytes: capacity.usable_bytes,
        content_bytes: contentBytes,
        over_capacity: overCapacity,
        larger_capacity_value: larger?.value ?? null,
        pieces_needed: Math.max(1, Math.ceil(contentBytes / capacity.usable_bytes)),
        usb_holds_it: overCapacity && !onUsb && contentBytes <= largestUsb,
        exfat_note: exfatNote,
    };
}
// The retired boolean. Kept as a NAME (never a new emitter) because two seams
// still have to recognize it on a stored line: the legacy read below, and the
// SchemaForm backfill that persists the enum forward on the next save.
export const LEGACY_MEDIA_ART_PRINTED_KEY = 'media_art_printed';
// The decoration a stored `media_art_printed` boolean meant, as a PURE function
// of already-captured data — the same shape as coverInkFromLegacy, and safe
// under CLAUDE.md R26 for exactly the same reason (it reads a value the customer
// already chose and invents no intent).
//
// `true` maps to 'printed', NOT 'laser_etched', even though the old boolean's
// help text covered both: the flag carried the IN-HOUSE printing charge, so
// 'printed' is the price-preserving reading (the same argument
// coverFinishFromLegacy makes for mapping a bare finish to its 1-sided variant).
// A legacy job that was really etched is a hand-correction, not a guess this
// function is allowed to make — mapping it to 'laser_etched' would silently
// re-route a priced order to an outsourced vendor Service.
export function mediaDecorationFromLegacy(value) {
    if (value === true)
        return 'printed';
    if (value === false)
        return 'none';
    return undefined;
}
// A line config's decoration, legacy-aware: the stored enum when the line has
// one, otherwise the retired boolean's mapping. The ONE reader every surface
// calls — the transmit seam, the finishing expander and the spec read-back —
// so a pre-FB-0075 line resolves identically wherever it is looked at. Returns
// undefined when the line answers neither key.
export function readMediaDecoration(config) {
    if (typeof config !== 'object' || config === null)
        return undefined;
    const record = config;
    const stored = record[MEDIA_DECORATION_CONFIG_KEY];
    if (typeof stored === 'string' && MEDIA_DECORATIONS.includes(stored)) {
        return stored;
    }
    return mediaDecorationFromLegacy(record[LEGACY_MEDIA_ART_PRINTED_KEY]);
}
// The decorations that put ART on a disc or drive: the ones the customer
// supplies a file for and approves a proof of (FB-0094). Nexus creates its
// digital and physical proof tasks for a media project on exactly this set, so
// the seed's `proof_type` gate and mediaLineCarriesArt read it rather than
// re-listing it.
export const MEDIA_ART_DECORATIONS = ['printed', 'laser_etched'];
// Legacy-aware through readMediaDecoration, so a pre-FB-0075 `media_art_printed`
// line counts as printed here exactly as it does on the wire.
export function mediaLineCarriesArt(config) {
    const decoration = readMediaDecoration(config);
    return decoration !== undefined && MEDIA_ART_DECORATIONS.includes(decoration);
}
// ── Retired option labels — READBACK ONLY, no new emitters ──────────────────
//
// Deprecate-don't-drop, at OPTION grain. `deprecated_at` exists on a form FIELD
// but there is no per-OPTION equivalent, so a retired option value simply stops
// being in the served `enum_options` set. Two consequences, both intended:
//
//   1. A NEW or CLONED line carrying a retired value is flagged `invalid_value`
//      by `lineConfigIssues` and cannot be priced until the customer re-picks.
//   2. A HISTORICAL line still reads back, because `SpecSummary.displayValue`
//      falls back off the served options. Without this map that fallback is
//      `humanizeToken`, which renders a value like 'dvd_r' as "Dvd R" — machine
//      code leaking into a customer's own spec summary.
//
// This map fixes (2) without weakening (1): it supplies the label ONLY, never an
// offered option, keyed by form-field key → value → the label that value carried
// while it was offered. Populate it when an option is retired; an entry whose
// option is offered again is unreachable (the served option label wins) and
// should be dropped, so the map never claims something is retired that isn't.
export const RETIRED_OPTION_LABELS = {
    // `tabs_method` used to ask printed-vs-plain; it now asks the INSERTION method
    // (hand vs machine), because every tab is printed — the tab stock is a divider
    // page inside the book's own artwork. These two values answered the OLD question,
    // so they are readback-only: they must never be offered again, and neither may be
    // re-minted for an insertion method (reusing `manual_insert` for machine insert
    // would invert the meaning of the rows already priced against it). A line still
    // holding one reads back with the label it carried, and lineConfigIssues flags it
    // `invalid_value` on edit/clone so the customer re-picks on the new axis.
    tabs_method: {
        printed: 'Printed tabs',
        manual_insert: 'Plain tabs (insert by hand)',
    },
    // A white-glove tier Argo does not sell. It arrived with the m2m_data pricing
    // curve as a rush-above-rush surcharge nobody is authorised to quote, and Nexus
    // retired its option row; a config still carrying it is refused there. The
    // field is sales-only, so no customer surface ever offered it.
    internal_priority: {
        vip: 'VIP / white-glove',
    },
    // FB-0077: none of these is a carrier Argo offers any more, on a drive or a
    // disc. Nexus retires its rows for them, so a stored line holding one is
    // flagged `invalid_value` on edit or clone and the customer re-picks rather
    // than the value reaching a 422.
    media_packaging: {
        sleeve: 'Paper sleeve',
        mailer_box: 'Mailer box',
        bulk: 'Bulk packed',
    },
};
// The label a retired option carried, or null if the value was never a retired
// option of that field. Callers fall through to their own humanize step on null.
export function retiredOptionLabel(fieldKey, value) {
    return RETIRED_OPTION_LABELS[fieldKey]?.[value] ?? null;
}
// How the finished kit is enclosed. `none` is the explicit "it ships as
// assembled" answer, last because it is the absence of the four above it: the
// question is REQUIRED (assemblyMissingFieldKeys), so without it a kit that needs
// no wrap has no answer to give and the section can never be completed.
export const KIT_TYPES = ['shrinkwrap', 'poly_bag', 'box_pack', 'insert_pack', 'none'];
export const KIT_TYPE_LABELS = {
    shrinkwrap: 'Shrinkwrap',
    poly_bag: 'Poly bag',
    box_pack: 'Box pack',
    insert_pack: 'Insert pack',
    none: 'Nothing else — leave the kit unpackaged',
};
// How a shipped order goes out, asked once the customer is shipping
// (ship_to_you / ship_to_list). Customer-facing names; the actual carrier
// + rate is confirmed by sales. STUB — not priced here. Ordered fastest →
// best-value, with freight as the pallet-scale escape, then `customer_labels`
// (FB-0131): the customer sends their own carrier labels — an Amazon FBA
// shipment, say — so the label, not an Argo tier, decides carrier and speed.
export const SHIPPING_SERVICES = [
    'argo_express',
    'argo_priority',
    'argo_economy',
    'freight_ltl',
    'customer_labels',
];
export const SHIPPING_SERVICE_LABELS = {
    argo_express: 'Argo Express: fastest',
    argo_priority: 'Argo Priority: a few days',
    argo_economy: 'Argo Economy: best value',
    freight_ltl: 'Freight / LTL: pallets',
    customer_labels: "My own labels: I'll send you the shipping labels",
};
// The customer's own carrier account (FB-0221), asked only under
// `customer_labels` and optional there: a customer sending prepaid labels may
// have no account to give. Nexus stores it once per ship-to as
// OpportunityShipToDestination.third_party_account (64 characters, blank = none)
// and refuses anything longer, so the same ceiling is held here. Free text, sent
// trimmed; it is the field key, the snapshot key and the wire key at once.
export const THIRD_PARTY_ACCOUNT_KEY = 'third_party_account';
export const THIRD_PARTY_ACCOUNT_FIELD_KEY = `order_services.${THIRD_PARTY_ACCOUNT_KEY}`;
export const THIRD_PARTY_ACCOUNT_MAX_LENGTH = 64;
// Over Nexus's ceiling? The one length rule: orderServicesMissingFieldKeys reports
// it and the quote save refuses it, so neither can accept what the other refuses.
export function thirdPartyAccountTooLong(value) {
    return (value ?? '').trim().length > THIRD_PARTY_ACCOUNT_MAX_LENGTH;
}
// ── Order services (quote-level) — "What do you need us to do with it?" ──
//
// After the customer configures their product(s), they pick — ONCE PER ORDER —
// how the finished product leaves us: shipping, fulfillment or pickup (each
// reveals its own section). Assembly / kitting is NOT an order service — it is the
// `assembly` product type, one of the item tiles, and each assembly item holds
// its own kit on QuoteLine.config.assembly (ADR-020).
//
// Like ship-to (below), this is QUOTE-LEVEL, intent-capture only, and NOT a
// per-line priced attribute: it lives as a dedicated quote-level surface in the
// editor and is forwarded to Nexus as INTENT — Nexus quotes/sales-follows-up on
// it (ZERO portal price math, same posture as ship-to). Shipping reuses the
// ShipTo shape below (the addresses are their own QuoteShipTo rows);
// fulfillment details ride in this snapshot. The field-keyed submit UX mirrors
// ship-to: each input's DOM id EQUALS its field key (`fulfillment.<prop>`).
//
// Pickup is a STORED answer like the other two, never the absence of them. It
// was once inferred from "neither Shipping nor Fulfillment", so an order whose
// services were empty, missing or carried over from an older save crossed to
// Nexus as a pickup the customer never chose. An order with none of the three
// has not answered yet (resolveDeliveryMode → null), and the submit gate asks.
export const ORDER_SERVICES = ['shipping', 'fulfillment', 'pickup'];
export const ORDER_SERVICE_LABELS = {
    shipping: 'Shipping',
    fulfillment: 'Fulfillment',
    pickup: 'Pickup',
};
// Canonical Nexus family_key per quote-level order service — the value the portal
// emits verbatim as the service project's `config.product_type` on BOTH the
// pricing feed and the Nexus opportunity-import feed (Pattern B, quote-level).
// Like FINISHING_OPS values / EXPEDITE_FAMILY_KEY, the string IS the family_key
// Nexus resolves — no translation layer.
//
// NEXUS HAND-OFF (portal-first): `fulfillment` is an existing Nexus Service (the
// "Parcel Shipping" Service). `print_order_shipping`
// is the "Print Order Shipping" Service, which is external-priced (the rep sets
// the rate) — Nexus must assign it this `family_key` so the transmitted shipping
// intent resolves. Shipping cost NEVER enters total_sale_amount (ADR-010 §12);
// these ride as transmitted intent, priced/routed by Nexus, not the portal.
// `print_order_pickup` is the "Print Order Pickup" Service, which Nexus's
// opportunity import already turns into the Pickup line when it arrives as a
// quote-level project — and, once it has, no longer infers one from
// `ship_to.delivery_method`.
export const ORDER_SERVICE_FAMILY_KEYS = {
    fulfillment: 'fulfillment',
    shipping: 'print_order_shipping',
    pickup: 'print_order_pickup',
};
// Who supplies the kit components (assembly section).
export const KIT_COMPONENT_SOURCES = ['customer_supplies', 'argo_sources', 'mixed'];
export const KIT_COMPONENT_SOURCE_LABELS = {
    customer_supplies: "I'll ship all components to you",
    argo_sources: 'Argo supplies everything',
    mixed: 'Some from you, some from Argo',
};
// What one row of a kit's contents IS, and where it came from. Nexus's Assembly
// Builder models a kit as a recipe whose rows each carry a role, a supply source
// and a per-kit quantity; these two key sets are ITS decided vocabulary, mirrored
// verbatim (the same no-translation-layer posture ORDER_SERVICE_FAMILY_KEYS takes
// for family_key). Nexus enums: KitComponentRole and ComponentSupplySource.
// Coining a synonym here is a defect — see ADR-018 Decision 1.
export const KIT_COMPONENT_ROLES = [
    'primary',
    'insert',
    'backing',
    'envelope',
    'label',
    'packaging',
    'filler',
    'other',
];
export const KIT_COMPONENT_ROLE_LABELS = {
    primary: 'The main item',
    insert: 'Insert',
    backing: 'Backing board',
    envelope: 'Envelope',
    label: 'Label',
    packaging: 'Packaging',
    filler: 'Filler / void fill',
    other: 'Something else',
};
// Who supplies THIS ROW. Distinct question from KIT_COMPONENT_SOURCES above, and
// both are needed: that one is quote-level, in the customer's voice ("do I ship
// you everything, or do you source it?"); this one is per-row, and is Nexus's
// ComponentSupplySource. 25 of 33 sampled build sheets mix supply sources inside
// a single kit, which is exactly why the quote-level triple cannot carry it.
// Neither is a rename of the other.
export const COMPONENT_SUPPLY_SOURCES = ['argo_produced', 'argo_purchased', 'partner_supplied'];
export const COMPONENT_SUPPLY_SOURCE_LABELS = {
    argo_produced: 'Argo prints or makes it',
    argo_purchased: 'Argo supplies it',
    partner_supplied: "I'll send it to you",
};
// Where the row's identity comes from — the three ways a customer names an item.
// This is a PORTAL authoring concern, not part of the Nexus key sets above: it
// records which of the three pickers filled the row in, so the editor can render
// the right control and the resolver knows which field to read. It never crosses
// to Nexus (the transmitted row is resolved — see kitContentsForTransmit).
//
//   catalog_sku — an item the company already stocks, or Argo stocks for it,
//                 picked off the company item search.
//   quote_line  — a line on THIS quote. The whole point of the combined flow: a
//                 partner's kit routinely contains the thing being printed now.
//   described   — a new item, in the customer's own words.
export const KIT_CONTENT_ORIGINS = ['catalog_sku', 'quote_line', 'described'];
export const KIT_CONTENT_ORIGIN_LABELS = {
    catalog_sku: 'An item you already have on file for me',
    quote_line: 'Another item on this order',
    described: "Something else: I'll describe it",
};
// ── The three ways the Assembly division works ──
//
// A kit is BUILT out of parts. A rework is done TO stock that already exists —
// "remove the label, apply a barcode over the existing one, put it back" — and
// produces no kit at the end. A per_unit job does the same work to each unit of
// several SKUs held in stock, so its rows are SKU × units and the item's quantity
// is their sum. Same division, same control, one stored mode (ADR-018 Decision 7,
// ADR-020), which is what makes the three kit-shaped requirements
// (kit_components_count, kit_components_supplied, kit_type) conditional instead
// of unconditional: only a kit build asks them.
//
// An absent value is 'build_kit': every snapshot stored before this field
// existed is a kit build, and a constant default is a pure function of the
// stored snapshot, which is the only shape R26 permits.
export const ASSEMBLY_MODES = ['build_kit', 'rework', 'per_unit'];
export const DEFAULT_ASSEMBLY_MODE = 'build_kit';
export const ASSEMBLY_MODE_LABELS = {
    build_kit: 'Build kits out of parts',
    rework: 'Replenish stock you already hold with us',
    per_unit: 'Same work on each unit of stock you hold with us',
};
// Which mode a snapshot is in. The ONE reader of the raw field: the validator,
// the normalizer's derivation and the control all resolve the default through
// here, so a snapshot that has never named a mode gets the same answer at every
// gate. Callers do not thread it — the mode lives ON the snapshot the validator
// is already handed, and re-deriving it caller-side is exactly the disagreement
// R12 exists to prevent.
export function assemblyMode(a) {
    return a?.assembly_mode ?? DEFAULT_ASSEMBLY_MODE;
}
// What a rework DOES to the stock. Multi-select: 46 corpus rows combine two or
// more in one job ("Disassemble - Print & Apply Barcode over Existing Label on
// Back Cover - Shrinkwrap"), so a single pick would force the customer to drop
// half of what they asked for.
//
// Grounded in the 17,898-row M2M task corpus, and ordered by how often each
// actually occurs rather than alphabetically — the same popularity-grounded
// exception to R25 the four-door entry grid took, and for the same reason: the
// first option is right for roughly half of all rework jobs. Counts:
// label-over-existing 48, repack/rebox 31, disassemble 15, rework-into-another
// kit 9, component swap 5. `other` is the describe-it escape every axis needs,
// and it is spelled out in `rework_actions_other` — the box revealed beside the
// pick itself. Sending the customer to the build steps for it (the shape until
// 2026-08-21) meant the commonest way to answer was to pick "Something else" and
// type nothing, which reaches a rep as a rework with no stated action.
export const REWORK_ACTIONS = [
    'apply_label',
    'repack',
    'disassemble',
    'rebuild_kit',
    'replace_component',
    'other',
];
export const REWORK_ACTION_LABELS = {
    apply_label: 'Apply a new label or barcode, often over an existing one',
    repack: 'Repack it into different boxes or cartons',
    disassemble: 'Take apart something already packed',
    rebuild_kit: 'Rebuild it into a different kit or SKU',
    replace_component: 'Add an item, or swap one for something different',
    other: 'Something else',
};
// ── The eight packing-detail axes (ADR-018 Decision 5) ──────────────────────
// Seeded FormSchema fields at group scope 'quote', rendered by SchemaForm bound
// to this snapshot. The VALUES live here and the seed's option rows are built
// from these same arrays (prisma/seed/customer-form.ts, assemblyAxisOptions), so
// there is one accepted set rather than two that can drift: normalizeOrderServices
// admits exactly what the picker offers, and the live catalog sweep
// (scripts/assembly-pricing-cases.ts) enumerates exactly what Nexus must mirror.
//
// Ordered by how often each value occurs in the 17,898-row M2M task corpus, not
// alphabetically — the same popularity-grounded exception to R25 the four-door
// entry grid and the rework actions already took, stated here so a reviewer does
// not flag it. Counts are recorded per axis below; where one comes from a
// deduped, kitting-scoped recount rather than the option dictionary's own sweep,
// the comment says which.
//
// Every axis carries `other`, and each one has its OWN describe-it box —
// `<axis>_other`, revealed by the pick and rendered directly under the control it
// belongs to (ASSEMBLY_OTHER_TEXT_KEYS below). That keeps the option label short
// enough to read in a chip grid without the answer having to be typed somewhere
// else: pointing at the build steps or assembly_instructions instead (the shape
// until 2026-08-21) meant a pick that said "Something else" and nothing more,
// which is the one answer a rep cannot work from.
// What is packed around the contents. Counts: crinkle 31, packing paper 21,
// bubble 11, tissue 2. Multi-valued because one kit routinely uses more than one
// ("white box with green crinkle, blue tissue paper"). Shredded paper, packing
// peanuts, air pillows, excelsior and dunnage have ZERO hits in 19,727 strings
// and are deliberately absent.
export const FILLER_TYPES = [
    'crinkle_paper',
    'packing_paper',
    'bubble_wrap',
    'tissue_paper',
    'other',
];
export const FILLER_TYPE_LABELS = {
    crinkle_paper: 'Crinkle paper',
    packing_paper: 'Packing paper',
    bubble_wrap: 'Bubble wrap',
    tissue_paper: 'Tissue paper',
    other: 'Something else',
};
// The filler's colour, which is an attribute OF the filler rather than a kind of
// filler: the corpus writes "Mint Green Crinkle Paper" and "60LBS pink and 80LBS
// black" — one job, two colours — so this is multi-valued too. Counts from a
// deduped pass over the 33 rows that name a paper filler: white 16, green 13,
// blue 13, red 8, then black / kraft / pink / orange at 1 each.
export const FILLER_COLORS = [
    'white',
    'green',
    'blue',
    'red',
    'black',
    'kraft',
    'pink',
    'orange',
    'other',
];
export const FILLER_COLOR_LABELS = {
    white: 'White',
    green: 'Green',
    blue: 'Blue',
    red: 'Red',
    black: 'Black',
    kraft: 'Kraft / natural',
    pink: 'Pink',
    orange: 'Orange',
    other: 'Another color',
};
// How the pack is closed. The corpus never names a tape BRAND, it describes the
// closure, which is why this axis replaced the "tape type" one originally
// proposed. Counts: wafer seals 31, rubber bands 17, an explicit do-not-seal 15.
// `tape` reads 36 on a deduped kitting-scoped recount, which would put it first,
// but most of those hits are a MEASURING tape packed as a kit item or a shared
// spreadsheet template — it is offered as a closure and ordered below the wafer
// seal the sweep actually measured. Water-activated versus standard tape has 0
// hits corpus-wide and is not asked at all. `heat_seal` and `glue_dot` come from
// the build-sheet sweep and carry no invoice-corpus count of their own.
// `self_seal` shuts a poly mailer on its own self-seal strip, so it is offered
// only when the kit ships in one (the seed's per-option hide_when on
// overboxing); it sits before `other` so "Something else" stays last.
export const CLOSURE_METHODS = [
    'wafer_seal',
    'tape',
    'rubber_band',
    'do_not_seal',
    'heat_seal',
    'glue_dot',
    'self_seal',
    'other',
];
export const CLOSURE_METHOD_LABELS = {
    wafer_seal: 'Clear wafer seals',
    tape: 'Tape',
    rubber_band: 'A rubber band',
    do_not_seal: 'Leave it open — do not seal',
    heat_seal: 'Heat seal',
    glue_dot: 'Glue dots',
    self_seal: 'Seal the mailer shut',
    other: 'Something else',
};
// The box the kit is packed in, asked only when the kit IS boxed (kit_type =
// box_pack). Counts: white 234, kraft 72, custom-printed 46. The real tier is
// stock versus custom-printed; a premium-versus-standard box grade has no corpus
// support and is not offered.
export const BOX_FINISHES = ['white', 'kraft', 'custom_printed', 'other'];
export const BOX_FINISH_LABELS = {
    white: 'Plain white',
    kraft: 'Kraft / natural brown',
    custom_printed: 'Custom printed',
    other: 'Something else',
};
// Whether the finished kit goes inside something else to ship. Counts from a
// deduped kitting-scoped pass: poly mailer 37, shipping box or outer carton 37,
// an explicit ship-as-is 14.
export const OVERBOXING_CHOICES = ['poly_mailer', 'shipping_box', 'pallet', 'none', 'other'];
export const OVERBOXING_LABELS = {
    poly_mailer: 'Into a poly mailer',
    shipping_box: 'Into a shipping box or outer carton',
    pallet: 'Boxed, on a pallet',
    none: 'Nothing — ship the kits as they are',
    other: 'Something else',
};
// What the labels we apply are FOR. Multi-valued: one job routinely applies an
// Amazon barcode and a shipping label. Counts from a deduped pass over the 1,826
// rows that mention a label or barcode: barcode/UPC 966, Amazon FNSKU 133,
// shipping or address 105, branding 60, serial or lot 19, compliance 8. The
// option dictionary's own sweep put barcode/UPC at 474 over a narrower pool; the
// ordering is the same either way.
export const LABEL_PURPOSES = [
    'barcode_upc',
    'fnsku_amazon',
    'shipping_address',
    'branding',
    'serial_or_lot',
    'compliance',
    'other',
];
export const LABEL_PURPOSE_LABELS = {
    barcode_upc: 'A barcode or UPC',
    fnsku_amazon: 'An Amazon FNSKU for FBA',
    shipping_address: 'A shipping or address label',
    branding: 'A logo or branding label',
    serial_or_lot: 'A serial, lot or date code',
    compliance: 'A warning or compliance label',
    other: 'Something else',
};
// Where the label goes. Counts from a deduped pass over the 1,544 rows that
// describe APPLYING a label: on the poly bag 55, bottom 45, back 43, top 21,
// over an existing label 19, outer box or carton 14, front 7. Over-an-existing
// label is the rework signature ("apply barcode over existing barcode"), and the
// option dictionary recorded 48 for it over its wider pool.
export const LABEL_PLACEMENTS = [
    'poly_bag',
    'bottom',
    'back',
    'top',
    'over_existing_label',
    'outer_carton',
    'front',
    'other',
];
export const LABEL_PLACEMENT_LABELS = {
    poly_bag: 'On the bag the piece is in',
    bottom: 'On the bottom',
    back: 'On the back',
    top: 'On the top',
    over_existing_label: 'Over the label or barcode already there',
    outer_carton: 'On the outer box or carton',
    front: 'On the front',
    other: 'Somewhere else',
};
// The recurring one-off actions a pack-out asks for that no other axis covers.
// Each names an action on the PIECES; how the pack is closed is closure_method
// and what is padded around them is filler_type. Counts from a deduped
// kitting-scoped pass: insert into an envelope 41, shrinkwrap each kit 32,
// bundle with a band 26.
export const SPECIAL_ACTIONS = [
    'insert_into_envelope',
    'shrinkwrap_each_kit',
    'bundle_with_band',
    'other',
];
export const SPECIAL_ACTION_LABELS = {
    insert_into_envelope: 'Slip it into an envelope',
    shrinkwrap_each_kit: 'Shrinkwrap each finished kit',
    bundle_with_band: 'Bundle the loose pieces with a band',
    other: 'Something else',
};
// ── the describe-it box behind every `other` pick ───────────────────────────
// One free-text slot per axis that offers an `other`, keyed `<axis>_other`, and
// asked WHERE the pick was made: each is a seeded field in the same
// `assembly_details` group as its axis, gated on that axis's own gate AND on the
// `other` value, so it appears directly under the control the moment the customer
// ticks "Something else".
//
// Both halves of that gate are load-bearing. The scrub judges every field
// against ONE pre-scrub snapshot in a single pass (scrubConfigForVisibility), so
// a box gated only on `includes(axis, 'other')` would survive the pass in which
// its axis was itself scrubbed away, and the stale text would ride to Nexus if
// nothing else was edited afterwards. Repeating the axis's gate makes the box's
// visibility a function of the same values its axis's is, independent of order.
//
// `rework_actions_other` is the same question for the rework list, which is a
// HAND-WRITTEN control in KittingFields rather than a seeded field — so its
// clear-on-untick rides the same patch that writes rework_actions, and it is
// listed here so the manifest, the sweep and the normalizer still cover it.
//
// All of them are free values, not axes: there is no option set for Nexus to
// mirror, and the axis's own `other` pick is what tells a reader the text is that
// pick's answer.
export const ASSEMBLY_OTHER_TEXT_KEYS = [
    'filler_type_other',
    'filler_color_other',
    'closure_method_other',
    'box_finish_other',
    'overboxing_other',
    'label_purpose_other',
    'label_placement_other',
    'special_actions_other',
    'rework_actions_other',
];
// Defensive normalizer for one BOM component (Nexus → portal, or persisted
// snapshot → DTO). Returns null (dropped) when there's no description.
export function normalizeBomComponent(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const r = raw;
    if (typeof r.description !== 'string' || r.description.length === 0)
        return null;
    const out = {
        description: r.description,
        quantity: typeof r.quantity === 'number' && Number.isFinite(r.quantity) ? r.quantity : 0,
    };
    if (typeof r.sku === 'string' && r.sku.length > 0)
        out.sku = r.sku;
    if (typeof r.unit === 'string' && r.unit.length > 0)
        out.unit = r.unit;
    return out;
}
// Plain-English fallback for a line whose title was never set — a configured
// line always has a product_type, and that reads better to a rep than a blank.
function lineDescription(line) {
    const title = typeof line.title === 'string' ? line.title.trim() : '';
    if (title)
        return title;
    const config = line.config;
    if (typeof config === 'object' && config !== null) {
        const pt = config.product_type;
        if (typeof pt === 'string' && PRODUCT_TYPES.includes(pt)) {
            return PRODUCT_TYPE_LABELS[pt];
        }
    }
    return 'Item on this order';
}
// Defensive normalizer for one authored contents row. Returns null (dropped)
// when the row carries no usable identity — role, supply source and origin are
// what make a row a row, and a row whose origin's own identifier is missing
// names nothing at all. Completeness beyond that (a per-kit quantity, a
// description on a described row) is reported by assemblyMissingFieldKeys at
// SUBMIT, never dropped here: the save posture is permissive so a half-filled
// row survives a mid-flow save, exactly like every other order-services field.
//
// `scope` rejects a line reference that is not one of the quote's own active
// lines. A rejected reference degrades the row to `described` (keeping whatever
// description was frozen at pick time) rather than deleting the row, so the
// customer's item survives even though its link did not; a row with nothing left
// to say is dropped.
//
// `rejected` collects what was refused, so the caller can decide between that
// healed snapshot and an outright refusal — see assemblyItemWriteDecision, which
// is what makes the distinction. This function always produces the healed shape;
// whether it is the shape that gets PERSISTED is not its call.
export function normalizeKitContentRow(raw, opts) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const r = raw;
    if (typeof r.row_id !== 'string' || r.row_id.length === 0)
        return null;
    if (!KIT_CONTENT_ORIGINS.includes(r.origin))
        return null;
    if (!KIT_COMPONENT_ROLES.includes(r.role))
        return null;
    if (!COMPONENT_SUPPLY_SOURCES.includes(r.supply_source)) {
        return null;
    }
    let origin = r.origin;
    const description = typeof r.description === 'string' && r.description.trim().length > 0
        ? r.description.trim()
        : undefined;
    // A row's stock pick must be one the Company actually reaches, exactly as its
    // `bom_id` sibling must. Checked against the SAME set (`bom_ids`, resolved from
    // the item master by exact key) — one grain down, one universe. Only when the
    // caller supplied a set: an absent one means "not checkable here", never
    // "nothing is valid", so a Nexus outage cannot strip a customer's rows.
    //
    // A refused sku falls through to the demote below and the row survives as
    // `described`, keeping its description. Dropping the row instead would delete
    // the customer's own line item over an identifier they never typed.
    let sku;
    if (typeof r.sku === 'string' && r.sku.trim().length > 0) {
        const claimed = r.sku.trim();
        const ownedIds = opts?.scope?.bom_ids;
        if (r.origin === 'catalog_sku' && ownedIds && !ownedIds.includes(claimed)) {
            opts?.rejected?.push({ field_key: KIT_CONTENTS_SKU_FIELD_KEY, value: claimed });
        }
        else {
            sku = claimed;
        }
    }
    let line_id;
    if (typeof r.line_id === 'string' && r.line_id.length > 0) {
        const lineIds = opts?.scope?.line_ids;
        if (lineIds && !lineIds.includes(r.line_id)) {
            opts?.rejected?.push({ field_key: KIT_CONTENTS_LINE_REF_FIELD_KEY, value: r.line_id });
        }
        else {
            line_id = r.line_id;
        }
    }
    // An origin whose own identifier did not survive is no longer that origin.
    if (origin === 'catalog_sku' && !sku)
        origin = 'described';
    if (origin === 'quote_line' && !line_id)
        origin = 'described';
    if (origin === 'described' && !description)
        return null;
    const out = {
        row_id: r.row_id,
        origin,
        role: r.role,
        supply_source: r.supply_source,
    };
    if (description)
        out.description = description;
    if (sku && origin === 'catalog_sku')
        out.sku = sku;
    if (line_id && origin === 'quote_line')
        out.line_id = line_id;
    if (typeof r.quantity_per_kit === 'number' && Number.isFinite(r.quantity_per_kit)) {
        out.quantity_per_kit = r.quantity_per_kit;
    }
    if (typeof r.unit === 'string' && r.unit.trim().length > 0)
        out.unit = r.unit.trim();
    return out;
}
// The contents rows a saved kit implies, for the rework picker's pre-fill: pick
// the kit you are reworking and the item list arrives already filled in
// ("it'll say this product has this, this and this in it" — the 2026-08-20
// meeting).
//
// It lives HERE, beside normalizeKitContentRow, because a pre-fill is a WRITE:
// rows the UI invents but the write boundary refuses would vanish on save with
// nothing said. Every row this emits is one that normalizer accepts — a real
// row_id, an origin whose own identifier is present, a canonical role and supply
// source — which is what stops the pre-fill from being a shape only the
// renderer understands.
//
// The caller supplies `newRowId` because the id is portal-internal and its
// generator (crypto.randomUUID) is a browser concern.
//
// The rows are ORDINARY authored rows once they land: editable, removable,
// reorderable. That is the point — a rework is a kit CHANGING, so a pre-fill
// the customer could not edit would describe the wrong job. And this runs once,
// on the pick, never as a read-time derivation of bom_snapshot: recomputing it
// per render would silently overwrite every edit the customer made (R26).
export function kitContentRowsFromBomSnapshot(snapshot, newRowId) {
    const components = snapshot?.components;
    if (!Array.isArray(components))
        return [];
    const rows = [];
    for (const c of components) {
        const description = (c?.description ?? '').trim();
        if (description === '')
            continue;
        const row = {
            row_id: newRowId(),
            // A component Argo stocks under a SKU keeps that identifier; one without
            // is the customer's own words, which is what `described` means.
            origin: c.sku ? 'catalog_sku' : 'described',
            // Most kits are one main item plus what goes with it — the same default
            // KitContentsEditor applies to a hand-added row.
            role: rows.length === 0 ? 'primary' : 'insert',
            // The stock is the partner's, sitting in Argo's warehouse. Argo neither
            // made nor bought it, so this is the truthful reading of a reorder — and
            // it is a DEFAULT the customer can change per row, not an assertion.
            supply_source: 'partner_supplied',
            description,
        };
        if (c.sku)
            row.sku = c.sku;
        if (typeof c.quantity === 'number' && Number.isFinite(c.quantity)) {
            row.quantity_per_kit = c.quantity;
        }
        if (c.unit)
            row.unit = c.unit;
        rows.push(row);
    }
    return rows;
}
// Defensive normalizer for one authored build step. Returns null (dropped) when
// the step says nothing to do — an id with no instruction names no work, the
// same "no usable identity" test normalizeKitContentRow applies to a described
// row with no description.
//
// This drop is exactly why buildStepIncomplete exists and is reported by
// assemblyMissingFieldKeys: a step the normalizer removes is INVISIBLE to the
// backend gate (it is gone before the gate reads the snapshot), so the SPA
// preflight is the only place the customer can be told, and the rule has to live
// in the ONE shared predicate rather than beside it (R12).
export function normalizeBuildStep(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const r = raw;
    if (typeof r.step_id !== 'string' || r.step_id.length === 0)
        return null;
    if (typeof r.instruction !== 'string')
        return null;
    const instruction = r.instruction.trim();
    if (instruction.length === 0)
        return null;
    return { step_id: r.step_id, instruction };
}
// Resolve ONE authored step into the shape that crosses to Nexus. Builds a FRESH
// object rather than spreading — the same discipline resolveKitContentRow keeps,
// so a field added to AssemblyBuildStep cannot ride out here unexamined (and
// cannot silently fail to, which R5 makes a defect either way).
//
// `index` is 0-based; the wire carries the 1-based position a human counts in.
export function resolveBuildStep(step, index) {
    return {
        step_number: index + 1,
        step_id: step.step_id,
        instruction: step.instruction,
    };
}
// How many distinct items the kit holds. Once the customer has authored contents
// rows the row count IS the answer, and it is what Nexus's own builder stores;
// with no rows the entered number stands. Pure over already-stored data on one
// snapshot (R26) — never a read-time synthesis from a moving input.
//
// A rework's rows are the STOCK being reworked, not a kit's contents, so the row
// count answers a different question there and the derivation stops: the entered
// number stands on its own, and a rework that names four cartons never reports
// "4 components per kit" to a rep reading the imported note.
export function deriveKitComponentsCount(a) {
    const entered = a?.kit_components_count;
    const typed = typeof entered === 'number' && Number.isFinite(entered) ? entered : undefined;
    if (assemblyMode(a) !== 'build_kit')
        return typed;
    const rows = a?.kit_contents;
    if (Array.isArray(rows) && rows.length > 0)
        return rows.length;
    return typed;
}
// Who supplies the components, DERIVED from the rows once the customer has
// authored them. Every contents row already names its own supply source, and a
// stored row always has a valid one (normalizeKitContentRow REJECTS a row whose
// supply_source is not in COMPONENT_SUPPLY_SOURCES), so the quote-level question
// is answerable from the rows alone and asking it again is asking twice.
//
// Both operands are already-stored customer input, so this is the pure-function
// derivation R26 permits — and like the count it is PERSISTED by
// normalizeOrderServices rather than synthesized at read or transmit time.
//
// A rework's rows are the STOCK being reworked rather than a kit's contents, so
// the derivation stops there exactly as the count's does — and the field is not
// asked in rework mode at all.
export function deriveKitComponentsSupplied(a) {
    const entered = a?.kit_components_supplied;
    if (assemblyMode(a) !== 'build_kit')
        return entered;
    const rows = a?.kit_contents;
    if (!Array.isArray(rows) || rows.length === 0)
        return entered;
    let argo = false;
    let partner = false;
    for (const row of rows) {
        if (row.supply_source === 'partner_supplied')
            partner = true;
        else
            argo = true;
    }
    if (argo && partner)
        return 'mixed';
    return partner ? 'customer_supplies' : 'argo_sources';
}
// Resolve ONE authored row into the shape that crosses to Nexus. The portal row
// id and the portal line id are gone by construction — this builds a fresh
// object rather than spreading, so a field added to KitContentRow can never ride
// out here unexamined.
//
// A quote_line row takes its description from the referenced line when it froze
// none of its own; an unresolvable reference falls back to whatever the row
// itself says. Quantity is the per-kit count (BomComponent's own basis), so a
// row that has not been given one transmits 0 — the same "absent means 0"
// normalizeBomComponent already applies to a BOM row.
export function resolveKitContentRow(row, linesById) {
    const line = row.line_id ? linesById.get(row.line_id) : undefined;
    const description = row.description ?? (line ? lineDescription(line) : 'Kit item');
    const out = {
        description,
        quantity: typeof row.quantity_per_kit === 'number' && Number.isFinite(row.quantity_per_kit)
            ? row.quantity_per_kit
            : 0,
        role: row.role,
        supply_source: row.supply_source,
    };
    if (row.sku)
        out.sku = row.sku;
    if (row.unit)
        out.unit = row.unit;
    return out;
}
// How the customer resolves the mandatory-barcode question when Argo will
// warehouse + fulfill the order (orderRequiresBarcode). A fulfilled order is
// picked from inventory by scan, so its artwork MUST carry a scannable barcode.
// The customer either confirms their art already has one, says Argo's warehouse
// already prints their box barcode label (from ArgoWMS, no fee), or asks Argo to
// apply one (a small fee Nexus prices — `apply_barcode` is the only answer that
// carries it). Auto-satisfied when the upload barcode scan
// already found a symbol (see orderBarcodeResolutionMissing) — the customer only
// has to answer when we DIDN'T detect one. Added 2026-07 (item 6b).
export const BARCODE_RESOLUTIONS = [
    'artwork_has_barcode',
    'warehouse_prints_barcode',
    'apply_barcode',
];
export const BARCODE_RESOLUTION_LABELS = {
    artwork_has_barcode: 'My artwork already includes a barcode',
    warehouse_prints_barcode: 'Your warehouse already prints a barcode label for my boxes',
    apply_barcode: 'Apply a barcode for me (small fee)',
};
// The LEGACY order-wide form of the question: does the fulfilled output go into
// a SKU the company already stocks, or a new one? Still the option set of the
// stored `FulfillmentSnapshot.sku_choice`, and still exported to Nexus for it.
export const FULFILLMENT_SKU_CHOICES = ['existing', 'new'];
export const FULFILLMENT_SKU_CHOICE_LABELS = {
    existing: 'One we already stock for you',
    new: 'A new one',
};
// Where ONE line's output goes on a fulfilled order, stored on that line at
// `QuoteLine.config.fulfillment_target`. One owner for the idea across every
// line, assembly items included, because every item on a fulfilled order is
// replenished into stock under its own SKU. Not a schema field: it persists and
// clones with the line's config for free, and the visibility scrub (which only
// drops seeded-field keys) leaves it alone.
//
// It never crosses on the line's own project. The transmit strip removes it, and
// the RESOLVED list of every line's target (fulfillmentTargetsForTransmit) rides
// the fulfillment block instead, so Nexus reads one bucket for "which SKU does
// each item go into" rather than a key per line it would have to go looking for.
export const FULFILLMENT_TARGET_CONFIG_KEY = 'fulfillment_target';
// `into_kit` is the answer for a printed item a kit on this order consumes: it
// is stocked only as part of the kits, never on its own. It is the DEFAULT for
// such an item, so storing it is only ever the customer confirming that default;
// picking a SKU instead stocks the print separately as well.
//
// `with_item` is an item that is part of ANOTHER item on this order — the folded
// poster bound into the back of a workbook, or a pre-assembly built into the
// final assembly — so it has no SKU of its own and is stocked only inside the
// item it goes with (`with_line_id`). It is also the DEFAULT for a printed line a
// book's insert rows name (insertHostsByLine), the same way `into_kit` is for a
// line a kit's contents name. An assembly item reaches it only by picking it.
export const FULFILLMENT_TARGET_CHOICES = ['existing', 'new', 'into_kit', 'with_item'];
export const FULFILLMENT_TARGET_CHOICE_LABELS = {
    existing: 'One we already stock for you',
    new: 'A new one',
    into_kit: 'Only goes into the kits on this order',
    with_item: 'Part of another item on this order',
};
export const FULFILLMENT_TARGET_DISPOSITIONS = [
    'existing_sku',
    'new_sku',
    'into_kit',
    'with_item',
    'kit_own_sku',
    'kit_stock_skus',
    'kit_new_sku',
    'unanswered',
];
// Does this quote carry quote-level order-services intent at all?
//
// NOT the same question as `services_requested.length > 0`, and asking it that
// way reads a quote's own captured data as absent. The array and the detail
// blocks are written independently: turning a service OFF removes it from the
// array and deliberately KEEPS the block, so nothing the customer typed is
// destroyed by a mis-click (R5). A customer who fills in fulfillment and then
// picks Shipping therefore has `['shipping']` and a full `fulfillment` object, and
// a snapshot whose array an older save emptied still holds its blocks.
//
// The distinction has teeth on the import feed and the empty-draft reaper, where
// keyed on the array alone a quote holding a filled-in fulfillment block would
// read as untouched.
export function hasOrderServicesIntent(raw) {
    const services = normalizeOrderServices(raw);
    if (!services)
        return false;
    // PRESENT is not the same as ANSWERED. `normalizeOrderServices` assigns the
    // block unconditionally once the raw value is an object, so a `{}` survives —
    // a value picked and then cleared leaves exactly that. Counting an empty block as intent would mark a
    // genuinely untouched draft as a real order, which on the reaper's side is the
    // difference between discarding a false start and keeping a blank row forever.
    const answered = (block) => block !== undefined && Object.keys(block).length > 0;
    return (services.services_requested.length > 0 ||
        answered(services.fulfillment) ||
        services.apply_barcode === true);
}
// Field-key prefixes for the field-keyed submit-failure UX (mirrors
// SHIP_TO_FIELD_PREFIX). The DOM id of each input EQUALS its field key.
export const ASSEMBLY_FIELD_PREFIX = 'assembly';
export const FULFILLMENT_FIELD_PREFIX = 'fulfillment';
// The contents list reports at LIST grain, not per row: one key for "the kit's
// contents are not finished yet". Per-row scroll targets belong with the control
// that owns the row DOM ids — KitContentsEditor, which builds them off the row's
// own row_id and namespaces them per mount; a key invented here would have to be
// re-derived there.
export const KIT_CONTENTS_FIELD_KEY = `${ASSEMBLY_FIELD_PREFIX}.kit_contents`;
// Reported by the write boundary when a row claimed a line that is not one of
// this quote's own active lines.
export const KIT_CONTENTS_LINE_REF_FIELD_KEY = `${KIT_CONTENTS_FIELD_KEY}.line_id`;
// Reported by the write boundary when a row claimed a stock item the Company does
// not reach. Its sibling above is the LOCAL half of the same question — a row's
// two identifiers, each checked against the set it can be checked against.
export const KIT_CONTENTS_SKU_FIELD_KEY = `${KIT_CONTENTS_FIELD_KEY}.sku`;
// The build recipe reports at LIST grain too, for the same reason the contents
// list does: the per-step scroll targets are DOM ids owned by the control that
// renders the steps, and a key invented here would have to be re-derived there.
export const BUILD_STEPS_FIELD_KEY = `${ASSEMBLY_FIELD_PREFIX}.build_steps`;
// Build a kit, or rework held stock. No gate reports this one — an unset mode
// resolves to the kit build, so it can never be missing — but the radio group
// still carries it as its fieldset id, and naming it here keeps the DOM id in
// one place with its siblings rather than as a literal in the component.
export const ASSEMBLY_MODE_FIELD_KEY = `${ASSEMBLY_FIELD_PREFIX}.assembly_mode`;
// What the rework does. Reported at GROUP grain, like the two lists above: the
// checkbox group carries this as its fieldset id, so a submit failure reveals and
// scrolls to the group rather than to one box inside it.
export const REWORK_ACTIONS_FIELD_KEY = `${ASSEMBLY_FIELD_PREFIX}.rework_actions`;
export const ASSEMBLY_BOM_FIELD_KEY = `${ASSEMBLY_FIELD_PREFIX}.bom_id`;
// One label per reportable key, for the submit-failure summary. A key that is
// reportable in BOTH assembly modes needs wording that is true in both — the map
// is flat and has no mode to branch on, so "What goes in the kit" on a rework
// that builds no kit would be the summary contradicting the section it points
// at. The kit-only keys keep their kit wording, because a rework never reports
// them.
export const ASSEMBLY_FIELD_LABELS = {
    'assembly.kit_components_count': 'Components per kit',
    'assembly.kit_contents': 'The item list',
    'assembly.kit_contents.line_id': 'The item list',
    'assembly.kit_contents.sku': 'The item list',
    'assembly.build_steps': 'The step-by-step instructions',
    'assembly.rework_actions': 'What we need to do to the stock',
    'assembly.bom_id': 'The kit or SKU we’re replenishing',
    'assembly.kit_components_supplied': 'Who supplies the components',
    'assembly.kit_type': 'How each kit is packaged',
};
export const FULFILLMENT_FIELD_LABELS = {
    'fulfillment.barcode_resolution': 'Barcode for fulfillment',
};
// The per-line target keys carry the line's id, so they cannot sit in the flat
// map above. Labels by LEAF instead, and a parser so a failure summary can find
// both the line and the label from one reported key.
export const FULFILLMENT_TARGET_FIELD_PREFIX = `${FULFILLMENT_FIELD_PREFIX}.target`;
export const FULFILLMENT_TARGET_FIELD_LABELS = {
    choice: 'Which SKU this goes into',
    sku: 'Which SKU this goes into',
    new_sku_name: 'Name for the new SKU',
    with_line_id: 'Which item this is part of',
};
export function fulfillmentTargetFieldKey(lineId, leaf) {
    return `${FULFILLMENT_TARGET_FIELD_PREFIX}.${lineId}.${leaf}`;
}
export function parseFulfillmentTargetFieldKey(key) {
    const prefix = `${FULFILLMENT_TARGET_FIELD_PREFIX}.`;
    if (!key.startsWith(prefix))
        return null;
    const rest = key.slice(prefix.length);
    const dot = rest.lastIndexOf('.');
    if (dot <= 0)
        return null;
    const leaf = rest.slice(dot + 1);
    if (leaf !== 'choice' && leaf !== 'sku' && leaf !== 'new_sku_name' && leaf !== 'with_line_id') {
        return null;
    }
    return { lineId: rest.slice(0, dot), leaf };
}
// Combined lookup for the editor's failure summary (one map, like
// SHIP_TO_FIELD_LABELS). The shipping ADDRESS labels stay in
// SHIP_TO_FIELD_LABELS; only the shipping SPEED pick is keyed here.
export const ORDER_SERVICE_FIELD_LABELS = {
    ...ASSEMBLY_FIELD_LABELS,
    ...FULFILLMENT_FIELD_LABELS,
    'order_services.shipping_service': 'Shipping method',
    [THIRD_PARTY_ACCOUNT_FIELD_KEY]: 'Carrier account number',
};
// Does the row say WHICH item it is? The identifier that answers that differs by
// origin, and normalizeKitContentRow demotes an identifier-less `catalog_sku` or
// `quote_line` row to `described` rather than dropping it — so a description is
// what decides whether that demote ends in a kept row or a dropped one, and the
// same three-way answer has to serve the validator below and the authoring
// control's per-row hint (R12).
export function kitContentRowNamesItem(row) {
    if (row.origin === 'catalog_sku' && (row.sku ?? '').trim() !== '')
        return true;
    if (row.origin === 'quote_line' && (row.line_id ?? '').trim() !== '')
        return true;
    return (row.description ?? '').trim() !== '';
}
// Is ONE authored contents row still unfinished? The per-row half of
// assemblyMissingFieldKeys, exported so the authoring control can mark the
// offending row without restating the rule beside the validator that decides it
// (R12) — the list-grain key the validator reports (KIT_CONTENTS_FIELD_KEY) says
// only that the kit is unfinished, not which row.
//
// A row that names nothing is unfinished for a second reason: it is the shape
// normalizeKitContentRow DROPS — WITHOUT pushing a `rejected` entry, so the save
// reports nothing and the row simply vanishes. The miss therefore has to reach
// the SPA preflight before the save that would erase it.
export function kitContentRowIncomplete(row) {
    if (!kitContentRowNamesItem(row))
        return true;
    return (typeof row.quantity_per_kit !== 'number' ||
        !Number.isFinite(row.quantity_per_kit) ||
        row.quantity_per_kit < 1);
}
// The same question asked in a given mode. A per_unit row counts whole units of
// stock, so a fractional count leaves it unfinished there; the other two modes
// keep exactly the rule above.
export function kitContentRowIncompleteIn(mode, row) {
    if (kitContentRowIncomplete(row))
        return true;
    return mode === 'per_unit' && !Number.isSafeInteger(row.quantity_per_kit);
}
// Is ONE authored build step still unfinished? The per-step half of
// assemblyMissingFieldKeys, exported so the authoring control can mark the
// offending step without restating the rule beside the validator that decides it
// (R12) — the list-grain key the validator reports (BUILD_STEPS_FIELD_KEY) says
// only that the recipe is unfinished, not which step.
//
// A step with no instruction is the shape normalizeBuildStep DROPS, so a customer
// who added a step, attached a photo to it and never typed the direction would
// watch the step vanish on save with nothing said. Reported here, that miss
// reaches the SPA preflight before the save that would erase it.
export function buildStepIncomplete(step) {
    return (step.instruction ?? '').trim() === '';
}
// "Is the assembly section complete enough to submit?" Mirrors
// shipToMissingFieldKeys — returns the missing field keys (empty ⇒ satisfied).
// purchased_labels is an optional flag in either mode.
//
// What is required depends on the MODE the snapshot itself carries
// (assemblyMode, ADR-018 Decision 7). A kit build requires
// kit_components_count, kit_components_supplied and kit_type; a rework produces
// no kit, so all three fall away and are replaced by the facts a rework cannot be
// worked without — which kit (bom_id), what to do to the stock (rework_actions)
// and at least one step saying how. The steps carry the rework's own escape
// hatch, which is why `other` needs no free-text field of its own. A per_unit job
// waives the same three and requires the stock list itself: at least one row,
// every row a whole number of units, and a sum that fits a line quantity.
export function assemblyMissingFieldKeys(a) {
    const missing = [];
    const mode = assemblyMode(a);
    // Satisfied by the DERIVED count (deriveKitComponentsCount), so a kit whose
    // contents are authored as rows never has to also be given a number, and a kit
    // authored before the contents list existed still validates on its entered
    // one. Contents rows themselves stay OPTIONAL — a customer may still describe
    // the kit in the instructions box — but a row that exists must be finished.
    if (mode === 'build_kit') {
        const count = deriveKitComponentsCount(a);
        if (typeof count !== 'number' || count < 1) {
            missing.push(`${ASSEMBLY_FIELD_PREFIX}.kit_components_count`);
        }
    }
    // The rows are the kit's contents, the stock being reworked, or the stock and
    // its units in per_unit. Required only in per_unit, finished in every mode.
    const rows = a?.kit_contents;
    if (Array.isArray(rows) &&
        rows.length > 0 &&
        rows.some((row) => kitContentRowIncompleteIn(mode, row))) {
        missing.push(KIT_CONTENTS_FIELD_KEY);
    }
    else if (mode === 'per_unit' &&
        (!Array.isArray(rows) ||
            rows.length === 0 ||
            rows.reduce((sum, row) => sum + (row.quantity_per_kit ?? 0), 0) > MAX_LINE_QUANTITY)) {
        missing.push(KIT_CONTENTS_FIELD_KEY);
    }
    // Build steps are OPTIONAL for a kit in the same way contents rows are — it
    // may be described in the instructions box instead — but a step that EXISTS
    // must say what to do, or the normalizer drops it on save. A REWORK needs at
    // least one: the work is defined by what happens to the stock, the reference
    // photo answering "which label do I remove" hangs off a step, and a rework
    // with no steps names no work at all.
    const steps = a?.build_steps;
    const authoredSteps = Array.isArray(steps) ? steps : [];
    if (mode === 'rework' && authoredSteps.length === 0) {
        missing.push(BUILD_STEPS_FIELD_KEY);
    }
    else if (authoredSteps.length > 0 && authoredSteps.some(buildStepIncomplete)) {
        missing.push(BUILD_STEPS_FIELD_KEY);
    }
    if (mode === 'rework') {
        // A rework works on stock Argo already holds, so naming WHICH stock is the
        // one thing it cannot proceed without — "we can't rework something we don't
        // know anything about" (the 2026-08-20 meeting). Required here and nowhere
        // else: a kit BUILD has no prior kit to point at, and the same pick stays
        // optional there exactly as it always was.
        if (!a?.bom_id)
            missing.push(ASSEMBLY_BOM_FIELD_KEY);
        if (!Array.isArray(a?.rework_actions) || a.rework_actions.length === 0) {
            missing.push(REWORK_ACTIONS_FIELD_KEY);
        }
    }
    else if (mode === 'build_kit') {
        // Satisfied by the DERIVED value, on the same terms as the count above: a kit
        // whose contents are authored as rows never has to answer "who supplies the
        // components" a second time, and a kit authored before the contents list
        // existed still validates on the answer it stored.
        if (!deriveKitComponentsSupplied(a)) {
            missing.push(`${ASSEMBLY_FIELD_PREFIX}.kit_components_supplied`);
        }
        if (!a?.kit_type)
            missing.push(`${ASSEMBLY_FIELD_PREFIX}.kit_type`);
    }
    return missing;
}
// One line's stored target, rebuilt from untrusted JSON through a closed
// allowlist. Only the fields that belong to the stored choice survive: `sku` and
// its snapshot on `existing` (the snapshot never stands alone — it describes the
// SKU that was picked), `new_sku_name` on `new`, `with_line_id` on `with_item`,
// nothing on `into_kit`. An unknown choice drops the whole target.
//
// Permissive on completeness, like every other order-services answer: an
// `existing` target with no SKU yet survives a mid-flow save, and the submit gate
// reports it. The name is kept as typed rather than trimmed, so a save landing
// mid-word cannot eat the space the customer just typed; the gate judges it
// trimmed.
export function normalizeLineFulfillmentTarget(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return undefined;
    const r = raw;
    if (!FULFILLMENT_TARGET_CHOICES.includes(r.choice))
        return undefined;
    const target = { choice: r.choice };
    if (target.choice === 'existing' && typeof r.sku === 'string' && r.sku.length > 0) {
        target.sku = r.sku;
        const snap = normalizeFulfilledSkuSnapshot(r.sku_snapshot);
        if (snap)
            target.sku_snapshot = snap;
    }
    if (target.choice === 'new' && typeof r.new_sku_name === 'string' && r.new_sku_name.length > 0) {
        target.new_sku_name = r.new_sku_name;
    }
    if (target.choice === 'with_item' && typeof r.with_line_id === 'string' && r.with_line_id.length > 0) {
        target.with_line_id = r.with_line_id;
    }
    return target;
}
function configRecord(config) {
    return typeof config === 'object' && config !== null && !Array.isArray(config)
        ? config
        : undefined;
}
function configIsAssemblyItem(config) {
    return configRecord(config)?.product_type === ASSEMBLY_ITEM_PRODUCT_TYPE;
}
// A configured line carries a product_type; one without is the configurator's
// placeholder and has captured nothing to stock. The same test isConfiguredLine
// (util/product-type.ts) applies, restated here because that module imports this
// one.
function configIsConfigured(config) {
    const pt = configRecord(config)?.product_type;
    return typeof pt === 'string' && pt.trim() !== '';
}
// The line's explicit answer: `config.fulfillment_target` when present, else, on
// an assembly item, the LEGACY `assembly.fulfillment_sku` remap read as an
// existing-SKU target. The legacy key is read only off an assembly item, the same
// gate stripFinishing flattens the kit on — a kit left on a line whose family
// moved is stale capture, not an answer.
export function configFulfillmentTarget(config) {
    const record = configRecord(config);
    if (!record)
        return undefined;
    if (FULFILLMENT_TARGET_CONFIG_KEY in record) {
        const target = normalizeLineFulfillmentTarget(record[FULFILLMENT_TARGET_CONFIG_KEY]);
        if (target)
            return target;
    }
    if (!configIsAssemblyItem(record))
        return undefined;
    const assembly = normalizeAssemblySnapshot(record[ASSEMBLY_ITEM_CONFIG_KEY]);
    if (!assembly.fulfillment_sku)
        return undefined;
    return {
        choice: 'existing',
        sku: assembly.fulfillment_sku,
        ...(assembly.fulfillment_sku_snapshot ? { sku_snapshot: assembly.fulfillment_sku_snapshot } : {}),
    };
}
// The place-order save's stocking answers: one entry per SAVED line whose target
// differs from the stored line's, null where the customer went back to the
// default. Compared through configFulfillmentTarget on both sides, so a legacy
// kit remap the customer has since cleared counts as a change. A draft with no
// stored counterpart is skipped — it has no id to name yet, and its first lines
// save carries the key.
export function changedFulfillmentTargets(drafts, stored) {
    const storedById = new Map(stored.map((l) => [l.id, l.config]));
    const out = [];
    for (const draft of drafts) {
        if (!storedById.has(draft.id))
            continue;
        const next = normalizeLineFulfillmentTarget(configFulfillmentTarget(draft.config)) ?? null;
        const prior = normalizeLineFulfillmentTarget(configFulfillmentTarget(storedById.get(draft.id))) ?? null;
        if (JSON.stringify(next) !== JSON.stringify(prior)) {
            out.push({ id: draft.id, fulfillment_target: next });
        }
    }
    return out;
}
// Which kits on this order consume each line: printed line id → the ids of the
// assembly items whose kit contents rows point at it (`origin: 'quote_line'`).
// Only references to a line in `lines` count, so a row naming a removed or foreign
// line consumes nothing. A kit referencing the same line twice is listed once.
export function kitConsumersByLine(lines) {
    const known = new Set(lines.map((l) => l.id));
    const out = new Map();
    for (const line of lines) {
        if (!configIsAssemblyItem(line.config))
            continue;
        const assembly = normalizeAssemblySnapshot(configRecord(line.config)?.[ASSEMBLY_ITEM_CONFIG_KEY]);
        for (const row of assembly.kit_contents ?? []) {
            if (row.origin !== 'quote_line' || !row.line_id)
                continue;
            if (row.line_id === line.id || !known.has(row.line_id))
                continue;
            const kits = out.get(row.line_id) ?? [];
            if (!kits.includes(line.id))
                kits.push(line.id);
            out.set(row.line_id, kits);
        }
    }
    return out;
}
// Which books on this order take each line as an insert: printed line id → the
// ids of the lines whose insert rows point at it (`origin: 'quote_line'`). The
// insert-axis counterpart of kitConsumersByLine, with the same rules: only a
// reference to a line in `lines` counts, never one to the book itself, and a
// book naming the same line twice is listed once.
export function insertHostsByLine(lines) {
    const known = new Set(lines.map((l) => l.id));
    const out = new Map();
    for (const line of lines) {
        if (!configIsConfigured(line.config))
            continue;
        for (const row of normalizeInsertRows(configRecord(line.config)?.[INSERTS_CONFIG_KEY])) {
            if (row.origin !== 'quote_line' || !row.line_id)
                continue;
            if (row.line_id === line.id || !known.has(row.line_id))
                continue;
            const hosts = out.get(row.line_id) ?? [];
            if (!hosts.includes(line.id))
                hosts.push(line.id);
            out.set(row.line_id, hosts);
        }
    }
    return out;
}
// The items a line is part of, before cycles are judged: its own `with_item` pick
// when it has one, else, on a printed line with no answer of its own and no kit
// consuming it, the books that take it as an insert. An assembly item has no
// insert default; its kit's defaults stock it.
function withItemHostIds(line, kits, inserts) {
    const explicit = configFulfillmentTarget(line.config);
    if (explicit) {
        return explicit.choice === 'with_item' && explicit.with_line_id ? [explicit.with_line_id] : [];
    }
    if (configIsAssemblyItem(line.config))
        return [];
    if ((kits.get(line.id) ?? []).length > 0)
        return [];
    return inserts.get(line.id) ?? [];
}
// Every item a line goes into: its `with_item` hosts, plus every kit whose
// contents name it, print or assembly, whatever its own stocking answer. The
// edges the loop test walks. A kit counts even when the item it consumes has a
// SKU of its own, because a kit cannot be part of something it is built from.
function goesIntoIds(line, kits, inserts) {
    return [...(kits.get(line.id) ?? []), ...withItemHostIds(line, kits, inserts)];
}
function stockingGraph(lines) {
    return {
        byId: new Map(lines.filter((l) => configIsConfigured(l.config)).map((l) => [l.id, l])),
        kits: kitConsumersByLine(lines),
        inserts: insertHostsByLine(lines),
    };
}
function graphLeadsBack(graph, lineId, hostId) {
    const seen = new Set();
    const queue = [hostId];
    while (queue.length > 0) {
        const id = queue.shift();
        if (id === lineId)
            return true;
        if (seen.has(id))
            continue;
        seen.add(id);
        const host = graph.byId.get(id);
        if (host)
            queue.push(...goesIntoIds(host, graph.kits, graph.inserts));
    }
    return false;
}
// True when `hostId` goes into `lineId`, directly or along a chain, so `lineId`
// being part of it would close a loop. The one loop test: the resolver drops
// such a host and the item picker never offers it.
export function fulfillmentHostLeadsBack(lineId, hostId, lines) {
    return graphLeadsBack(stockingGraph(lines), lineId, hostId);
}
// A `with_item` line's hosts that can actually carry it: configured lines on this
// order, never the line itself, and never one whose own chain leads back here.
function liveWithItemHosts(line, lines) {
    const graph = stockingGraph(lines);
    return withItemHostIds(line, graph.kits, graph.inserts).filter((id) => id !== line.id && graph.byId.has(id) && !graphLeadsBack(graph, line.id, id));
}
// A `with_item` answer, resolved: its live hosts, or unanswered when none is left.
function resolveWithItem(line, lines) {
    const hosts = liveWithItemHosts(line, lines);
    return hosts.length > 0 ? { kind: 'with_item', host_line_ids: hosts } : { kind: 'unanswered' };
}
// An explicit target, resolved — or undefined when it is not finished (an
// existing SKU not yet picked, a new SKU not yet named). `into_kit` and
// `with_item` are resolved by the caller, which knows whether a kit still
// consumes the line and whether the item it goes with is still on the order.
function resolveExplicitTarget(target) {
    if (target.choice === 'existing' && target.sku) {
        return {
            kind: 'existing_sku',
            sku: target.sku,
            ...(target.sku_snapshot ? { sku_snapshot: target.sku_snapshot } : {}),
            source: 'line',
        };
    }
    const name = (target.new_sku_name ?? '').trim();
    if (target.choice === 'new' && name !== '') {
        return { kind: 'new_sku', new_sku_name: name, source: 'line' };
    }
    return undefined;
}
// The LEGACY order-wide answer, when it is a finished one.
function resolveOrderWideTarget(f) {
    if (f?.sku_choice === 'existing' && f.sku) {
        return {
            kind: 'existing_sku',
            sku: f.sku,
            ...(f.sku_snapshot ? { sku_snapshot: f.sku_snapshot } : {}),
            source: 'order_wide',
        };
    }
    const name = (f?.new_sku_name ?? '').trim();
    if (f?.sku_choice === 'new' && name !== '') {
        return { kind: 'new_sku', new_sku_name: name, source: 'order_wide' };
    }
    return undefined;
}
// Where ONE line's output goes on a fulfilled order.
//
// A printed (non-assembly) line: its own finished answer; else, when a kit on
// this order consumes it, into that kit; else the LEGACY order-wide answer when
// it is a finished one; else unanswered. An unfinished explicit answer is
// unanswered (the gate names the missing leaf), and so is an explicit `into_kit`
// on a line no kit consumes any longer — the kit that made it true was removed.
// `with_item` follows the kit rule one axis over: an explicit pick resolves while
// the item it names is still on the order (and is not, in turn, part of this
// one); with no answer and no kit, a line a book's inserts name goes with that
// book.
//
// An assembly item: its own answer; else the kit's defaults — the kit's own SKU,
// a per_unit job's units back into the SKUs they came from, or a new SKU sales
// sets up. So an assembly item with no answer is never unanswered. A `with_item`
// pick resolves exactly as a printed line's does, so a pre-assembly built into
// the final assembly gets no stocking of its own. `into_kit` is not an answer an
// assembly can give; it resolves unanswered, which the gate reports as a missing
// choice.
// The legacy order-wide answer never applies to one: an order holding assembly
// items was never asked it for them.
export function resolveLineFulfillmentTarget(line, ctx) {
    const explicit = configFulfillmentTarget(line.config);
    if (configIsAssemblyItem(line.config)) {
        if (explicit?.choice === 'with_item')
            return resolveWithItem(line, ctx.lines);
        if (explicit)
            return resolveExplicitTarget(explicit) ?? { kind: 'unanswered' };
        const assembly = normalizeAssemblySnapshot(configRecord(line.config)?.[ASSEMBLY_ITEM_CONFIG_KEY]);
        if (assembly.bom_id) {
            const name = assembly.bom_snapshot?.name?.trim();
            return {
                kind: 'kit_own_sku',
                sku: assembly.bom_snapshot?.bom_number ?? assembly.bom_id,
                ...(name ? { name } : {}),
            };
        }
        if (assemblyMode(assembly) === 'per_unit')
            return { kind: 'kit_stock_skus' };
        return { kind: 'kit_new_sku' };
    }
    const kits = kitConsumersByLine(ctx.lines).get(line.id) ?? [];
    if (explicit) {
        if (explicit.choice === 'into_kit') {
            return kits.length > 0 ? { kind: 'into_kit', kit_line_ids: kits } : { kind: 'unanswered' };
        }
        if (explicit.choice === 'with_item')
            return resolveWithItem(line, ctx.lines);
        return resolveExplicitTarget(explicit) ?? { kind: 'unanswered' };
    }
    if (kits.length > 0)
        return { kind: 'into_kit', kit_line_ids: kits };
    const inserted = resolveWithItem(line, ctx.lines);
    if (inserted.kind === 'with_item')
        return inserted;
    return resolveOrderWideTarget(ctx.orderFulfillment) ?? { kind: 'unanswered' };
}
// Fulfillment completeness, per line: the field keys of every configured line
// whose output has nowhere to go yet, keyed `fulfillment.target.<line id>.<leaf>`.
// The one validator for the question (R12): the SPA pre-block and the backend
// gate both reach it through orderServicesMissingFieldKeys.
//
//   choice        — no answer, and no default applies; or an `into_kit` answer
//                   that is not true (an assembly, or no kit consumes the line).
//   sku           — `existing` with no SKU picked.
//   new_sku_name  — `new` with no name (blank after trimming).
//   with_line_id  — `with_item` with no item picked, or one no longer on the
//                   order, or one that itself goes into this line
//                   (fulfillmentHostLeadsBack). Assembly items included.
//
// It never asks "does this company have an agreement?" — that answer comes from
// a network call which degrades to false, so a gate keyed on it would make
// submission depend on Nexus being reachable and would let the client pre-block
// and the backend gate disagree whenever the two calls landed differently. The
// agreement changes the COPY, not the gate.
export function fulfillmentTargetsMissingFieldKeys(lines, orderFulfillment) {
    const missing = [];
    for (const line of lines) {
        if (!configIsConfigured(line.config))
            continue;
        if (resolveLineFulfillmentTarget(line, { lines, orderFulfillment }).kind !== 'unanswered')
            continue;
        const explicit = configFulfillmentTarget(line.config);
        const leaf = explicit?.choice === 'existing'
            ? 'sku'
            : explicit?.choice === 'new'
                ? 'new_sku_name'
                : explicit?.choice === 'with_item'
                    ? 'with_line_id'
                    : 'choice';
        missing.push(fulfillmentTargetFieldKey(line.id, leaf));
    }
    return missing;
}
// Every configured line's resolved target, in its wire shape (FulfillmentTargetWire
// above) — the ONE builder (R7). Kit references are mapped to the kits' wire ids,
// the same ids those kits' own projects carry.
export function fulfillmentTargetsForTransmit(lines, orderFulfillment) {
    const wireId = (line) => line.draft_line_id ?? line.id;
    const wireIdById = new Map(lines.map((l) => [l.id, wireId(l)]));
    const out = [];
    for (const line of lines) {
        if (!configIsConfigured(line.config))
            continue;
        const resolved = resolveLineFulfillmentTarget(line, { lines, orderFulfillment });
        const entry = { draft_line_id: wireId(line), disposition: resolved.kind };
        const title = typeof line.title === 'string' ? line.title.trim() : '';
        if (title)
            entry.title = title;
        const productType = configRecord(line.config)?.product_type;
        if (typeof productType === 'string')
            entry.product_type = productType;
        if (resolved.kind === 'existing_sku') {
            entry.sku = resolved.sku;
            if (resolved.sku_snapshot)
                entry.sku_snapshot = resolved.sku_snapshot;
        }
        else if (resolved.kind === 'new_sku') {
            entry.new_sku_name = resolved.new_sku_name;
        }
        else if (resolved.kind === 'kit_own_sku') {
            entry.sku = resolved.sku;
        }
        else if (resolved.kind === 'into_kit') {
            entry.into_kit_draft_line_ids = resolved.kit_line_ids.map((id) => wireIdById.get(id) ?? id);
        }
        else if (resolved.kind === 'with_item') {
            entry.with_draft_line_ids = resolved.host_line_ids.map((id) => wireIdById.get(id) ?? id);
        }
        out.push(entry);
    }
    return out;
}
// The order services as every feed sends them: the stored snapshot, with the
// resolved targets riding the fulfillment block. Attached when fulfillment is the
// delivery mode, and ALSO when it is not but a line still holds an answer of its
// own — the strip takes `fulfillment_target` off the line's project, so this
// block is the only carrier that captured value has (R5), exactly as a toggled-
// off fulfillment block itself keeps crossing.
export function orderServicesForTransmit(snapshot, lines) {
    const fulfilled = resolveDeliveryMode(snapshot) === 'fulfillment';
    const anyLineAnswer = lines.some((l) => configIsConfigured(l.config) &&
        normalizeLineFulfillmentTarget(configRecord(l.config)?.[FULFILLMENT_TARGET_CONFIG_KEY]) !== undefined);
    const sent = withTransmittedAccount(snapshot);
    if (!fulfilled && !anyLineAnswer)
        return sent;
    const base = sent ?? { services_requested: [] };
    return {
        ...base,
        fulfillment: {
            ...(base.fulfillment ?? {}),
            targets: fulfillmentTargetsForTransmit(lines, base.fulfillment),
        },
    };
}
// The carrier account crosses only where it applies (thirdPartyAccountForTransmit)
// and trimmed. Switching to Pickup or Fulfillment keeps the shipping answers, so a
// stored account outlives the mode it belongs to and must not ride along.
function withTransmittedAccount(snapshot) {
    if (!snapshot || snapshot.third_party_account === undefined)
        return snapshot ?? null;
    const { third_party_account: _stored, ...rest } = snapshot;
    const account = thirdPartyAccountForTransmit(snapshot);
    return account === null ? rest : { ...rest, third_party_account: account };
}
// All missing keys across the SELECTED services. Shared by the SPA pre-block
// AND the authoritative backend gate (services/order-services/order-services-
// gate.ts) so the two can never disagree. Shipping completeness stays in
// shipToMissingFieldKeys (the address lives in its own snapshot), so this
// covers fulfillment and the shipping speed. An assembly item's kit is judged by
// lineConfigIssues, with the rest of its line. An UNANSWERED mode is reported by
// shipToMissingFieldKeys alone (DELIVERY_MODE_FIELD_KEY), never twice.
//
// Fulfillment is judged per line (fulfillmentTargetsMissingFieldKeys): `lines` is
// the order's ACTIVE lines, id + config. Required, so a caller cannot forget them
// and silently pass a fulfilled order with nothing judged.
export function orderServicesMissingFieldKeys(s, opts) {
    const mode = resolveDeliveryMode(s);
    if (!s || mode === null)
        return [];
    const missing = [];
    if (mode === 'fulfillment') {
        missing.push(...fulfillmentTargetsMissingFieldKeys(opts.lines, s.fulfillment));
    }
    // Shipping: the destination ADDRESS is gated separately (shipToMissingFieldKeys
    // on the ship_to snapshot); here we require the SPEED pick when shipping.
    if (mode === 'shipping' && !s.shipping_service) {
        missing.push('order_services.shipping_service');
    }
    // Optional, so never missing — but Nexus refuses it past its column width.
    // Judged only while it is on screen and on the wire: under Shipping.
    if (mode === 'shipping' && thirdPartyAccountTooLong(s.third_party_account)) {
        missing.push(THIRD_PARTY_ACCOUNT_FIELD_KEY);
    }
    return missing;
}
// The ceiling POST/PATCH /api/quotes enforces on a line quantity.
export const MAX_LINE_QUANTITY = 10_000_000;
// Whether a scannable barcode (UPC/EAN/tracking code) is REQUIRED on the
// printed piece, given the quote-level order services. A barcode is required
// only when Argo will warehouse and FULFILL the order from inventory — every
// pick pulled from stock is scanned, so the artwork must carry one. Plain print
// that ships once (or is handed back) and pieces never inventoried (business
// cards, marketing inserts) don't need one. Retail "sold in a store" also needs
// one, but the portal has no field to detect that, so the optional advisory copy
// surfaces it as guidance rather than forcing it here. Drives the customer-facing
// barcode advisory's Required-vs-Optional wording (ArtworkBarcodeNotice) — it is
// NOT a submit/price gate (V1), purely the message context.
export function orderRequiresBarcode(s) {
    return resolveDeliveryMode(s) === 'fulfillment';
}
// Item 6b — the barcode requirement is now a real SUBMIT GATE (not just advisory
// copy). When the order is fulfilled (orderRequiresBarcode), the customer must
// resolve the barcode question before submitting: either their artwork already
// carries a barcode, or they ask Argo to apply one. It is AUTO-SATISFIED when the
// upload barcode scan already detected a symbol on the required artwork — then no
// explicit answer is needed. Returns true when the gate is UNMET (block submit).
//
// `barcodeDetected` is the caller's asset-derived signal: true when the required
// artwork scanned as `found`. The scan is only a hint (never blocks on its own),
// so a `missing`/`unknown` scan simply falls through to requiring the explicit
// pick — which the customer can answer "my art has one" to. Kept OUT of
// orderServicesMissingFieldKeys (which is pure over stored data and reused where
// assets aren't in scope); callers with asset context add this check alongside.
export function orderBarcodeResolutionMissing(s, opts) {
    if (!orderRequiresBarcode(s))
        return false; // not a fulfilled order → N/A
    if (opts.barcodeDetected)
        return false; // scan already found one → auto-satisfied
    return !s?.fulfillment?.barcode_resolution; // else an explicit answer is required
}
// The field key the barcode gate reports (for the field-keyed failure summary /
// scroll-to-field UX), mirroring the other fulfillment keys.
export const BARCODE_RESOLUTION_FIELD_KEY = `${FULFILLMENT_FIELD_PREFIX}.barcode_resolution`;
// The optional barcode-label add-on on an order that ships or is picked up. It is
// the same work, and the same small fee, as fulfillment's `apply_barcode`
// answer, which Nexus already turns into a line on its Artwork Barcode Service.
// So it crosses as a Pattern B project on that Service's family_key, emitted
// verbatim (the ORDER_SERVICE_FAMILY_KEYS posture), and the title matches the
// line Nexus builds for the fulfillment answer. Never a gate — nothing about a
// shipped or collected order needs a barcode the way a scan-picked stocked order
// does. Intent only: Nexus prices it, the portal shows no number (R3).
export const ARTWORK_BARCODE_FAMILY_KEY = 'artwork_barcode';
export const ARTWORK_BARCODE_LABEL = 'Artwork barcode';
export const APPLY_BARCODE_FIELD_KEY = 'order_services.apply_barcode';
// Does this order ask for the add-on? Pure over the stored snapshot (R26): the
// flag counts on Shipping or Pickup, and is dormant on Fulfillment, which asks
// its own mandatory barcode question instead. The control is only on screen
// under Shipping or Pickup, so "not fulfillment" is the whole test.
export function orderRequestsBarcodeLabels(s) {
    return !!s && s.apply_barcode === true && !orderRequiresBarcode(s);
}
// Which mode wins when a stored array holds more than one. Fulfillment first —
// the deliberately-added, information-richer mode that owns its own sub-fields
// and was never a default — then Shipping, then Pickup.
const DELIVERY_MODE_PRECEDENCE = ['fulfillment', 'shipping', 'pickup'];
// How the finished order leaves the building, or null when the customer has not
// said. The ONE place the order's delivery mode is decided: the ship-to rows'
// `delivery_method` is a stamp derived from this (reconcileDeliveryIntent), and
// every gate, form and feed reads the mode through here.
//
// Null for an absent snapshot, and for one whose array names no mode. A quote
// saved while Pickup was inferred from "neither Shipping nor Fulfillment" stores
// exactly that empty array, so it reads as unanswered here — never as pickup —
// and the submit gate asks the customer rather than reporting a guess to Nexus.
// Pure over one stored value (R26).
export function resolveDeliveryMode(s) {
    if (!s || !Array.isArray(s.services_requested))
        return null;
    return DELIVERY_MODE_PRECEDENCE.find((m) => s.services_requested.includes(m)) ?? null;
}
// Defensive normalizer for the persisted order_services_snapshot JSON (DB →
// DTO). Returns null for absent/malformed data; otherwise narrows to the
// OrderServicesSnapshot shape, dropping junk. Mirrors normalizeShipTo.
export function normalizeOrderServices(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const r = raw;
    const requested = Array.isArray(r.services_requested)
        ? r.services_requested.filter((v) => ORDER_SERVICES.includes(v))
        : [];
    // The three modes are MUTUALLY EXCLUSIVE (exactly one per order — how the
    // finished order leaves the building). Shipping and Fulfillment were once
    // independent multi-select toggles, so a quote persisted under that old
    // behavior can carry BOTH. Heal to the one mode resolveDeliveryMode reads, so
    // stale rows reach the form, the missing-field gate and the Nexus feeds as a
    // single valid mode. An array with none of the three stays EMPTY: that is the
    // unanswered state, never a pickup.
    const mode = DELIVERY_MODE_PRECEDENCE.find((m) => requested.includes(m));
    const out = { services_requested: mode ? [mode] : [] };
    if (SHIPPING_SERVICES.includes(r.shipping_service)) {
        out.shipping_service = r.shipping_service;
    }
    // Kept verbatim and at any length: the editor re-reads its draft through here
    // while the customer types, so trimming would eat a space mid-word, and
    // truncating would change the account they gave. The trim is applied on the
    // way out (thirdPartyAccountForTransmit), the ceiling by the validator + save.
    if (out.shipping_service === 'customer_labels' &&
        typeof r.third_party_account === 'string' &&
        r.third_party_account.trim() !== '') {
        out.third_party_account = r.third_party_account;
    }
    if (r.apply_barcode === true)
        out.apply_barcode = true;
    if (typeof r.fulfillment === 'object' && r.fulfillment !== null) {
        const f = r.fulfillment;
        const ful = {};
        if (BARCODE_RESOLUTIONS.includes(f.barcode_resolution)) {
            ful.barcode_resolution = f.barcode_resolution;
        }
        if (FULFILLMENT_SKU_CHOICES.includes(f.sku_choice)) {
            ful.sku_choice = f.sku_choice;
        }
        if (typeof f.sku === 'string' && f.sku.length > 0)
            ful.sku = f.sku;
        if (typeof f.new_sku_name === 'string' && f.new_sku_name.length > 0) {
            ful.new_sku_name = f.new_sku_name;
        }
        const skuSnapshot = normalizeFulfilledSkuSnapshot(f.sku_snapshot);
        if (skuSnapshot)
            ful.sku_snapshot = skuSnapshot;
        out.fulfillment = ful;
    }
    return out;
}
// A picked fulfilled SKU's frozen display fields, rebuilt from stored JSON. Read
// by every place a fulfilled SKU is picked: a line's own target, and the legacy
// order-wide answer and assembly-item remap it superseded.
function normalizeFulfilledSkuSnapshot(raw) {
    if (typeof raw !== 'object' || raw === null)
        return undefined;
    const snap = raw;
    const sk = {
        name: typeof snap.name === 'string' ? snap.name : '',
        kind: snap.kind === 'kit' ? 'kit' : 'finished_good',
    };
    if (typeof snap.component_count === 'number')
        sk.component_count = snap.component_count;
    return sk;
}
// ONE assembly snapshot — an `assembly` item's kit at QuoteLine.config.assembly —
// rebuilt from raw stored JSON through the one closed-allowlist reconstructor.
// The kit reaches the database as untyped JSON riding the line's config, so every
// reader of it (the per-line transmit strip, the clone remap, the per-line half
// of the config validator) rebuilds it here rather than casting and trusting it.
//
// `scope` additionally checks the identifiers the snapshot CLAIMS against what
// the caller knows really exists — the quote's own active lines and the
// Company's kit catalog. Callers with that context pass it (the write boundary);
// callers without it pass nothing and the checks are skipped rather than failed.
// Use normalizeAssemblySnapshotScoped when you need to know what it rejected.
export function normalizeAssemblySnapshot(raw, scope) {
    return normalizeAssemblySnapshotScoped(raw, scope).assembly;
}
// normalizeAssemblySnapshot plus the field keys `scope` rejected. Same single
// implementation, so the two can never disagree about what survived.
export function normalizeAssemblySnapshotScoped(raw, scope) {
    const rejected = [];
    const assembly = buildAssemblySnapshot(raw, scope, rejected);
    return { assembly, rejected };
}
// One value against a closed option set, and the multi-valued form of the same
// question. Both are used only by the assembly detail axes, whose option arrays
// are the same ones the seed builds its FormFieldEnumOption rows from — so
// "accepted here" and "offered there" are one list rather than two.
function acceptedOption(raw, options) {
    return options.includes(raw) ? raw : undefined;
}
// Rebuilt from canonical order, so a posted array is deduped and re-ordered on
// the way in. Returns undefined for an empty result, which keeps an unanswered
// axis off the snapshot entirely rather than storing an empty array.
function acceptedOptions(raw, options) {
    if (!Array.isArray(raw))
        return undefined;
    const posted = raw;
    const picked = options.filter((option) => posted.includes(option));
    return picked.length > 0 ? picked : undefined;
}
// Free text off an untrusted snapshot: trimmed, and undefined when nothing is
// left. Used by the nine `<axis>_other` describe-it boxes, which need exactly the
// treatment assembly_instructions already gets — a whitespace-only answer is not
// an answer, and one stored would read back as though the pick had been spelled
// out.
function acceptedText(raw) {
    if (typeof raw !== 'string')
        return undefined;
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}
function buildAssemblySnapshot(raw, scope, rejected) {
    const asm = {};
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return asm;
    {
        const a = raw;
        if (ASSEMBLY_MODES.includes(a.assembly_mode)) {
            asm.assembly_mode = a.assembly_mode;
        }
        // Rebuilt from the canonical key set rather than filtered in place, which
        // dedupes and fixes the stored order in one move (the same posture
        // services_requested takes). Nothing captured is dropped when the mode
        // switches back to a kit build — Nexus decides what a kit build that also
        // relabels stock is, not the portal (ADR-013).
        if (Array.isArray(a.rework_actions)) {
            const posted = a.rework_actions;
            const actions = REWORK_ACTIONS.filter((key) => posted.includes(key));
            if (actions.length > 0)
                asm.rework_actions = actions;
        }
        if (typeof a.kit_components_count === 'number' && Number.isFinite(a.kit_components_count)) {
            asm.kit_components_count = a.kit_components_count;
        }
        if (KIT_COMPONENT_SOURCES.includes(a.kit_components_supplied)) {
            asm.kit_components_supplied = a.kit_components_supplied;
        }
        if (KIT_TYPES.includes(a.kit_type))
            asm.kit_type = a.kit_type;
        if (typeof a.purchased_labels === 'boolean')
            asm.purchased_labels = a.purchased_labels;
        if (typeof a.units_per_pack === 'number' && Number.isFinite(a.units_per_pack)) {
            asm.units_per_pack = a.units_per_pack;
        }
        if (typeof a.assembly_instructions === 'string') {
            const trimmed = a.assembly_instructions.trim();
            if (trimmed)
                asm.assembly_instructions = trimmed;
        }
        // ── the eight packing-detail axes ──────────────────────────────────────
        // Checked against the SAME arrays the seed builds its option rows from, so
        // the write boundary admits exactly the set the picker offers. That is what
        // lets this stay the ONE acceptance check for these axes: a schema-side
        // gate beside it could only ever agree, and a third judge of one fact is
        // what ADR-011 exists to prevent (ADR-018 Decision 5).
        //
        // A multi-valued axis is rebuilt from canonical order rather than filtered
        // in place, which dedupes and fixes the stored order in one move — the same
        // posture rework_actions and services_requested take.
        const fillerTypes = acceptedOptions(a.filler_type, FILLER_TYPES);
        if (fillerTypes)
            asm.filler_type = fillerTypes;
        const fillerColors = acceptedOptions(a.filler_color, FILLER_COLORS);
        if (fillerColors)
            asm.filler_color = fillerColors;
        const closure = acceptedOption(a.closure_method, CLOSURE_METHODS);
        if (closure)
            asm.closure_method = closure;
        const boxFinish = acceptedOption(a.box_finish, BOX_FINISHES);
        if (boxFinish)
            asm.box_finish = boxFinish;
        const overboxing = acceptedOption(a.overboxing, OVERBOXING_CHOICES);
        if (overboxing)
            asm.overboxing = overboxing;
        const labelPurposes = acceptedOptions(a.label_purpose, LABEL_PURPOSES);
        if (labelPurposes)
            asm.label_purpose = labelPurposes;
        const labelPlacement = acceptedOption(a.label_placement, LABEL_PLACEMENTS);
        if (labelPlacement)
            asm.label_placement = labelPlacement;
        const specialActions = acceptedOptions(a.special_actions, SPECIAL_ACTIONS);
        if (specialActions)
            asm.special_actions = specialActions;
        // The describe-it text behind each `other` pick. Trimmed, and dropped when
        // what is left is empty — exactly as assembly_instructions is: a
        // whitespace-only answer is not an answer, and storing one would make the
        // recap read as though the pick had been spelled out.
        //
        // Accepted WITHOUT re-checking that the axis still holds `other`. The
        // schema's conditional_show is the one author of that visibility, evaluated
        // by scrubConfigForVisibility at the write chokepoint; a second judge here
        // could only ever agree with it or silently destroy a value (ADR-011), and
        // ADR-018 Decision 9's "nothing captured is dropped" points the same way.
        const fillerTypeOther = acceptedText(a.filler_type_other);
        if (fillerTypeOther)
            asm.filler_type_other = fillerTypeOther;
        const fillerColorOther = acceptedText(a.filler_color_other);
        if (fillerColorOther)
            asm.filler_color_other = fillerColorOther;
        const closureOther = acceptedText(a.closure_method_other);
        if (closureOther)
            asm.closure_method_other = closureOther;
        const boxFinishOther = acceptedText(a.box_finish_other);
        if (boxFinishOther)
            asm.box_finish_other = boxFinishOther;
        const overboxingOther = acceptedText(a.overboxing_other);
        if (overboxingOther)
            asm.overboxing_other = overboxingOther;
        const labelPurposeOther = acceptedText(a.label_purpose_other);
        if (labelPurposeOther)
            asm.label_purpose_other = labelPurposeOther;
        const labelPlacementOther = acceptedText(a.label_placement_other);
        if (labelPlacementOther)
            asm.label_placement_other = labelPlacementOther;
        const specialActionsOther = acceptedText(a.special_actions_other);
        if (specialActionsOther)
            asm.special_actions_other = specialActionsOther;
        const reworkActionsOther = acceptedText(a.rework_actions_other);
        if (reworkActionsOther)
            asm.rework_actions_other = reworkActionsOther;
        // The picked kit must be one of the Company's own (defect D4: this accepted
        // any non-empty string, persisted it and transmitted it, so a client could
        // claim another Company's kit SKU). Checked only when the caller supplied a
        // catalog — a Nexus outage must not delete a customer's kit pick, so an
        // absent set means "not checkable here". A refused bom_id takes its display
        // snapshot with it: the snapshot describes the kit we just refused.
        let bomAccepted = false;
        if (typeof a.bom_id === 'string' && a.bom_id.length > 0) {
            if (scope?.bom_ids && !scope.bom_ids.includes(a.bom_id)) {
                rejected.push({ field_key: ASSEMBLY_BOM_FIELD_KEY, value: a.bom_id });
            }
            else {
                asm.bom_id = a.bom_id;
                bomAccepted = true;
            }
        }
        if (bomAccepted && typeof a.bom_snapshot === 'object' && a.bom_snapshot !== null) {
            const snap = a.bom_snapshot;
            const components = Array.isArray(snap.components)
                ? snap.components.map(normalizeBomComponent).filter((c) => c !== null)
                : [];
            const bs = {
                name: typeof snap.name === 'string' ? snap.name : '',
                component_count: typeof snap.component_count === 'number' && Number.isFinite(snap.component_count)
                    ? snap.component_count
                    : components.length,
                components,
            };
            if (typeof snap.bom_number === 'string' && snap.bom_number.length > 0) {
                bs.bom_number = snap.bom_number;
            }
            asm.bom_snapshot = bs;
        }
        // A fulfillment remap takes its display snapshot with it, and the snapshot
        // never stands alone: it describes the SKU that was picked.
        if (typeof a.fulfillment_sku === 'string' && a.fulfillment_sku.length > 0) {
            asm.fulfillment_sku = a.fulfillment_sku;
            const snap = normalizeFulfilledSkuSnapshot(a.fulfillment_sku_snapshot);
            if (snap)
                asm.fulfillment_sku_snapshot = snap;
        }
        if (Array.isArray(a.kit_contents)) {
            const contents = a.kit_contents
                .map((row) => normalizeKitContentRow(row, { ...(scope ? { scope } : {}), rejected }))
                .filter((row) => row !== null);
            if (contents.length > 0)
                asm.kit_contents = contents;
        }
        // The build recipe. No scope check: a step carries no identifier that names
        // anything outside this snapshot (contrast bom_id and a row's line_id, both
        // of which claim something owned elsewhere and are checked above).
        if (Array.isArray(a.build_steps)) {
            const steps = a.build_steps
                .map(normalizeBuildStep)
                .filter((step) => step !== null);
            if (steps.length > 0)
                asm.build_steps = steps;
        }
        // kit_components_count is DERIVED once rows exist on a kit build, and this
        // is where it is PERSISTED — this reconstructor is on the write path
        // (assemblyItemWriteDecision) as well as the read path, so writing it here is
        // what keeps R26 satisfied: the stored value already equals the derivation,
        // and nothing recomputes it at transmit time.
        const derivedCount = deriveKitComponentsCount(asm);
        if (typeof derivedCount === 'number')
            asm.kit_components_count = derivedCount;
        const derivedSupplied = deriveKitComponentsSupplied(asm);
        if (derivedSupplied)
            asm.kit_components_supplied = derivedSupplied;
    }
    return asm;
}
// Project ONE assembly snapshot into its wire shape — the one strip (R7). An
// item's snapshot, which lives at QuoteLine.config.assembly, reaches it via the
// per-line config strip (stripFinishing). No feed may spread a stored assembly
// snapshot itself: a raw row id and a raw QuoteLine id are meaningless outside
// this database and ADR-018 Decision 3 is what keeps them out.
//
// `lines` are the quote's own active lines, used both to resolve a row's
// description and to drop a reference that does not belong to this quote. A
// dangling reference therefore cannot reach a feed even if one were persisted.
export function assemblyForTransmit(assembly, lines) {
    const { kit_contents, build_steps, ...restAssembly } = assembly;
    const transmitAssembly = { ...restAssembly };
    // The recipe crosses with its positions resolved. Nothing is stripped here —
    // a step names only itself, and its step_id is what the submit-time artwork
    // manifest stamps on each reference photo so the two halves join on the far
    // side (the receiver is still owed; see the tracker's R5 table).
    if (Array.isArray(build_steps) && build_steps.length > 0) {
        transmitAssembly.build_steps = build_steps.map(resolveBuildStep);
    }
    if (Array.isArray(kit_contents) && kit_contents.length > 0) {
        const linesById = new Map(lines.map((l) => [l.id, l]));
        const lineIds = lines.map((l) => l.id);
        const surviving = kit_contents.filter((row) => !row.line_id || lineIds.includes(row.line_id));
        transmitAssembly.kit_contents = surviving.map((row) => resolveKitContentRow(row, linesById));
        // BOTH values derived from these rows are re-derived from the rows actually
        // SENT, never carried over from the stored snapshot. The filter above can drop
        // a row — a stored reference whose line has since gone away — and a spread of
        // the stored assembly would then ship "3 components" alongside 2 items, and
        // `mixed` sourcing against rows that are now all partner-supplied. The count a
        // rep can eyeball against the list; the supply source they cannot, so it is
        // the more dangerous of the two: nothing on the imported Note contradicts it.
        //
        // Through the SAME two derivations normalizeAssemblySnapshot persists from, given
        // the same rows, so the wire can never state something a save would not. That
        // is the whole reason there is no arithmetic here: `surviving.length` and a
        // hand-rolled empty case would be a second author of one fact, and the two
        // disagree at the edges — with nothing left to derive FROM, the derivations
        // let the ENTERED answers stand (the legacy shape: a kit described by hand,
        // with a count and no itemized list), and the write boundary's own heal does
        // exactly the same. A rework needs no special case for the same reason: both
        // derivations already stop there, because its rows are the STOCK being
        // reworked rather than a kit's contents.
        //
        // The write boundary heals the stored state too (assemblyItemWriteDecision),
        // so this is the second of two independent defences and not the only one.
        if (surviving.length !== kit_contents.length) {
            const survivingAssembly = { ...assembly, kit_contents: surviving };
            const count = deriveKitComponentsCount(survivingAssembly);
            const supplied = deriveKitComponentsSupplied(survivingAssembly);
            if (typeof count === 'number')
                transmitAssembly.kit_components_count = count;
            else
                delete transmitAssembly.kit_components_count;
            if (supplied)
                transmitAssembly.kit_components_supplied = supplied;
            else
                delete transmitAssembly.kit_components_supplied;
        }
    }
    return transmitAssembly;
}
// ── Book inserts — extra pieces bindery puts into a bound book ──
//
// The 2026-10-14 review: a coil book with a folded 11×17 poster at the back was
// being entered as an assembly, but assembly is the assembly division's work and
// this is an in-house bindery job. So an insert is a finishing touch on the BOOK
// line, the same shape as tab dividers: its own Pattern B op (`inserts`) priced
// per piece × inserts per book, with the insertion method as its one priced axis.
// A book can carry several, each a different piece — usually another project on
// this same order, printed alongside the book.
//
// The list lives at `QuoteLine.config.inserts` as AUTHORED rows (InsertRow) and
// crosses resolved (ResolvedInsert). Same split as kit contents: a row_id is
// meaningless outside this database and never transmits. A referenced line
// crosses as its WIRE id (`draft_line_id ?? id`), the id that line's own project
// already carries on the same payload — so it resolves on the far side, which is
// the test ADR-018 applies to a portal id (the into_kit_draft_line_ids precedent).
export const INSERTS_TRIGGER_KEY = 'inserts_included';
export const INSERTS_CONFIG_KEY = 'inserts';
export const INSERTS_COUNT_KEY = 'inserts_count';
export const INSERTS_METHOD_KEY = 'inserts_method';
export const INSERTS_FAMILY_KEY = 'inserts';
export const INSERT_ORIGINS = ['quote_line', 'described'];
export const INSERT_ORIGIN_LABELS = {
    quote_line: 'Another item on this order',
    described: "Something else: I'll describe it",
};
// Where the insert goes. `back` leads: the case that started this (a poster as
// the book's last page) and the common one.
export const INSERT_PLACEMENTS = ['back', 'front', 'after_page'];
export const INSERT_PLACEMENT_LABELS = {
    back: 'At the back',
    front: 'At the front',
    after_page: 'After a page',
};
// How a DESCRIBED insert prints. A described insert is one flat sheet, so its
// ink is the flat-work N/M set (PRINT_INKS, front/back wording), plus `supplied`
// for a piece the customer sends already printed — the one answer that tells
// Nexus to price the insertion and no printing. A `quote_line` insert carries
// none of this: that project's own ink and stock are already on its own line.
export const INSERT_INKS = [...PRINT_INKS, 'supplied'];
export const INSERT_INK_LABELS = {
    '4_4': 'Full color, both sides',
    '4_1': 'Color front, black back',
    '4_0': 'Full color, front only',
    '1_1': 'Black, both sides',
    '1_0': 'Black, front only',
    supplied: 'Already printed: you send them',
};
export const INSERTS_LINE_REF_FIELD_KEY = `${INSERTS_CONFIG_KEY}.line_id`;
function positiveWhole(v) {
    if (typeof v !== 'number' || !Number.isFinite(v))
        return undefined;
    const n = Math.round(v);
    return n >= 1 ? n : undefined;
}
// One authored row, defensively. Same posture as normalizeKitContentRow: a line
// reference outside `scope.line_ids` is pushed onto `rejected` and the row
// demotes to `described` (keeping its frozen description); a row that names
// nothing at all is dropped.
export function normalizeInsertRow(raw, opts) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return null;
    const r = raw;
    if (typeof r.row_id !== 'string' || r.row_id.length === 0)
        return null;
    if (!INSERT_ORIGINS.includes(r.origin))
        return null;
    let origin = r.origin;
    const description = typeof r.description === 'string' && r.description.trim().length > 0
        ? r.description.trim()
        : undefined;
    let line_id;
    if (typeof r.line_id === 'string' && r.line_id.length > 0) {
        const lineIds = opts?.scope?.line_ids;
        if (lineIds && !lineIds.includes(r.line_id)) {
            opts?.rejected?.push({ field_key: INSERTS_LINE_REF_FIELD_KEY, value: r.line_id });
        }
        else {
            line_id = r.line_id;
        }
    }
    if (origin === 'quote_line' && !line_id)
        origin = 'described';
    if (origin === 'described' && !description)
        return null;
    const out = { row_id: r.row_id, origin };
    if (line_id && origin === 'quote_line')
        out.line_id = line_id;
    if (description)
        out.description = description;
    const qty = positiveWhole(r.quantity_per_book);
    if (qty !== undefined)
        out.quantity_per_book = qty;
    if (INSERT_PLACEMENTS.includes(r.placement)) {
        out.placement = r.placement;
        const page = positiveWhole(r.placement_page);
        if (out.placement === 'after_page' && page !== undefined)
            out.placement_page = page;
    }
    if (origin === 'described' && INSERT_INKS.includes(r.ink)) {
        out.ink = r.ink;
        if (out.ink !== 'supplied' && INSERT_PAPERS.includes(r.paper)) {
            out.paper = r.paper;
        }
    }
    if (typeof r.notes === 'string' && r.notes.trim().length > 0)
        out.notes = r.notes.trim();
    return out;
}
export function normalizeInsertRows(raw, opts) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const r of raw) {
        const row = normalizeInsertRow(r, opts);
        if (row)
            out.push(row);
    }
    return out;
}
// Inserts per book across every row — the `inserts` op's per-book count, so the
// op prices on line quantity × this (the tabs basis). A pure function of the
// stored rows; written beside them on every save so the two never disagree.
export function insertsPerBook(rows) {
    return rows.reduce((sum, r) => sum + (positiveWhole(r.quantity_per_book) ?? 1), 0);
}
// A line config with its insert rows replaced and the per-book count re-derived
// from them. The ONE writer of the pair — the editor and the write boundary both
// call it.
export function withInsertRows(config, rows) {
    const { [INSERTS_CONFIG_KEY]: _rows, [INSERTS_COUNT_KEY]: _count, ...rest } = config;
    if (rows.length === 0)
        return rest;
    return { ...rest, [INSERTS_CONFIG_KEY]: [...rows], [INSERTS_COUNT_KEY]: insertsPerBook(rows) };
}
// Project stored rows into their wire shape — the one strip for this axis (R7).
// A reference to a line not in `lines` (deleted, another quote's, or one this
// feed does not emit) demotes to its frozen description and loses its wire id;
// with no description it is dropped, never sent unresolved.
export function insertsForTransmit(raw, lines) {
    const linesById = new Map(lines.map((l) => [l.id, l]));
    const out = [];
    for (const row of normalizeInsertRows(raw)) {
        const line = row.line_id ? linesById.get(row.line_id) : undefined;
        // A resolvable line is named as it stands now — the piece IS that project, and
        // the customer's read-back shows its current name. The frozen description
        // speaks only for a line that no longer resolves.
        const description = line ? lineDescription(line) : row.description;
        if (!description)
            continue;
        const resolved = {
            position: out.length + 1,
            source: line ? 'quote_line' : 'described',
            description,
            quantity_per_book: row.quantity_per_book ?? 1,
        };
        if (row.placement)
            resolved.placement = row.placement;
        if (row.placement_page !== undefined)
            resolved.placement_page = row.placement_page;
        if (!line && row.ink)
            resolved.ink = row.ink;
        if (!line && row.paper)
            resolved.paper = row.paper;
        if (row.notes)
            resolved.notes = row.notes;
        if (line)
            resolved.source_draft_line_id = line.draft_line_id ?? line.id;
        out.push(resolved);
    }
    return out;
}
// Every identifier a STORED assembly snapshot already claims — the kit's line
// references and its picked BOM. The write boundary compares a rejection against
// this to tell "just claimed" from "already here, now orphaned".
function assemblyIdentifiers(raw) {
    const assembly = normalizeAssemblySnapshot(raw);
    const out = [];
    if (assembly.bom_id)
        out.push(assembly.bom_id);
    for (const row of assembly.kit_contents ?? []) {
        if (row.line_id)
            out.push(row.line_id);
    }
    return out;
}
// Every build-step id THE QUOTE holds: each `assembly` item's own recipe on
// `QuoteLine.config.assembly`, normalized the same way every read of it is. The
// submit-time artwork manifest joins each reference photo's
// Asset.assembly_step_id against this so a photo whose step has since been
// removed crosses WITHOUT a scope that names nothing.
//
// A `step_id` is a `crypto.randomUUID`, so it appears in exactly one recipe and
// the union loses nothing by flattening. That is also what lets the far side
// attribute a photo without a discriminator on the wire — every recipe transmits
// its steps through `assemblyForTransmit`, so a stamped `step_id` resolves to
// exactly one project's `build_steps`. The step-LESS finished-kit photo has no
// such key, which is why ITS scope is the Asset's own `quote_line_id` instead.
export function quoteBuildStepIds(line_configs) {
    const ids = [];
    for (const config of line_configs) {
        if (typeof config !== 'object' || config === null || Array.isArray(config))
            continue;
        const raw = config[ASSEMBLY_ITEM_CONFIG_KEY];
        if (raw == null)
            continue;
        for (const step of normalizeAssemblySnapshot(raw).build_steps ?? [])
            ids.push(step.step_id);
    }
    return ids;
}
// Narrow each artwork row's step scope to the steps the recipe actually holds.
// The FILE always survives; only a correlation key that names no transmitted step
// is dropped, so a rep never reads a scope pointing at nothing.
//
// A derived field and the thing it is derived from have to be filtered TOGETHER
// — the same rule assemblyForTransmit keeps when it recomputes the contents
// count from the rows it actually sends. Pure and generic over the row shape, so
// it is testable without a database.
export function scopeStepPhotosToRecipe(rows, stepIds) {
    const known = new Set(stepIds);
    return rows.map((row) => row.assembly_step_id && !known.has(row.assembly_step_id)
        ? { ...row, assembly_step_id: null }
        : row);
}
// The line a file belongs to, in the SAME identifier the rest of the wire uses.
//
// `Asset.quote_line_id` is a portal QuoteLine.id, which names nothing on the far
// side: every feed refers to a line by `draft_line_id ?? line.id` (the id the
// pricing projects[] carry and ERP idempotency is keyed on), so emitting the raw
// column would hand Nexus a correlation key that joins to nothing whenever a line
// has a draft id — i.e. every portal-originated line. `draftIdByLineId` is that
// mapping, built from the lines the quote actually transmits.
//
// A row whose line is not in the map is line-LESS on the wire, not dropped —
// the same rule scopeStepPhotosToRecipe keeps for a removed step: the FILE always
// crosses, only a scope that would resolve to nothing is cleared. That covers
// both a line-less upload and a photo whose line was since removed (the FK is ON DELETE SET NULL, but a soft-deleted line keeps the
// column populated while dropping out of the transmitted set).
//
// This is what tells N finished-kit photos apart. `assembly_kit_photo` is
// step-LESS by construction, so it has no `assembly_step_id` to carry its recipe;
// migration 20260822120000 put the line in its ROW identity for exactly that
// reason, and without this the manifest stored the distinction and then failed to
// transmit it — N kit photos arriving indistinguishable.
export function scopeAssetLinesToDraftIds(rows, draftIdByLineId) {
    return rows.map(({ quote_line_id, ...rest }) => ({
        ...rest,
        draft_line_id: quote_line_id ? (draftIdByLineId.get(quote_line_id) ?? null) : null,
    }));
}
// What the write boundary should DO about an assembly item's kit
// (`QuoteLine.config.assembly`), which reaches the database as untyped JSON
// riding the line's own config: rebuilt through the one closed-allowlist
// reconstructor, judged against what this quote and this Company actually own,
// and split refuse-from-heal.
//
//   refuse — the identifier is not in the stored snapshot, so this payload just
//            claimed it. A legitimate client never posts a line id or kit SKU it
//            was not offered, so this is a bug or an attempt: answer 400 and
//            persist nothing (R32).
//   heal   — the identifier WAS stored and stopped resolving, which is what
//            happens when the customer removes a line a kit row referenced.
//            Refusing that would make the removal unsavable and strand the
//            customer on an error they cannot clear; persist the healed snapshot.
//
// There is no second judge of what an assembly snapshot may contain (R12): the
// nine packing-detail axes are admitted against the seed's own option arrays in
// the reconstructor and nowhere else (ADR-018 Decision 5). The kit count is the
// item's own `QuoteLine.quantity`, not a field of the snapshot.
export function assemblyItemWriteDecision(args) {
    const { assembly, rejected } = normalizeAssemblySnapshotScoped(args.incoming, args.scope);
    const alreadyStored = assemblyIdentifiers(args.stored);
    const refuse = rejected.filter((r) => !alreadyStored.includes(r.value));
    if (refuse.length > 0)
        return { assembly: undefined, refuse };
    // An empty result drops the key instead of storing `{}`: an item that has not
    // been given a kit yet reads back as having none, and `stripFinishing` decides
    // whether to flatten by the key's presence.
    return { assembly: Object.keys(assembly).length > 0 ? assembly : undefined, refuse };
}
// The assembly snapshot's keys, split by the SHAPE of the Nexus attribute each
// one needs. The split is what makes an unmirrored axis findable: a key with a
// closed option set has to be swept option-by-option against the live catalog
// (scripts/assembly-pricing-cases.ts) before anyone can say Nexus accepts it,
// and a free value or a JSON blob cannot be. Adding a key to AssemblySnapshot
// breaks the assertion below until it is placed in one of the three, so the
// sweep grows with the vocabulary instead of being remembered separately.
// Closed option sets — one ServiceAttribute plus one ServiceAttributeOption per
// value on the Nexus side. `multi` marks an axis that transmits as an array.
export const ASSEMBLY_AXIS_KEYS = [
    'assembly_mode',
    'rework_actions',
    'kit_components_supplied',
    'kit_type',
    'filler_type',
    'filler_color',
    'closure_method',
    'box_finish',
    'overboxing',
    'label_purpose',
    'label_placement',
    'special_actions',
];
export const ASSEMBLY_AXIS_OPTIONS = {
    assembly_mode: { options: ASSEMBLY_MODES, multi: false },
    rework_actions: { options: REWORK_ACTIONS, multi: true },
    kit_components_supplied: { options: KIT_COMPONENT_SOURCES, multi: false },
    kit_type: { options: KIT_TYPES, multi: false },
    filler_type: { options: FILLER_TYPES, multi: true },
    filler_color: { options: FILLER_COLORS, multi: true },
    closure_method: { options: CLOSURE_METHODS, multi: false },
    box_finish: { options: BOX_FINISHES, multi: false },
    overboxing: { options: OVERBOXING_CHOICES, multi: false },
    label_purpose: { options: LABEL_PURPOSES, multi: true },
    label_placement: { options: LABEL_PLACEMENTS, multi: false },
    special_actions: { options: SPECIAL_ACTIONS, multi: true },
};
// Free values — a number, a boolean or free text. One captured-only
// ServiceAttribute each, with no option rows to mirror.
export const ASSEMBLY_SCALAR_KEYS = [
    'kit_components_count',
    'purchased_labels',
    'units_per_pack',
    'assembly_instructions',
    'bom_id',
    'fulfillment_sku',
    // The describe-it text behind each axis's `other` pick. Free values by the
    // same reasoning assembly_instructions is one: there is nothing for Nexus to
    // mirror but a captured-only text attribute, and the axis beside it already
    // carries the option set. Spread from the ONE list so adding a box grows the
    // manifest and the catalog sweep with it rather than being remembered here.
    ...ASSEMBLY_OTHER_TEXT_KEYS,
];
// Nested JSON. Nexus has no JSON attribute type and
// OpportunityServiceAttributeValue.value is a single nullable String with no
// fan-out, so these ride as canonical JSON on a `text` attribute exactly as
// bom_snapshot already does.
export const ASSEMBLY_STRUCTURED_KEYS = [
    'bom_snapshot',
    'kit_contents',
    'build_steps',
    'fulfillment_sku_snapshot',
];
export const ASSEMBLY_SNAPSHOT_KEYS = [
    ...ASSEMBLY_AXIS_KEYS,
    ...ASSEMBLY_SCALAR_KEYS,
    ...ASSEMBLY_STRUCTURED_KEYS,
];
// resolveBuildStep builds a FRESH object too, so the same reasoning applies:
// every key listed, and every key transmitted. There is no portal-only half here
// — a step's id crosses deliberately (it is the photo correlation key), and if a
// field is ever added that must NOT cross, list it under a suppressed heading
// the way KitContentRow does rather than quietly leaving it out of the resolver.
export const ASSEMBLY_BUILD_STEP_KEYS = ['step_id', 'instruction'];
// resolveKitContentRow builds a FRESH object rather than spreading, which is what
// keeps a portal-only field from riding out — but it also means a field added to
// KitContentRow and forgotten there silently fails to transmit, and R5 makes a
// captured-but-not-sent value a defect. Phase 4 adds fields to this type, so the
// same drift guard the snapshots carry applies here: list every key, and say which
// side of the transmit boundary it falls on.
export const KIT_CONTENT_ROW_TRANSMITTED_KEYS = [
    'role',
    'supply_source',
    'description',
    'quantity_per_kit',
    'unit',
    'sku',
];
// Portal-only, deliberately suppressed (ADR-018 Decision 3).
export const KIT_CONTENT_ROW_PORTAL_ONLY_KEYS = ['row_id', 'origin', 'line_id'];
// Every key a book's insert row carries on the wire, pinned to ResolvedInsert the
// same way, so the hand-off manifest's row shape cannot fall behind the type.
// InsertRow's row_id / origin / line_id never cross (insertsForTransmit).
export const RESOLVED_INSERT_KEYS = [
    'position',
    'source',
    'description',
    'quantity_per_book',
    'placement',
    'placement_page',
    'ink',
    'paper',
    'notes',
    'source_draft_line_id',
];
// The same three-way split for fulfillment. It has no live-price path of its own
// — fulfillment rides the opportunity-import feed — so nothing sweeps these
// against the catalog, but the manifest still has to name every option set Nexus
// must mirror, and naming them from the type is what keeps the list complete.
export const FULFILLMENT_AXIS_KEYS = ['barcode_resolution', 'sku_choice'];
export const FULFILLMENT_AXIS_OPTIONS = {
    barcode_resolution: { options: BARCODE_RESOLUTIONS, multi: false },
    sku_choice: { options: FULFILLMENT_SKU_CHOICES, multi: false },
};
export const FULFILLMENT_SCALAR_KEYS = ['sku', 'new_sku_name'];
export const FULFILLMENT_STRUCTURED_KEYS = ['sku_snapshot'];
export const FULFILLMENT_SNAPSHOT_KEYS = [
    ...FULFILLMENT_AXIS_KEYS,
    ...FULFILLMENT_SCALAR_KEYS,
    ...FULFILLMENT_STRUCTURED_KEYS,
];
// What the fulfillment block carries on the wire beyond its stored keys: the
// resolved per-line targets (orderServicesForTransmit). Pinned to the transmit
// type so a key added there reaches the manifest too.
export const FULFILLMENT_TRANSMIT_ONLY_KEYS = ['targets'];
// One line's stored target (normalizeLineFulfillmentTarget builds it fresh, so a
// key added to the type and forgotten there is silently dropped on save) and one
// wire entry (fulfillmentTargetsForTransmit builds that fresh too). Both pinned,
// and both listed in the Nexus hand-off manifest.
export const LINE_FULFILLMENT_TARGET_KEYS = [
    'choice',
    'sku',
    'sku_snapshot',
    'new_sku_name',
    'with_line_id',
];
export const FULFILLMENT_TARGET_WIRE_KEYS = [
    'draft_line_id',
    'title',
    'product_type',
    'disposition',
    'sku',
    'sku_snapshot',
    'new_sku_name',
    'into_kit_draft_line_ids',
    'with_draft_line_ids',
];
export const ORDER_SERVICES_SNAPSHOT_KEYS = [
    'services_requested',
    'shipping_service',
    'third_party_account',
    'fulfillment',
    'apply_barcode',
];
// ── Entry division — which door the customer came through ────────────────
//
// The portal's front door is keyed on the NEXUS DIVISION rather than a
// portal-coined job type: Nexus already owns this taxonomy, and its data
// dictionary forbids minting a synonym for a concept that has a decided name.
// So these values are canonical `Division.key` strings emitted VERBATIM — the
// same no-translation-layer posture ORDER_SERVICE_FAMILY_KEYS takes. See
// docs/ADR-015-division-keyed-quote-entry.md.
//
// Two lists live here and must not be confused. NEXUS_DIVISION_KEYS is the
// AUTHORITY, a downstream replica of
// nexus_erp/packages/backend/src/constants/divisions.ts; ENTRY_DIVISIONS is the
// portal's CURATION of it — the doors that exist here. This is the one narrow
// inversion of "the portal masters the vocabulary": the portal decides which
// doors open, Nexus decides what a division IS. The replica is pinned to Nexus's
// set by packages/backend/src/guards/entry-division-vocabulary.test.ts.
export const NEXUS_DIVISION_KEYS = [
    'fulfillment',
    'shipping',
    'assembly',
    'print',
    'media',
    'technology',
];
// The doors the portal opens. A door is a ROUTING HINT, never a partition: it
// pre-selects and reorders the configurator and is recorded on the quote, but it
// never constrains what that quote may contain. A quote entered through `print`
// legitimately carries Assembly-division kitting and Shipping-division
// print_order_shipping, so nothing downstream may branch on the entry division
// to reject a configuration.
//
// ORDER IS DEMAND ORDER — deliberately not alphabetical and not Nexus's
// Division.sort_order. Print dominates, so it leads; the "something else" escape
// hatch goes last, because an escape action never splits a card grid. The
// alphabetical convention governs dropdowns; this is a primary-path picker.
//
// APPENDING A DOOR (`fulfillment`, `shipping` — quote-level order services
// today) is one entry here plus its ENTRY_DIVISION_META row, and nothing else:
// no migration, because Quote.entry_division_key stores a bare Division.key from
// day one. The Record below makes that append fail typecheck until its label and
// help text exist.
export const ENTRY_DIVISIONS = ['print', 'media', 'assembly', 'technology'];
export const ENTRY_DIVISION_META = {
    print: {
        label: 'Print job',
        help: 'Books, manuals, flyers, labels and other printed work.',
        available: true,
    },
    media: {
        // Reuses the product type's own customer-facing label so the door and the
        // thing behind it read identically. Note the door value is the DIVISION key
        // (`media`); the product type it pre-selects is `media_replication`.
        label: PRODUCT_TYPE_LABELS.media_replication,
        help: 'USB drives or discs, printed, loaded with your files, or both.',
        available: true,
    },
    assembly: {
        // The door seeds one `assembly` item, so it reuses that tile's own label and
        // the door and the thing behind it read identically (ADR-020).
        label: PRODUCT_TYPE_LABELS.assembly,
        help: 'We gather your components and pack them into finished kits or sets.',
        available: true,
    },
    technology: {
        label: 'Something else',
        // Says who handles it, not how it is billed. Whether a technology change
        // is priced or quoted is Nexus's call (ADR-013), and the portal must not
        // answer it in customer copy.
        // Sets the expectation up front: this door returns a callback, not a number.
        help: 'A system or account change — EDI, API access, licenses, reports or support. Our team handles these with you directly.',
        // Open, but onto a stub: /technology-request explains the hand-off and
        // gives the customer a way to reach us. The door mints no quote until
        // phase 7 settles where a technology request lands in Nexus.
        available: true,
    },
};
// Is this an entry division the portal opens? Narrowing guard for the write
// boundary (a quote is stamped with its door at creation) and for reading a
// persisted value back. Deliberately NOT satisfied by a canonical Division.key
// the portal has no door for — that would let a quote claim a door that cannot
// mint it.
export function isEntryDivision(value) {
    return typeof value === 'string' && ENTRY_DIVISIONS.includes(value);
}
export function openEntryDivisions() {
    return ENTRY_DIVISIONS.filter((key) => ENTRY_DIVISION_META[key].available).map((key) => ({
        key,
        label: ENTRY_DIVISION_META[key].label,
        help: ENTRY_DIVISION_META[key].help,
    }));
}
// Line-level config marker the Media door writes when it pre-seeds a line's
// `product_type` on the customer's behalf (ADR-015 phase 4), so the STEP 1
// picker (FamilyCardGrid) can tell "the door set this for me" apart from "I
// picked media_replication myself" — only the former collapses the grid.
// Stripped from the primary line by PORTAL_ONLY_CONFIG_KEYS
// (services/pricing/finishing.ts) before transmit, and it is the ONLY marker
// that is. It carries a different posture from the `sheet_use` / `media_use`
// preset markers, which both cross: this one records how the FORM WAS
// PRE-FILLED, not a customer choice.
// The marker is WRITE-ONCE and never cleared: nothing offers a door-seeded line
// a way back to the picker, so the marker and the line's product type move
// together for the line's whole life. A customer who wants the other thing adds
// a second project, whose own picker is unmarked and shows the full grid.
export const DOOR_PRESELECTED_PRODUCT_TYPE_KEY = 'door_preselected_product_type';
// Line-level config marker for which media path a line is on: `usb` or `disc`.
// It is a pure function of the line's own `media_type` (usb → 'usb', any optical
// format → 'disc'), which the form writes beside it the moment the format is
// picked (mediaUseForConfig). The same posture as the `sheet_use` preset marker: a
// portal-side steer for which media fields the form asks, and Nexus owes no
// ServiceAttribute for it.
//
// NEITHER marker is a member of PORTAL_ONLY_CONFIG_KEYS
// (`packages/backend/src/services/pricing/finishing.ts`) and neither may be
// added there: both transmit on all three feeds as ordinary line config, because
// stripping a captured value is an R5 defect. "Portal-only" here means Nexus
// owes it no attribute, not that the portal withholds it. `sheet_use` WAS
// stripped while this one crossed, which is the divergence to keep closed —
// the two are written and cleared together by one PRESET_MARKER_KEYS list.
//
// Every field in the seed's `media` group below `media_type` gates on
// isSet('media_use') — media_capacity, media_decoration, media_print_sides,
// media_print_method, the disc/drive art upload and media_packaging — so the
// format is the media item's first question and the rest waits on it. The marker
// is read once more at OPTION grain: media_decoration's `laser_etched` hides on
// the disc path, where there is no drive body to etch, and it is one arm of the
// gate that keeps `media_print_sides` off the disc path before a format is
// picked. Config predicates evaluate against stored
// config values, so the marker outlives its own schema field. Widening those
// gates to drop the isSet arm would surface required fields that stored media
// lines were never asked, and flag them `missing` (R12 retroactive
// invalidation).
export const MEDIA_USE_CONFIG_KEY = 'media_use';
// The `media_use` marker a config's own `media_type` implies, or undefined when
// the line is not a media line or no format is picked yet. The ONE derivation:
// the form applies it inside the same write as the format pick, so the
// visibility scrub judges the new format and the new path together and a
// disc-only answer cannot outlive a switch to USB.
export function mediaUseForConfig(config) {
    if (config[ATTRIBUTE_KEYS.product_type] !== 'media_replication')
        return undefined;
    const mediaType = config[ATTRIBUTE_KEYS.media_type];
    if (mediaType === 'usb')
        return 'usb';
    if (OPTICAL_MEDIA_TYPES.includes(mediaType))
        return 'disc';
    return undefined;
}
// The twin marker, written by the Dust jacket preset card: a `single_sheet` line
// whose sheet is a book wrap, which is what filters the size suggestions down to
// the `dj_*` wrap sizes and excludes it from the ordinary flat-sheet rules.
// Same posture as the key above — a customer CHOICE, so it crosses on all three
// feeds and Nexus owes it no ServiceAttribute.
export const SHEET_USE_CONFIG_KEY = 'sheet_use';
// The portal-only line-config markers the product picker and its preset card
// steer by, other than `product_type` itself. The picker CLEARS all of them whenever a card is picked
// (before applying the picked card's own config, so a card that re-establishes a
// marker still wins) — otherwise a stale marker keeps showing dust-jacket sizes,
// or a line moved off media keeps a media path. Neither is a schema field, so
// `scrubConfigForVisibility` cannot reach them and this list is the only thing
// that clears them.
//
// Mastered HERE rather than in the picker because the transmit strip's own
// documentation asserts a property about this exact set — that both markers cross
// and neither is in `PORTAL_ONLY_CONFIG_KEYS`
// (`packages/backend/src/services/pricing/finishing.ts`). A list hardcoded in a
// component is a guarantee the boundary cannot actually make: `sheet_use` was
// stripped while `media_use` crossed, which is precisely the divergence naming
// the set in one place keeps closed.
export const PRESET_MARKER_KEYS = [
    SHEET_USE_CONFIG_KEY,
    MEDIA_USE_CONFIG_KEY,
];
// Where an ASSEMBLY ITEM keeps its kit recipe: nested under its own line's
// config, as an AssemblySnapshot. Named here because three places must agree
// about it and none of them can see the others — the product picker clears it
// when a card moves the line to another family (it is not a schema field, so
// scrubConfigForVisibility cannot reach it), the item's AssemblyPanel is bound to
// it, and the per-line transmit strip projects it through assemblyForTransmit so
// a stored snapshot's raw row ids never cross (ADR-018 Decision 3).
export const ASSEMBLY_ITEM_CONFIG_KEY = 'assembly';
// The `product_type` an assembly ITEM carries — the one assembly flow (ADR-020).
// Named so the surfaces that have to recognise an assembly item — the line editor's panel mount and the per-line
// half of the config validator — compare against one constant instead of each
// keeping its own copy of the literal.
export const ASSEMBLY_ITEM_PRODUCT_TYPE = 'assembly';
function perUnitAssemblyItem(config) {
    if (typeof config !== 'object' || config === null || Array.isArray(config))
        return undefined;
    const record = config;
    if (record.product_type !== ASSEMBLY_ITEM_PRODUCT_TYPE)
        return undefined;
    const assembly = normalizeAssemblySnapshot(record[ASSEMBLY_ITEM_CONFIG_KEY]);
    return assemblyMode(assembly) === 'per_unit' ? assembly : undefined;
}
export function assemblyItemIsPerUnit(config) {
    return perUnitAssemblyItem(config) !== undefined;
}
// Undefined while any row is one the gate refuses, so the heal never stamps a
// quantity from rows assemblyMissingFieldKeys would still report.
export function assemblyItemUnitTotal(config) {
    const assembly = perUnitAssemblyItem(config);
    if (!assembly)
        return undefined;
    const rows = assembly.kit_contents ?? [];
    if (rows.some((row) => kitContentRowIncompleteIn('per_unit', row)))
        return undefined;
    const total = rows.reduce((sum, row) => sum + (row.quantity_per_kit ?? 0), 0);
    return Number.isSafeInteger(total) && total > 0 && total <= MAX_LINE_QUANTITY ? total : undefined;
}
export function withAssemblyItemUnitTotal(config) {
    const total = assemblyItemUnitTotal(config);
    if (total === undefined || config.quantity === total)
        return config;
    return { ...config, quantity: total };
}
// A per_unit item's quantity is units and its rows count units of stock; a kit
// build's or rework's quantity is kits and its rows count one kit's parts. So
// neither the quantity nor the list is read across a switch into or out of
// per_unit.
export function assemblyModeCrossesPerUnit(from, to) {
    return (from === 'per_unit') !== (to === 'per_unit');
}
// Whether that switch has a list to clear, and so has to be confirmed first.
export function assemblyModeSwitchClearsRows(from, to, rows) {
    return assemblyModeCrossesPerUnit(from, to) && (rows?.length ?? 0) > 0;
}
function rawAssemblyItemSnapshot(config) {
    const raw = config[ASSEMBLY_ITEM_CONFIG_KEY];
    return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
        ? raw
        : undefined;
}
export function assemblyItemSwitchCrossesPerUnit(config, next) {
    return assemblyModeCrossesPerUnit(assemblyMode(rawAssemblyItemSnapshot(config)), assemblyMode(next));
}
// An assembly item's config with its snapshot replaced. Across per_unit the
// quantity counted the other unit (units summed from the old rows, or kits), so it
// is dropped, for the customer to enter or for the new rows to sum. The caller
// owning a second copy of the quantity resets that copy too
// (assemblyItemSwitchCrossesPerUnit).
export function withAssemblyItemSnapshot(config, next) {
    const out = { ...config, [ASSEMBLY_ITEM_CONFIG_KEY]: next };
    if (assemblyItemSwitchCrossesPerUnit(config, next))
        delete out.quantity;
    return withAssemblyItemUnitTotal(out);
}
export function lineConfigWithFreshAssemblyIds(config, newId) {
    const raw = config[ASSEMBLY_ITEM_CONFIG_KEY];
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return config;
    const assembly = { ...raw };
    if (Array.isArray(assembly.kit_contents)) {
        assembly.kit_contents = assembly.kit_contents.map((row) => typeof row === 'object' && row !== null ? { ...row, row_id: newId() } : row);
    }
    if (Array.isArray(assembly.build_steps)) {
        assembly.build_steps = assembly.build_steps.map((step) => typeof step === 'object' && step !== null ? { ...step, step_id: newId() } : step);
    }
    return { ...config, [ASSEMBLY_ITEM_CONFIG_KEY]: assembly };
}
// The `product_type` the escape-hatch item carries. Named for the same reason as
// the one above: three surfaces have to recognise it and none can see the others.
export const SOMETHING_ELSE_PRODUCT_TYPE = 'something_else';
// The product types there is something to PROOF on — a printed piece, or a
// replicated disc or duplicated drive: the things a customer approves a sample of before we
// make them. Mastered here and read by both authors of that one fact, so they
// cannot drift: the seed builds `proof_type`'s `applies_to` and `conditional_show`
// from it, and the submit gate uses it to tell "the customer cleared their proof"
// apart from "nothing on this order is proofable" (assertProofSelected).
//
// Derived by EXCLUSION so the default is the safe one — a new printed family is
// proofed the day it is added, and only a deliberate non-product has to be named
// here. The two exclusions are the whole point: `assembly` is kitting work over
// parts printed somewhere else (or never printed), and `something_else` is a
// request the order form does not spec at all. Neither has anything to show the
// customer, which is precisely the assumption `assertProofSelected` was written
// able to make about EVERY product type and could not from 2026-08-21, when the
// two of them became product types (ADR-019).
//
// A `media_replication` line is proofed only when it carries art
// (mediaLineCarriesArt, FB-0094): a plain drive or disc has nothing to approve.
// A `custom_packaging` line likewise only when it prints (customPackagingCarriesArt). That
// narrowing is per LINE, so it lives in the seed's gate and in
// lineIsProofed, never as a removal from this list.
export const PROOFED_PRODUCT_TYPES = PRODUCT_TYPES.filter((type) => type !== ASSEMBLY_ITEM_PRODUCT_TYPE && type !== SOMETHING_ELSE_PRODUCT_TYPE);
// ── Ship-to (delivery destination) — quote-level, configurator step 12 ──
//
// WHERE the finished order goes. This is QUOTE-LEVEL (one destination per
// order), address-shaped, and persisted to Nexus — it is NOT a per-line
// PRICED attribute, so it lives as a dedicated quote-level surface in the
// editor (alongside Order name + Need-By), NOT as a FormSchema field. The
// per-line `SchemaForm` renders once per project; an address would render
// N times there, which is wrong. So step 12 adds NO FormSchema field/group
// and needs NO re-seed; this section is the portal-master vocabulary for the
// new ship-to shape (ordered change flow — Nexus mirrors it, see the hand-off
// note below).
//
// Distinct from the `fulfillment` STUB product type (ADR-010 §13): that line
// captures "what logistics service do you need" and is priced/routed by Nexus
// later; THIS captures the bare destination so the order can actually be
// delivered, stamped with the delivery mode `services_requested` decides. They compose — a quote may have both,
// one, or (pickup) neither.
//
// ── HARD RULE: shipping cost is NEVER in total_sale_amount ───────────────
// The portal computes ZERO shipping math — no weight, no rate, no shipping
// price (see the weight/rate STUBS in packages/portal/src/shipping/
// estimate.ts). Shipping is its own figure: charged total =
// total_sale_amount + tax_amount + shipping (ADR-010 §12). Capturing the
// ship-to here unlocks the ERP-side tax figure (tax_amount is null until the
// jurisdiction is known) and the eventual ERP/carrier rate — both Nexus's,
// never the portal's.
export const DELIVERY_METHODS = ['ship', 'pickup'];
// Field-key prefix for the field-keyed submit-failure UX (inline error +
// near-button summary + reveal/scroll). The DOM id of each ship-to input
// EQUALS its field key (`ship_to.<prop>`), so the editor's existing
// scrollToField(getElementById) lands on the right input — exactly like the
// step-8 variable-data gate keys on `variable_print`.
export const SHIP_TO_FIELD_PREFIX = 'ship_to';
// What a destination is FOR. A quote's addresses are one list in one table, and
// this is the discriminator that says which order-flow each row belongs to:
//
//   'final' — where the finished ORDER goes. The historical meaning of every
//             address, and the default, so a row written before proof
//             destinations existed keeps saying exactly what it always said.
//   'proof' — where a printed PROOF SAMPLE is mailed (FB-0036). A proof is
//             mailed by definition, so a proof destination is always
//             delivery_method 'ship' and is never offered Pickup.
//
// The two never mix in a balance: the final rows' unit counts add up to the
// ORDER quantity, the proof rows' to the proof SAMPLE count. Reading one list
// where the other was meant is what would make every order carrying a proof
// unsubmittable, so the readers take the purpose explicitly rather than
// defaulting to "all rows".
export const SHIP_TO_PURPOSES = ['final', 'proof'];
// Field-key prefix for a PROOF destination. Its own namespace, not an index into
// the order's list: the two lists are rendered on the page at the same time, and
// a shared prefix would give a proof address and a final address the same DOM id
// — the reveal/scroll would land on whichever mounted first.
export const PROOF_SHIP_TO_FIELD_PREFIX = 'proof_ship_to';
export function shipToFieldPrefix(purpose) {
    return purpose === 'proof' ? PROOF_SHIP_TO_FIELD_PREFIX : SHIP_TO_FIELD_PREFIX;
}
// How many printed proof samples the customer wants — a quote-level COUNT, and
// the field key the count's input carries as its DOM id. Not an Address column,
// not a price: Nexus prices the proof's own print + shipping from the count
// (ADR-010 §12 posture — the portal transmits the intent and computes nothing).
export const PROOF_QUANTITY_FIELD_KEY = 'proof_quantity';
// A proof sample count is a whole number of samples, at least one — the same
// shape rule as a destination's unit count, stated separately because they
// answer different questions and drift apart if one is aliased to the other.
export function isProofQuantity(v) {
    return typeof v === 'number' && Number.isInteger(v) && v > 0;
}
// The count to put on the wire for a stored value. A quote that predates the
// column (null) resolves to ONE, which is exactly what the physical_proof
// project has always transmitted — so nothing about a legacy quote changes
// shape. Pure over stored data, never over a moving input (R26).
export const PROOF_QUANTITY_DEFAULT = 1;
export function resolveProofQuantity(stored) {
    return isProofQuantity(stored) ? stored : PROOF_QUANTITY_DEFAULT;
}
// Required address props when delivery_method === 'ship', INDEPENDENT of
// country. attention_name + street_line_2 + street_line_3 are intentionally optional.
// `state_or_province` is NOT here — it is required only for countries whose
// addresses need a state/province (see countryRequiresStateOrProvince + the
// conditional push in shipToMissingFieldKeys), so a valid UK address isn't
// rejected for a missing state. Append-only: never remove a prop without a
// migration of existing snapshots.
export const SHIP_TO_REQUIRED_PROPS = [
    'location_name',
    'street_line_1',
    'city',
    'postal_code',
    'country_iso2',
    'location_phone',
];
// OFFLINE FALLBACK for the ship-to country picker. The picker is populated at
// runtime from Nexus's shared Country list (GET /api/addresses/countries →
// Nexus POST /api/erp/reference/countries) — Nexus is the source of truth. Each
// option's VALUE is the iso2 code (the stable shared key the wire carries);
// only the name is displayed. This constant is a verbatim mirror of Nexus's
// seeded Country list (packages/backend/prisma/seeds/16-reference-data.ts) used
// ONLY when that fetch fails, so a Nexus hiccup can't strand a shipping order
// with an empty dropdown (country can NOT degrade to free text). Keep it in
// sync if Nexus's seed changes; the live list wins whenever it loads.
export const SHIP_TO_COUNTRIES = [
    { name: 'Australia', iso2: 'AU' },
    { name: 'Canada', iso2: 'CA' },
    { name: 'Costa Rica', iso2: 'CR' },
    { name: 'Denmark', iso2: 'DK' },
    { name: 'France', iso2: 'FR' },
    { name: 'Germany', iso2: 'DE' },
    { name: 'Greece', iso2: 'GR' },
    { name: 'Ireland', iso2: 'IE' },
    { name: 'Italy', iso2: 'IT' },
    { name: 'Mexico', iso2: 'MX' },
    { name: 'New Zealand', iso2: 'NZ' },
    { name: 'Portugal', iso2: 'PT' },
    { name: 'Puerto Rico', iso2: 'PR' },
    { name: 'Republic of Panama', iso2: 'PA' },
    { name: 'Romania', iso2: 'RO' },
    { name: 'South Africa', iso2: 'ZA' },
    { name: 'Switzerland', iso2: 'CH' },
    { name: 'Thailand', iso2: 'TH' },
    { name: 'United Kingdom', iso2: 'GB' },
    { name: 'United States', iso2: 'US' },
];
// iso2 codes whose addresses require a state/province — a verbatim mirror of
// Nexus's STATE_REQUIRED_ISO2 (nexus_erp/packages/backend/src/constants/addressState.ts).
// Every other country's is optional (a valid UK address has no state). The two
// lists must agree: Nexus answers a missing one with 422 ADDRESS_STATE_REQUIRED,
// which carries no contact-sales envelope, so the submit-time persist swallows it
// as best-effort and the order is placed with no Address saved in Nexus.
const STATE_OR_PROVINCE_REQUIRED_ISO2 = new Set(['AU', 'CA', 'US']);
// Whether the given country (iso2 code) requires a state/province. Absent/blank
// ⇒ not required here (country itself is a required field, so an empty country
// is flagged on its own — we don't double-flag the state on top of it).
export function countryRequiresStateOrProvince(countryIso2) {
    return typeof countryIso2 === 'string' && STATE_OR_PROVINCE_REQUIRED_ISO2.has(countryIso2.trim());
}
// Customer-voice labels, keyed by the full field key so the editor's failure
// summary + the form share one source of truth. No print jargon here, so no
// inline definitions are needed.
// Keys mirror the Nexus Address column names (see ShipTo); LABELS stay
// customer-voice (a label may differ from its key — vocabulary design rule #2).
//
// The delivery-mode key keeps its historical `ship_to.delivery_method` spelling so
// the editor's ship-to scope, label lookup and live re-keying all cover it, but it
// names the order-services card grid — the one control that answers the mode.
export const DELIVERY_MODE_FIELD_KEY = `${SHIP_TO_FIELD_PREFIX}.delivery_method`;
export const SHIP_TO_FIELD_LABELS = {
    [DELIVERY_MODE_FIELD_KEY]: 'How your order should reach you',
    'ship_to.location_name': 'Recipient name',
    'ship_to.attention_name': 'Attention to',
    'ship_to.street_line_1': 'Street address',
    'ship_to.street_line_2': 'Apartment, suite, or unit',
    'ship_to.street_line_3': 'Additional address line',
    'ship_to.city': 'City',
    'ship_to.state_or_province': 'State / province',
    'ship_to.postal_code': 'ZIP / postal code',
    'ship_to.country_iso2': 'Country',
    'ship_to.location_phone': 'Phone',
};
function isBlankString(v) {
    return typeof v !== 'string' || v.trim().length === 0;
}
// The single source of truth for "is the ship-to complete enough to submit?",
// shared by the SPA client pre-block AND the authoritative backend gate
// (services/shipping/ship-to-gate.ts) so the two can never disagree. Returns
// the missing field keys (empty ⇒ satisfied). For the ORDER's list the answer
// turns on the delivery mode the customer picked (resolveDeliveryMode over
// `services`), never on the rows' own `delivery_method` stamp:
//   - no mode picked        → [DELIVERY_MODE_FIELD_KEY] (must decide; an empty
//                             or legacy services array is NOT a pickup)
//   - pickup / fulfillment  → [] (no address needed — self-pickup / own freight,
//                             or stock that leaves against the fulfillment
//                             agreement)
//   - shipping              → every blank required address prop, per address,
//                             plus each address's unit count and the sum of
//                             those counts (split shipping)
//
// `services` is REQUIRED for the order's list, so a caller cannot forget it and
// have an unanswered order pass. A proof list ignores it: a sample is mailed.
//
// WIDENED to the LIST rather than given a sibling for the list (R12's posture:
// one validator, extended). A single ShipTo is still accepted and behaves exactly
// as it did, so every existing caller and every historical `ship_to.*` field key
// is unchanged. `orderQuantity` is the total the counts must add up to — the
// caller supplies it (splitBaseQuantity), and null means the order has no single
// correct total, in which case the sum is not checked at all.
export function shipToMissingFieldKeys(shipTos, opts) {
    const purpose = opts.purpose ?? 'final';
    const proof = purpose === 'proof';
    if (!proof) {
        const mode = resolveDeliveryMode(opts.services);
        if (mode === null)
            return [DELIVERY_MODE_FIELD_KEY];
        if (mode !== 'shipping')
            return [];
    }
    // An empty list on a shipped order, like an empty proof list, is one blank
    // address the customer has yet to fill — the form renders it at position zero —
    // so it reports that address's keys rather than passing with nowhere to ship.
    const given = Array.isArray(shipTos) ? shipTos : shipTos ? [shipTos] : [];
    const list = given.length === 0 ? [{ delivery_method: 'ship' }] : given;
    const missing = [];
    list.forEach((shipTo, index) => {
        const record = shipTo;
        for (const prop of SHIP_TO_REQUIRED_PROPS) {
            if (isBlankString(record[prop]))
                missing.push(shipToFieldKey(index, prop, purpose));
        }
        // State/province is required ONLY for countries that use one (US, CA, AU).
        // A valid UK/international address has no state, so we don't reject it —
        // Nexus's extended contract makes state_or_province optional for non-US.
        if (countryRequiresStateOrProvince(record.country_iso2) &&
            isBlankString(record.state_or_province)) {
            missing.push(shipToFieldKey(index, 'state_or_province', purpose));
        }
        // "Omitted ⇒ the whole order goes here" cannot be true of two addresses at
        // once, so from two up each one must state its own count.
        if (list.length > 1 && !isShipToQuantity(shipTo.quantity)) {
            missing.push(shipToQuantityFieldKey(index, purpose));
        }
    });
    // …and those counts must add up to the total this list is a split OF — the
    // ORDER quantity for the final destinations, the proof SAMPLE count for the
    // proof ones. Only the portal can check either: it is the only side that knows
    // the total, and Nexus deliberately does not gate the sum. Suppressed while any
    // individual count is still unanswered, so the customer is not told the total
    // is wrong before they have finished stating it.
    const split = shipToQuantitySplit(list, opts.orderQuantity ?? null);
    if (split && !split.balanced && !missing.some((k) => k.endsWith(`.${SHIP_TO_QUANTITY_PROP}`))) {
        missing.push(shipToQuantityTotalFieldKey(purpose));
    }
    return missing;
}
// The single source of truth for "has the customer said enough about their
// PHYSICAL PROOF to place the order?" (FB-0036), shared by the SPA pre-block and
// the authoritative backend gate so the two can never disagree. Returns the
// missing field keys (empty ⇒ satisfied):
//   - no physical proof on the quote → [] (nothing to ask; a digital proof is
//                                      emailed and a quote with none is already
//                                      refused by the proof gate)
//   - no sample count               → ['proof_quantity']
//   - every blank required address prop of every proof destination, plus each
//     destination's sample count and the sum of those counts once the samples go
//     to more than one place
//
// Extended INTO the shared ship-to validator rather than beside it (R12): the
// address half is shipToMissingFieldKeys under purpose 'proof', so a proof
// address and a final address are judged complete by exactly the same rule.
export function proofDeliveryMissingFieldKeys(input) {
    if (input.proof_type !== 'physical')
        return [];
    const missing = [];
    if (!isProofQuantity(input.proof_quantity))
        missing.push(PROOF_QUANTITY_FIELD_KEY);
    missing.push(...shipToMissingFieldKeys(input.proof_ship_tos ?? [], {
        services: null,
        purpose: 'proof',
        // The samples split against the SAMPLE count, never the order quantity.
        // Null while the count is unanswered, which leaves the sum unchecked — the
        // count's own miss above is what the customer acts on first.
        orderQuantity: isProofQuantity(input.proof_quantity) ? input.proof_quantity : null,
    }));
    return missing;
}
// The optional string address props normalizeShipTo copies. Hoisted to module
// scope so the drift guard below checks the SAME list the loop reads —
// delivery_method (always set), `quantity`, and these must equal keyof ShipTo, or
// typecheck fails (see the drift-guard note above normalizeOrderServices).
export const SHIP_TO_STRING_PROPS = [
    'location_name', 'attention_name', 'street_line_1', 'street_line_2', 'street_line_3',
    'city', 'state_or_province', 'postal_code', 'country_iso2', 'location_phone',
];
// Defensive normalizer for one ship-to arriving from outside — a PATCH body, a
// local draft, a QuoteShipTo row. Returns null for an absent/malformed value (no
// ship-to captured), never a broken object; otherwise narrows to the ShipTo shape
// with a valid delivery_method (defaulting to 'ship' if the stored value is junk).
export function normalizeShipTo(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const r = raw;
    const delivery_method = r.delivery_method === 'pickup' ? 'pickup' : 'ship';
    const out = { delivery_method };
    for (const p of SHIP_TO_STRING_PROPS) {
        const v = r[p];
        if (typeof v === 'string' && v.length > 0)
            out[p] = v;
    }
    // The unit count, handled on its own because it is a NUMBER — the loop above
    // is the string address props. A non-count (zero, negative, fractional, a
    // numeric string) is dropped rather than coerced: it would read as a
    // deliberate answer, and the submit gate would then pass a quantity nobody
    // stated.
    if (isShipToQuantity(r[SHIP_TO_QUANTITY_PROP])) {
        out[SHIP_TO_QUANTITY_PROP] = r[SHIP_TO_QUANTITY_PROP];
    }
    return out;
}
export function reconcileDeliveryIntent(input, services) {
    if (!input)
        return input;
    const mode = resolveDeliveryMode(services);
    const method = mode === 'pickup' ? 'pickup' : 'ship';
    const settle = (s) => s.delivery_method === method ? s : { ...s, delivery_method: method };
    return Array.isArray(input) ? input.map(settle) : settle(input);
}
// ── Several ship-to addresses on one order (split shipping) ──────────────
//
// Nexus feedback FB-0025: "20 copies sent to one address and 50 sent to
// another" — one order going to several addresses instead of one order per
// address. A quote holds a LIST of `ShipTo`, ordered, and each entry states how
// many units go there (`quantity`). There is no second type for this: an entry
// in the list is the same `ShipTo` a one-address order has always had.
//
// ON THE WIRE Nexus calls the array `destinations` and reads it in PREFERENCE to
// the single `ship_to`, creating one `Print Order Shipping` quote line per entry
// (array order becomes its sort_order, display only). That snapshot is REPLACING
// and idempotent there, so every feed sends the whole list and never a delta.
// `destinations` is Nexus's name for the wire field and appears ONLY at the three
// feed call sites — inside the portal this is the quote's ship-to list.
//
// Shipping COST is untouched by all of this: no per-address rate is computed,
// displayed or transmitted, and nothing here reaches total_sale_amount
// (ADR-010 §12).
// The prop holding a ship-to's unit count. NOT in SHIP_TO_STRING_PROPS — that
// list is the string address props the drift guard checks, and this is a number,
// so it is handled on its own in normalizeShipTo and named in the guard's
// handled union.
export const SHIP_TO_QUANTITY_PROP = 'quantity';
// A ship-to's quantity is a whole number of units, at least one. Anything else
// (blank, zero, negative, fractional, a string) is not a count.
export function isShipToQuantity(v) {
    return typeof v === 'number' && Number.isInteger(v) && v > 0;
}
// Defensive normalizer for a stored ship-to LIST (DB rows → DTO) and for the
// PATCH body (the route delegates to it rather than keeping a parallel Zod field
// list). Non-array, empty, or all-junk ⇒ null, meaning "no ship-to captured".
export function normalizeShipTos(raw) {
    if (!Array.isArray(raw))
        return null;
    const out = [];
    for (const entry of raw) {
        const s = normalizeShipTo(entry);
        if (s)
            out.push(s);
    }
    return out.length > 0 ? out : null;
}
// The field key — and therefore the DOM id — for one address's property.
//
// Address ONE keeps the historical bare `ship_to.<prop>` keys AT EVERY LIST
// LENGTH, and only the addresses after it carry an index. That matters: keying
// address one on the list length would reshape its ids the moment a second
// address is added, orphaning an error already on screen (the red highlight
// silently disappears, and the summary link scrolls to an element that no longer
// exists under that id). Address one's keys are now fixed for good.
//
// ONE producer, called by the form for the id, by the SPA pre-flight, and by the
// authoritative server gate — so an input's id and the key that names it cannot
// disagree.
// The `purpose` parameter is what lets the ONE repeatable address list render
// twice on the same page — the order's destinations and the proof samples' —
// without the two claiming the same ids.
export function shipToFieldKey(index, prop, purpose = 'final') {
    const prefix = shipToFieldPrefix(purpose);
    return index === 0 ? `${prefix}.${prop}` : `${prefix}.${index}.${prop}`;
}
// The unit count is ALWAYS indexed, address one included: it is asked only once
// an order goes to several addresses, so a bare `ship_to.quantity` would name a
// control that exists in no other state.
export function shipToQuantityFieldKey(index, purpose = 'final') {
    return `${shipToFieldPrefix(purpose)}.${index}.${SHIP_TO_QUANTITY_PROP}`;
}
// The one key for "the counts do not add up to the total". Not a property of any
// single address — every count is implicated — so it names the reconciliation
// line itself, which carries this id so reveal/scroll lands on the sentence that
// explains the problem. One per list: the order's counts and the proof samples'
// counts fail independently and each has its own sentence on screen.
export const SHIP_TO_QUANTITY_TOTAL_FIELD_KEY = `${SHIP_TO_FIELD_PREFIX}.quantity_total`;
export const PROOF_SHIP_TO_QUANTITY_TOTAL_FIELD_KEY = `${PROOF_SHIP_TO_FIELD_PREFIX}.quantity_total`;
export function shipToQuantityTotalFieldKey(purpose = 'final') {
    return purpose === 'proof'
        ? PROOF_SHIP_TO_QUANTITY_TOTAL_FIELD_KEY
        : SHIP_TO_QUANTITY_TOTAL_FIELD_KEY;
}
const INDEXED_SHIP_TO_FIELD_KEY_RE = /^(proof_ship_to|ship_to)\.(\d+)\.(.+)$/;
// Customer-voice label for any ship-to field key, in EITHER shape. The failure
// summary resolves labels by exact lookup, so an indexed key would otherwise
// resolve to nothing and the miss would drop out of the summary entirely.
// Returns undefined for a key that is not a ship-to field, so callers keep
// falling through to their other maps.
export function shipToFieldLabel(key) {
    const direct = SHIP_TO_FIELD_LABELS[key];
    if (direct)
        return direct;
    if (key === PROOF_QUANTITY_FIELD_KEY)
        return 'How many printed samples you need';
    if (key === SHIP_TO_QUANTITY_TOTAL_FIELD_KEY)
        return 'How the addresses add up';
    if (key === PROOF_SHIP_TO_QUANTITY_TOTAL_FIELD_KEY) {
        return 'How the printed-sample addresses add up';
    }
    // A PROOF address key at position ZERO is bare, like a final one — the label
    // still has to say which list it belongs to, or the summary shows two
    // identically-named "Street address" misses and the customer cannot tell the
    // sample's address from the order's.
    const bareProof = SHIP_TO_FIELD_LABELS[key.replace(`${PROOF_SHIP_TO_FIELD_PREFIX}.`, `${SHIP_TO_FIELD_PREFIX}.`)];
    if (key.startsWith(`${PROOF_SHIP_TO_FIELD_PREFIX}.`) && bareProof) {
        return `${bareProof} (printed sample)`;
    }
    const match = INDEXED_SHIP_TO_FIELD_KEY_RE.exec(key);
    if (!match)
        return undefined;
    const prefix = match[1];
    const rawIndex = match[2];
    const prop = match[3];
    if (!prefix || !rawIndex || !prop)
        return undefined;
    const base = prop === SHIP_TO_QUANTITY_PROP
        ? 'How many go here'
        : SHIP_TO_FIELD_LABELS[`${SHIP_TO_FIELD_PREFIX}.${prop}`];
    if (!base)
        return undefined;
    const which = prefix === PROOF_SHIP_TO_FIELD_PREFIX ? 'printed sample address' : 'address';
    return `${base} (${which} ${Number(rawIndex) + 1})`;
}
// The item's own name. It is a structural QuoteLine COLUMN, not a schema field,
// so no `schema.fields` entry carries its key or label — both live here, beside
// the other labels a failure can name, so the summary, the inline error and a
// backend-returned `details.field_keys` entry all read it from one place.
export const LINE_TITLE_FIELD_KEY = 'title';
export const LINE_TITLE_FIELD_LABEL = 'Item name';
// A failure key the portal's own save boundary attributes to ONE item:
// `lines.<index>.<field_key>`, where `index` is the item's position in the
// `lines` array the save SENT. Every other per-line key on the wire (a Nexus 422,
// a gate) is bare and names no item; this one does, so the editor can light the
// field on the item the customer actually has to fix rather than on item 1.
export const LINE_ISSUE_KEY_PREFIX = 'lines';
export function lineIssueKey(line_index, field_key) {
    return `${LINE_ISSUE_KEY_PREFIX}.${line_index}.${field_key}`;
}
export function parseLineIssueKey(key) {
    const match = /^lines\.(\d+)\.(.+)$/.exec(key);
    if (!match)
        return null;
    return { line_index: Number(match[1]), field_key: match[2] };
}
// The control a per-item key is answered on. A refused insert reference names
// the row's `line_id`, but the customer fixes it in the inserts list itself.
export function lineIssueAnchorKey(field_key) {
    return field_key.startsWith(`${INSERTS_CONFIG_KEY}.`) ? INSERTS_CONFIG_KEY : field_key;
}
// Keys only the portal's save boundary names — quote columns and the write-side
// identifier checks, none of which is a schema field — in the words the editor
// prints beside each control.
const SAVE_BOUNDARY_FIELD_LABELS = {
    project_title: 'Order name',
    requested_finish_date: 'In-hands date',
    expedite_requested: 'Rush',
    [LINE_ISSUE_KEY_PREFIX]: 'Items',
    [INSERTS_LINE_REF_FIELD_KEY]: 'Pieces to insert',
    [MEDIA_PACKAGING_STOCK_ITEM_KEY]: 'Packaging',
};
// Customer-voice label for ANY field key a failure can name — the last resort
// when a blocked outcome has to say WHICH field blocked it and the caller has no
// richer source. Callers that hold the served schema (the SPA) resolve a
// rendered field's label from the schema first and fall through to this; callers
// that do not (PricingBlock, rendering a Nexus 422's `details.field_keys`) use it
// directly. One resolver over the label maps that already exist, so a key that
// reaches the customer can never come out as raw `cover_finish` on one surface
// and "Lamination" on another. Never returns undefined: an unrecognized key is
// humanized rather than dropped, because a blocked outcome that names nothing is
// the failure mode this exists to prevent (FB-0051).
export function fieldKeyLabel(key) {
    const lineIssue = parseLineIssueKey(key);
    if (lineIssue)
        return fieldKeyLabel(lineIssue.field_key);
    if (key === LINE_TITLE_FIELD_KEY)
        return LINE_TITLE_FIELD_LABEL;
    const saveBoundary = SAVE_BOUNDARY_FIELD_LABELS[key];
    if (saveBoundary)
        return saveBoundary;
    const shipTo = shipToFieldLabel(key);
    if (shipTo)
        return shipTo;
    const service = ORDER_SERVICE_FIELD_LABELS[key];
    if (service)
        return service;
    const attribute = ATTRIBUTE_LABELS[key];
    if (attribute)
        return attribute;
    // `some.nested.key` reads as its leaf; `cover_finish` becomes "Cover finish".
    const segments = key.split('.');
    const leaf = segments[segments.length - 1] ?? key;
    const words = leaf.replace(/_/g, ' ').trim();
    return words === '' ? key : words.charAt(0).toUpperCase() + words.slice(1);
}
// The order quantity a split has to add up to, or null when the order has no
// single correct total. Pure over the captured line quantities: one configured
// line — or several that all state the SAME quantity, the "three pieces of one
// 100-piece run" case — gives an unambiguous total; a mixed-quantity
// multi-product order does not, and there the sum is not checked at all (which is
// also why Nexus does not gate on it: a shipping line there is bound to no one
// product).
export function splitBaseQuantity(lineQuantities) {
    const stated = lineQuantities.filter((q) => typeof q === 'number' && Number.isFinite(q) && q > 0);
    if (stated.length === 0)
        return null;
    const first = stated[0];
    return stated.every((q) => q === first) ? first : null;
}
// How a split adds up, for the form's reconciliation line. Returns null when
// there is nothing to say — fewer than two addresses, or no unambiguous order
// quantity to compare against. The SPA words all three outcomes, since this is
// customer copy and copy is translated there.
//
// `balanced === false` is a SUBMIT BLOCK, reported by shipToMissingFieldKeys as
// SHIP_TO_QUANTITY_TOTAL_FIELD_KEY. The portal is the only place it can be
// enforced: Nexus deliberately does not gate the sum, because a multi-product
// order has no single correct total for it to gate against.
export function shipToQuantitySplit(shipTos, baseQuantity) {
    const list = shipTos ?? [];
    if (list.length < 2 || baseQuantity === null)
        return null;
    const assigned = list.reduce((sum, s) => sum + (isShipToQuantity(s.quantity) ? s.quantity : 0), 0);
    return { assigned, expected: baseQuantity, balanced: assigned === baseQuantity };
}
// The FINAL ship-to list as it crosses to Nexus in `destinations`, or null when
// there is nothing to send there. Callers hand it the order's own destinations;
// a proof sample's addresses are a different list with a different producer
// (proofDeliveryForTransmit below) and a different wire field.
// ONE producer for all three quote-carrying feeds
// (R5/R7) — the pricing request's order_context, /quote-detail and the submit
// body all call this — so a split cannot reach one feed and silently not another.
//
// Below TWO addresses it returns null and the single `ship_to` field carries the
// destination exactly as it always has. Nexus reads `destinations` in preference
// to `ship_to`, so a one-element array would be equivalent but for one thing: it
// would bind a per-destination quantity to the single shipping line where today
// none is bound. Nothing regresses by staying silent there.
//
// PICKUP sends nothing. Nexus drops a pickup entry — pickup carries no address,
// and the decision is already the identity of the print_order_pickup service
// line — so the array would arrive empty. The pickup DECISION still crosses on
// `ship_to` and `order_services`, as it always has. FULFILLMENT sends nothing
// either: a fulfilled order goes into stock, so an address left over from before
// the customer switched is not a destination, and Nexus would otherwise file it
// as one.
//
// Entries cross VERBATIM, including one the customer has not finished filling:
// Nexus drops an entry with neither street_line_1 nor postal_code, and
// pre-filtering to that rule here would renumber the array and, if address one
// were the blank one, drop a COMPLETE later address below the threshold and lose
// it (R5 — send it and let Nexus decide).
export function shipTosForTransmit(shipTos, services) {
    const mode = resolveDeliveryMode(services);
    if (mode === 'pickup' || mode === 'fulfillment')
        return null;
    const settled = reconcileDeliveryIntent(shipTos ?? [], services);
    if (settled.length < 2)
        return null;
    const account = thirdPartyAccountForTransmit(services);
    return settled.map((entry) => withThirdPartyAccount(entry, account));
}
// The carrier account every destination of this order ships on, or null. Pure
// over the stored snapshot (R26): only a Shipping order on `customer_labels`
// has one, so any other order's wire is unchanged.
export function thirdPartyAccountForTransmit(services) {
    if (resolveDeliveryMode(services) !== 'shipping')
        return null;
    if (services?.shipping_service !== 'customer_labels')
        return null;
    const account = services.third_party_account?.trim() ?? '';
    return account === '' ? null : account;
}
function withThirdPartyAccount(entry, account) {
    return account === null ? entry : { ...entry, [THIRD_PARTY_ACCOUNT_KEY]: account };
}
// The SINGLE `ship_to` field every feed has always carried: address one, delivery
// mode reconciled. The unit count is stripped — Nexus's `ship_to` object is an
// Address plus the delivery method and has no quantity slot, and the count is
// asked only of a split, which travels in `destinations` instead. Nothing is lost:
// a one-address order has no count to send.
//
// Only a SHIPPING order carries an address. An address the editor still holds
// after the customer switched away from Shipping is not a destination, and Nexus
// would file it as one (the Opportunity's address, the ship-to note). So Pickup
// crosses as the bare decision `{ delivery_method: 'pickup' }`, and a fulfilled or
// unanswered order sends no `ship_to` at all.
export function primaryShipToForTransmit(shipTos, services) {
    const mode = resolveDeliveryMode(services);
    if (mode === 'pickup')
        return { delivery_method: 'pickup' };
    if (mode !== 'shipping')
        return null;
    const first = reconcileDeliveryIntent(shipTos ?? [], services)[0];
    if (!first)
        return null;
    const { [SHIP_TO_QUANTITY_PROP]: _count, ...withoutCount } = first;
    return withThirdPartyAccount(withoutCount, thirdPartyAccountForTransmit(services));
}
// ── The printed proof sample: how many, and where they are mailed ────────
//
// Nexus feedback FB-0036: checking "a printed sample, mailed to you" opened
// nothing that asked how many samples or where to send them, so the
// physical_proof project crossed with a hardcoded quantity of 1 and no address
// anywhere on any feed.
//
// This is the ONE producer of both wire values, called by all three
// quote-carrying feeds (the pricing request's order_context, /quote-detail, and
// the submit body) — the same posture shipTosForTransmit holds for the order's
// destinations (R5/R7). Both values come back from ONE call so a feed cannot
// carry the count while dropping the addresses it counts.
//
// PROOF DESTINATIONS ARE THEIR OWN WIRE FIELD, never entries in `destinations`.
// Nexus turns each `destinations` entry into a `Print Order Shipping` line for
// the finished order; a proof sample is not that, and folding the two lists
// together would silently invoice proof postage as order shipping.
//
// Gated on the resolved proof intent: a quote that captured proof addresses and
// then dropped back to a digital-only proof emits neither field, because the
// physical_proof project it would describe is not being emitted either. Nothing
// is lost — the rows stay stored and cross again the moment a physical proof is
// re-selected.
//
// Entries cross VERBATIM, an unfinished one included — the same reasoning as
// shipTosForTransmit: pre-filtering here would renumber the list and could drop a
// complete later address (R5 — send it and let Nexus decide). NO postage, rate or
// proof charge is computed here or anywhere in the portal; Nexus prices the
// proof's own print + shipping from the count (ADR-010 §12).
export function proofDeliveryForTransmit(input) {
    if (input.proof_type !== 'physical')
        return { proof_quantity: null, proof_destinations: null };
    const list = (input.proof_ship_tos ?? []).map((s) => ({
        ...s,
        // A proof is mailed by definition, so a proof row is 'ship' whatever the
        // order's own delivery decision was — reconcileDeliveryIntent settles the
        // ORDER's mode and must never be handed this list.
        delivery_method: 'ship',
    }));
    return {
        proof_quantity: resolveProofQuantity(input.proof_quantity),
        proof_destinations: list.length > 0 ? list : null,
    };
}
// Project a saved address into a ShipTo the editor can hold. delivery_method
// is always 'ship' (you only pick a saved address when you're shipping); the
// address_id / label / is_default fields are dropped — they're picker-only
// metadata, not part of the persisted destination. The customer can still
// edit any field after picking (it just prefills).
export function shipToFromAddress(a) {
    const out = { delivery_method: 'ship' };
    const props = [
        'location_name', 'attention_name', 'street_line_1', 'street_line_2', 'street_line_3',
        'city', 'state_or_province', 'postal_code', 'country_iso2', 'location_phone',
    ];
    for (const p of props) {
        const v = a[p];
        if (typeof v === 'string' && v.length > 0)
            out[p] = v;
    }
    return out;
}
// Defensive normalizer for one untrusted address-book row (Nexus → portal).
// Returns null (dropped) when address_id is absent/blank — a saved address
// with no stable ref is useless to the picker. Narrows every other field to
// the allowlist, so nothing Nexus might add ever propagates.
export function normalizeCompanyShipToAddress(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const r = raw;
    if (typeof r.address_id !== 'string' || r.address_id.length === 0)
        return null;
    const out = {
        address_id: r.address_id,
        label: typeof r.label === 'string' && r.label.length > 0 ? r.label : null,
    };
    const props = [
        'location_name', 'attention_name', 'street_line_1', 'street_line_2', 'street_line_3',
        'city', 'state_or_province', 'postal_code', 'country_iso2', 'location_phone',
    ];
    for (const p of props) {
        const v = r[p];
        if (typeof v === 'string' && v.length > 0)
            out[p] = v;
    }
    if (r.is_default === true)
        out.is_default = true;
    return out;
}
// ── NEXUS SHIP-TO CONTRACT (field names match the Nexus Address table) ───
//
// Argo is one company; the portal↔Nexus ship-to calls are near-identical, so
// the ShipTo / CompanyShipToAddress field names above ARE the Nexus `Address`
// column names (location_name, attention_name, street_line_1..3, city,
// state_or_province, postal_code, location_phone). There is NO translation
// layer on either side —
// the portal sends/receives these keys verbatim, and Nexus's portalShipToService
// / portalAddressBookService read/return the same keys. COUNTRY is the one
// resolved field: the wire carries `country_iso2` (an iso2 CODE from Nexus's
// shared Country list — matching on a name string is brittle), which Nexus
// resolves to its Country FK. `delivery_method` is the one portal-level extra
// (ship vs pickup). There is NO company field on the ship-to — the customer's
// company is captured by the account flow (sign-up + submit identity prompt →
// Nexus lead-capture / provision-from-portal), not the shipping address.
//
//   1. POST /api/erp/quotes/ship-to  (persist)
//        body  { portal_quote_id, company_id, contact_id, ship_to: ShipTo }
//        auth  Argo S2S HMAC (X-Argo-Signature) — the SAME portal↔Nexus
//              secret as pricing; the portal resolves company_id/contact_id
//              SERVER-SIDE from the verified quote, never from the client.
//        resp  { address_id } (or null for pickup).
//      Idempotent on portal_quote_id (the portal may re-push). Nexus owns
//      address persistence AND the eventual weight + shipping-rate computation;
//      the portal STUBS those (estimate.ts) and performs ZERO shipping math.
//      Shipping cost stays OUT of total_sale_amount (ADR-010 §12); department
//      routing is Nexus-owned and UNCHANGED by capturing a ship-to.
//   2. POST /api/erp/companies/ship-to-addresses  (read, picker + manager)
//        body  { company_id }         auth  Argo S2S HMAC (as above)
//        resp  { addresses: CompanyShipToAddress[] } — customer-safe subset
//              ONLY (label + the Address fields + address_id + is_default). NO
//              internal fields (department, cost center, account codes); the
//              portal sanitizes to the allowlist at the boundary regardless.
//      The book is COMPANY-scoped, not contact-scoped: every user at a Company
//      shares one reusable address book (matching the company-wide quote
//      visibility model). Nexus scopes by company_id ONLY — no contact_id.
//      Read-only here. The portal NEVER caches the result (same no-caching
//      posture as identity/tier). A Nexus outage degrades to an empty list —
//      the customer just types the address by hand.
//   3. POST /api/erp/companies/ship-to-addresses/remove  (customer removal)
//        body  { company_id, address_id }   auth  Argo S2S HMAC (as above)
//        resp  { removed: true }
//      A customer may prune addresses from the Company's shared book. Nexus
//      MUST refuse to remove the Company DEFAULT (Address.priority ===
//      'primary') — only the ERP changes/removes the default — returning a
//      non-2xx the portal renders as "contact us to change the default." The
//      portal also guards this on the read-back before calling (defense in
//      depth), but Nexus is authoritative. Removal is company-scoped by
//      company_id; a matching address_id not on THIS company is a no-op/404.
//      (Write-back on endpoint 1 above likewise upserts a novel destination at
//      the COMPANY level now, not per contact — so the whole company can reuse
//      an address any one member ships to.)
// Paper stock catalog. Stock codes are industry-conventional weight + finish
// identifiers, and each one is the Nexus PaperStock.sku it prices from. Nexus
// resolves interior_paper and cover_paper through the same exact-sku lookup, so a
// stock keeps ONE code on every picker; a spelling of it for one question
// matches no stock.
//
// Two weight bases, each a ladder in weight order — not alphabetical (R25): a
// ladder read out of order hides the step the customer is looking for.
// TEXT_PAPERS holds the retired text stocks too, which the seed hides from the
// inside-pages picker with meta.deprecated because stored quotes still carry them.
export const TEXT_PAPERS = [
    '50_offset',
    '60_offset',
    '60_uncoated_text',
    '60_warm_white_text',
    '70_uncoated_text',
    '70_cougar_text',
    '80_uncoated_text',
    '80_gloss_text',
    '100_uncoated_text',
    '100_gloss_text',
    '100_matte_text',
    '20_bond',
    '24_bond',
    '32_writing',
];
export const COVER_STOCK_PAPERS = [
    '65_cover',
    '80_cover',
    '100_cover',
    '120_cover',
    '10pt_cover',
    '10pt_c2s',
    '12pt_c1s',
    '12pt_c2s',
    '14pt_cover',
    '14pt_c1s',
    '14pt_c2s',
];
// Every stock is offered for the inside pages and for the cover alike (FB-0255):
// a calendar runs one 10pt card through cover and pages, and plenty of jobs are
// one text stock throughout. Only the escape values differ by question:
// carbonless sets are inside-pages only, the retired self-cover is cover only.
export const INTERIOR_PAPERS = [
    ...TEXT_PAPERS,
    ...COVER_STOCK_PAPERS,
    'ncr_2_part',
    'ncr_3_part',
    'ncr_4_part',
    'other',
];
export const COVER_PAPERS = [...COVER_STOCK_PAPERS, ...TEXT_PAPERS, 'self_cover', 'other'];
// `10pt_cover` / `14pt_cover` read "card stock", never "cover": both pickers list
// them beside the 10pt / 14pt C1S and C2S boards, and "10pt cover" there read as
// a second name for 10pt C2S.
export const PAPER_LABELS = {
    '50_offset': '50# offset',
    '60_offset': '60# offset',
    '60_uncoated_text': '60# uncoated text',
    '60_warm_white_text': '60# warm white text',
    '70_uncoated_text': '70# uncoated text',
    '70_cougar_text': '70# Cougar text (premium uncoated)',
    '80_uncoated_text': '80# uncoated text',
    '80_gloss_text': '80# gloss text',
    '100_uncoated_text': '100# uncoated text',
    '100_gloss_text': '100# gloss text',
    '100_matte_text': '100# matte text',
    '20_bond': '20# bond',
    '24_bond': '24# bond',
    '32_writing': '32# writing',
    '65_cover': '65# cover',
    '80_cover': '80# cover',
    '100_cover': '100# cover',
    '120_cover': '120# cover',
    '10pt_cover': '10pt card stock',
    '10pt_c2s': '10pt C2S (coated both sides)',
    '12pt_c1s': '12pt C1S (coated one side)',
    '12pt_c2s': '12pt C2S (coated both sides)',
    '14pt_cover': '14pt card stock',
    '14pt_c1s': '14pt C1S (coated one side)',
    '14pt_c2s': '14pt C2S (coated both sides)',
    ncr_2_part: 'NCR: 2-part carbonless',
    ncr_3_part: 'NCR: 3-part carbonless',
    ncr_4_part: 'NCR: 4-part carbonless',
    self_cover: 'Self-cover (same as interior paper)',
    other: 'Other: describe in notes',
};
export const INSERT_PAPERS = [
    ...TEXT_PAPERS,
    ...COVER_STOCK_PAPERS,
    'other',
];
// ── Self-cover is offered ONLY for saddle-stitch binding ────────────────
//
// Self-cover (the cover printed on the SAME stock as the interior, as part of
// the same folded signature) is physically meaningful only for saddle stitch —
// the outermost folded sheet of the signature IS the cover. Other bindings
// can't be self-cover: a perfect-bound cover wraps the spine on heavier stock,
// a coil/wire-o cover is a separate sheet, and loose-leaf has no cover sheet at
// all. So self-cover is gated to `saddle_stitch_book` everywhere:
//   - the form (customer-form.ts): the `self_cover` toggle is shown ONLY for
//     saddle stitch. The `cover_paper` 'self_cover' option is RETIRED
//     (meta.deprecated) — it was a second control for the toggle's decision and
//     could contradict it — so the value now arrives only from a stored quote,
//     and this sanitizer is what still has to handle it;
//   - this sanitizer: a defensive backstop that strips any self-cover signal
//     reaching a non-saddle-stitch line's config — covers legacy rows, clones,
//     and direct API writes that the form gate can't reach.
//
// Two config representations carry "self-cover", and BOTH are stripped when the
// product isn't saddle stitch:
//   - self_cover: true            (the boolean toggle)
//   - cover_paper: 'self_cover'   (the cover-stock pick)
export const SELF_COVER_PRODUCT_TYPE = 'saddle_stitch_book';
// Reconcile a single line's self-cover signals. Returns the config UNTOUCHED
// when the two already agree — same "no-op unless needed" shape as the pricing
// service's stripFinishing. Tolerant of junk input (non-object → returned
// as-is); the configurator emits an object, but config is `unknown` at the API
// boundary. Two disagreements are possible and BOTH are settled here rather than
// at one caller, because four paths depend on it — save, clone, pricing, and the
// Nexus quote-detail feed:
//
//   - WRONG FAMILY: any self-cover signal on a binding that cannot self-cover.
//     Both keys are dropped.
//   - SIGNALS DISAGREE on a saddle-stitch line: the stock says self-cover while
//     the toggle says no. `cover_paper: 'self_cover'` IS a declaration of
//     self-cover, so the toggle is raised to match it. The picker no longer
//     offers that stock (it is meta.deprecated in the seed), but the option row
//     still exists for stored quotes and nothing stops a direct API write, so
//     the UI change alone would not close this.
export function stripInvalidSelfCover(config) {
    if (typeof config !== 'object' || config === null)
        return config;
    const record = config;
    // Saddle stitch is the one binding where self-cover is allowed. Nothing is
    // stripped there — but the two signals still have to agree.
    if (record.product_type === SELF_COVER_PRODUCT_TYPE) {
        if (record.cover_paper !== 'self_cover' || record.self_cover === true)
            return config;
        return { ...record, self_cover: true };
    }
    const hasToggle = 'self_cover' in record;
    const hasStock = record.cover_paper === 'self_cover';
    if (!hasToggle && !hasStock)
        return config;
    const out = {};
    for (const [k, v] of Object.entries(record)) {
        if (k === 'self_cover')
            continue; // toggle — drop entirely (matches the form scrub)
        if (k === 'cover_paper' && v === 'self_cover')
            continue; // invalid stock pick — drop
        out[k] = v;
    }
    return out;
}
// What each finish on a stocked sheet means, for the help panel. Every finish
// a sheet carries is defined, so none reads as the odd one out (R36).
export const LABEL_FINISH_DEFINITIONS = {
    gloss: { label: 'Gloss', help_text: 'Shiny coated face; colors pop.' },
    high_gloss: { label: 'High gloss', help_text: 'Extra-shiny, almost mirror-like.' },
    semi_gloss: { label: 'Semi-gloss', help_text: 'A soft sheen, between gloss and matte.' },
    matte: { label: 'Matte', help_text: 'Flat, no shine.' },
    matte_black_backed: { label: 'Matte, black-backed', help_text: 'Flat face with a dark backing that hides whatever the label covers.' },
    laser: { label: 'Laser', help_text: 'Made for laser printers: uncoated and paper-like, takes pen and pencil.' },
};
// The label sheets Argo stocks (PressSense), one option per stocked SKU — the
// Labels & Stickers size list is exactly this set plus the custom escape
// (FB-0134 / FB-0151). Each VALUE is the ERP material SKU lowercased with `_`
// for `-` and a leading 0 on a sub-inch dimension (LBL-LTR-RCT-.94X3-SG-REP →
// lbl_ltr_rct_0.94x3_sg_rep), so Nexus can tie a pick to the stocked material.
// The four `common` sheets are the stocked matches for the retired Avery
// 5160 / 5163 / 5161 / 5164 codes; `popularity` orders the rest by shape, then
// smallest → largest. Same-size sheets list the `common` one first, so artwork
// size detection lands on it.
const LABEL_STOCK_SHEETS = [
    { value: 'lbl_ltr_rct_1x2.63_gl_rep', label: '1″ × 2.625″, gloss', sublabel: 'Repositionable · 30 per sheet', group: 'rectangle', finish: 'gloss', popularity: 1, common: true, dimensions_in: { width: 1, height: 2.625 } },
    { value: 'lbl_ltr_rct_2x4_hg_prm', label: '2″ × 4″, high gloss', sublabel: '10 per sheet', group: 'rectangle', finish: 'high_gloss', popularity: 2, common: true, dimensions_in: { width: 2, height: 4 } },
    { value: 'lbl_ltr_rct_1x4_lz_prm', label: '1″ × 4″, laser', sublabel: '20 per sheet', group: 'rectangle', finish: 'laser', popularity: 3, common: true, dimensions_in: { width: 1, height: 4 } },
    { value: 'lbl_ltr_rct_3.33x4_lz_prm', label: '3.33″ × 4″, laser', sublabel: '6 per sheet', group: 'rectangle', finish: 'laser', popularity: 4, common: true, dimensions_in: { width: 3.333, height: 4 } },
    { value: 'lbl_ltr_rct_0.5x1_gl_prm', label: '0.5″ × 1″, gloss', sublabel: '144 per sheet', group: 'rectangle', finish: 'gloss', popularity: 10, common: false, dimensions_in: { width: 0.5, height: 1 } },
    { value: 'lbl_ltr_rct_0.5x1.75_gl_prm', label: '0.5″ × 1.75″, gloss', sublabel: '80 per sheet', group: 'rectangle', finish: 'gloss', popularity: 11, common: false, dimensions_in: { width: 0.5, height: 1.75 } },
    { value: 'lbl_ltr_rct_0.75x1.5_hg_prm', label: '0.75″ × 1.5″, high gloss', sublabel: '48 per sheet', group: 'rectangle', finish: 'high_gloss', popularity: 12, common: false, dimensions_in: { width: 0.75, height: 1.5 } },
    { value: 'lbl_ltr_rct_1x2_lz_prm', label: '1″ × 2″, laser', sublabel: '35 per sheet', group: 'rectangle', finish: 'laser', popularity: 13, common: false, dimensions_in: { width: 1, height: 2 } },
    { value: 'lbl_ltr_rct_0.94x3_sg_rep', label: '0.94″ × 3″, semi-gloss', sublabel: 'Repositionable · 20 per sheet', group: 'rectangle', finish: 'semi_gloss', popularity: 14, common: false, dimensions_in: { width: 0.9375, height: 3 } },
    { value: 'lbl_ltr_rct_1x3_lz_prm', label: '1″ × 3″, laser', sublabel: '21 per sheet', group: 'rectangle', finish: 'laser', popularity: 15, common: false, dimensions_in: { width: 1, height: 3 } },
    { value: 'lbl_ltr_rct_1.5x2.5_lz_prm', label: '1.5″ × 2.5″, laser', sublabel: '21 per sheet', group: 'rectangle', finish: 'laser', popularity: 16, common: false, dimensions_in: { width: 1.5, height: 2.5 } },
    { value: 'lbl_ltr_rct_1x4_hg_prm', label: '1″ × 4″, high gloss', sublabel: '20 per sheet', group: 'rectangle', finish: 'high_gloss', popularity: 17, common: false, dimensions_in: { width: 1, height: 4 } },
    { value: 'lbl_ltr_rct_1.5x3.5_lz_prm', label: '1.5″ × 3.5″, laser', sublabel: '15 per sheet', group: 'rectangle', finish: 'laser', popularity: 18, common: false, dimensions_in: { width: 1.5, height: 3.5 } },
    { value: 'lbl_ltr_rct_2x2.69_xx_prm', label: '2″ × 2.69″, laser', sublabel: '15 per sheet', group: 'rectangle', finish: 'laser', popularity: 19, common: false, dimensions_in: { width: 2, height: 2.6875 } },
    { value: 'lbl_ltr_rct_2x3_lz_prm', label: '2″ × 3″, laser', sublabel: '10 per sheet', group: 'rectangle', finish: 'laser', popularity: 20, common: false, dimensions_in: { width: 2, height: 3 } },
    { value: 'lbl_ltr_rct_2x3_gl_agr', label: '2″ × 3″, gloss', sublabel: 'Extra-strong adhesive · 10 per sheet', group: 'rectangle', finish: 'gloss', popularity: 21, common: false, dimensions_in: { width: 2, height: 3 } },
    { value: 'lbl_ltr_rct_2.5x4_lz_prm', label: '2.5″ × 4″, laser', sublabel: '8 per sheet', group: 'rectangle', finish: 'laser', popularity: 22, common: false, dimensions_in: { width: 2.5, height: 4 } },
    { value: 'lbl_ltr_rct_3.33x4_hg_prm', label: '3.33″ × 4″, high gloss', sublabel: '6 per sheet', group: 'rectangle', finish: 'high_gloss', popularity: 23, common: false, dimensions_in: { width: 3.333, height: 4 } },
    { value: 'lbl_ltr_rct_4x6_lz_prm', label: '4″ × 6″, laser', sublabel: '2 per sheet', group: 'rectangle', finish: 'laser', popularity: 24, common: false, dimensions_in: { width: 4, height: 6 } },
    { value: 'lbl_ltr_rct_4x6_hg_prm', label: '4″ × 6″, high gloss', sublabel: '2 per sheet', group: 'rectangle', finish: 'high_gloss', popularity: 25, common: false, dimensions_in: { width: 4, height: 6 } },
    { value: 'lbl_ltr_rct_5.5x8.5_lz_prm', label: '5.5″ × 8.5″, laser', sublabel: '2 per sheet', group: 'rectangle', finish: 'laser', popularity: 26, common: false, dimensions_in: { width: 5.5, height: 8.5 } },
    { value: 'lbl_ltr_ovl_1x2.5_hg_prm', label: '1″ × 2.5″ oval, high gloss', sublabel: '27 per sheet', group: 'oval', finish: 'high_gloss', popularity: 30, common: false, dimensions_in: { width: 1, height: 2.5 } },
    { value: 'lbl_ltr_ovl_1.75x2.5_mb_prm', label: '1.75″ × 2.5″ oval, matte, black-backed', sublabel: '15 per sheet', group: 'oval', finish: 'matte_black_backed', popularity: 31, common: false, dimensions_in: { width: 1.75, height: 2.5 } },
    { value: 'lbl_ltr_ovl_2x3_hg_prm', label: '2″ × 3″ oval, high gloss', sublabel: '10 per sheet', group: 'oval', finish: 'high_gloss', popularity: 32, common: false, dimensions_in: { width: 2, height: 3 } },
    { value: 'lbl_ltr_ovl_2x3_sg_rep', label: '2″ × 3″ oval, semi-gloss', sublabel: 'Repositionable · 10 per sheet', group: 'oval', finish: 'semi_gloss', popularity: 33, common: false, dimensions_in: { width: 2, height: 3 } },
    { value: 'lbl_ltr_cir_1_hg_prm', label: '1″ circle, high gloss', sublabel: '48 per sheet', group: 'circle', finish: 'high_gloss', popularity: 40, common: false, dimensions_in: { width: 1, height: 1 } },
    { value: 'lbl_ltr_cir_1.06_hg_prm', label: '1.06″ circle, high gloss', sublabel: '48 per sheet', group: 'circle', finish: 'high_gloss', popularity: 41, common: false, dimensions_in: { width: 1.0625, height: 1.0625 } },
    { value: 'lbl_ltr_cir_1.5_gl_prm', label: '1.5″ circle, gloss', sublabel: '30 per sheet', group: 'circle', finish: 'gloss', popularity: 42, common: false, dimensions_in: { width: 1.5, height: 1.5 } },
    { value: 'lbl_ltr_cir_2_xx_prm', label: '2″ circle, laser', sublabel: '20 per sheet', group: 'circle', finish: 'laser', popularity: 43, common: false, dimensions_in: { width: 2, height: 2 } },
    { value: 'lbl_ltr_cir_3_mt_prm', label: '3″ circle, matte', sublabel: '6 per sheet', group: 'circle', finish: 'matte', popularity: 44, common: false, dimensions_in: { width: 3, height: 3 } },
    { value: 'lbl_ltr_cir_3.33_hg_prm', label: '3.33″ circle, high gloss', sublabel: '6 per sheet', group: 'circle', finish: 'high_gloss', popularity: 45, common: false, dimensions_in: { width: 3.333, height: 3.333 } },
    { value: 'lbl_ltr_cir_4_hg_prm', label: '4″ circle, high gloss', sublabel: '4 per sheet', group: 'circle', finish: 'high_gloss', popularity: 46, common: false, dimensions_in: { width: 4, height: 4 } },
    { value: 'lbl_ltr_sqr_0.88_lz_prm', label: '0.875″ square, laser', sublabel: '88 per sheet', group: 'square', finish: 'laser', popularity: 50, common: false, dimensions_in: { width: 0.875, height: 0.875 } },
    { value: 'lbl_ltr_rct_8.5x11_sg_prm', label: '8.5″ × 11″ full sheet, semi-gloss', sublabel: '1 per sheet', group: 'full_sheet', finish: 'semi_gloss', popularity: 60, common: false, dimensions_in: { width: 8.5, height: 11 } },
    { value: 'lbl_tab_rct_11x17_gl_prm', label: '11″ × 17″ full sheet, gloss', sublabel: '1 per sheet', group: 'full_sheet', finish: 'gloss', popularity: 61, common: false, dimensions_in: { width: 11, height: 17 } },
];
export const LABEL_STOCK_SIZES = LABEL_STOCK_SHEETS.map((s) => ({
    ...s,
    applies_to: ['labels'],
}));
export const FINISHED_SIZES = [
    // Single sheet (flat-print) standards
    { value: '8.5x11', label: '8.5 × 11', sublabel: 'Letter', applies_to: ['single_sheet'], popularity: 1, common: true },
    { value: '5x7', label: '5 × 7', applies_to: ['single_sheet'], popularity: 2, common: true },
    { value: 'a4', label: 'A4', sublabel: '8.27 × 11.69', applies_to: ['single_sheet'], popularity: 3, common: true },
    { value: '4x6', label: '4 × 6', sublabel: 'Postcard', applies_to: ['single_sheet'], popularity: 4, common: true },
    { value: '3.5x5', label: '3.5 × 5', applies_to: ['single_sheet'], popularity: 5, common: true },
    { value: '3.5x2', label: '3.5 × 2', sublabel: 'Business card', applies_to: ['single_sheet'], popularity: 6, common: true },
    { value: '11x17', label: '11 × 17', sublabel: 'Tabloid', applies_to: ['single_sheet'], popularity: 20, common: false },
    { value: '5.5x8.5', label: '5.5 × 8.5', sublabel: 'Half-letter', applies_to: ['single_sheet'], popularity: 21, common: false },
    // Book / bound families
    { value: 'book_8.5x11', label: '8.5 × 11', sublabel: 'Letter', applies_to: ['saddle_stitch_book', 'coil_or_wire_o_book', 'loose_leaf'], popularity: 1, common: true },
    // v2 cleanup: print-trade jargon ("Digest" / "Trade") removed from these
    // sublabels. VALUE strings are unchanged — Nexus prices on the value, and
    // labels may differ per surface.
    { value: 'book_5.5x8.5', label: '5.5 × 8.5', applies_to: ['saddle_stitch_book', 'perfect_bound_book', 'coil_or_wire_o_book', 'loose_leaf'], popularity: 1, common: true },
    { value: 'book_6x9', label: '6 × 9', applies_to: ['saddle_stitch_book', 'perfect_bound_book', 'coil_or_wire_o_book', 'loose_leaf'], popularity: 2, common: true },
    { value: 'book_7x10', label: '7 × 10', applies_to: ['saddle_stitch_book', 'perfect_bound_book', 'coil_or_wire_o_book', 'loose_leaf'], popularity: 3, common: true },
    { value: 'pb_8.5x11', label: '8.5 × 11', sublabel: 'Letter', applies_to: ['perfect_bound_book'], popularity: 4, common: true },
    // Labels: the stocked sheets. The Avery codes and generic sizes before them
    // are retired, kept for quotes that already carry them.
    ...LABEL_STOCK_SIZES,
    { value: 'avery_5160', label: 'Avery 5160', sublabel: '1 × 2.625 · address', applies_to: ['labels'], popularity: 101, common: false, deprecated: true, dimensions_in: { width: 1, height: 2.625 } },
    { value: 'avery_5163', label: 'Avery 5163', sublabel: '2 × 4 · shipping', applies_to: ['labels'], popularity: 102, common: false, deprecated: true, dimensions_in: { width: 2, height: 4 } },
    { value: 'avery_5161', label: 'Avery 5161', sublabel: '1 × 4 · address', applies_to: ['labels'], popularity: 103, common: false, deprecated: true, dimensions_in: { width: 1, height: 4 } },
    { value: 'avery_5164', label: 'Avery 5164', sublabel: '3.33 × 4 · shipping', applies_to: ['labels'], popularity: 104, common: false, deprecated: true, dimensions_in: { width: 3.33, height: 4 } },
    { value: 'label_2x3', label: '2 × 3', applies_to: ['labels'], popularity: 105, common: false, deprecated: true, dimensions_in: { width: 2, height: 3 } },
    { value: 'label_3x5', label: '3 × 5', applies_to: ['labels'], popularity: 106, common: false, deprecated: true, dimensions_in: { width: 3, height: 5 } },
    { value: 'label_2x2', label: '2 × 2', applies_to: ['labels'], popularity: 107, common: false, deprecated: true, dimensions_in: { width: 2, height: 2 } },
    // Universal custom escape — applies to every printed type
    {
        value: 'custom',
        label: 'Custom size: tell us in notes',
        applies_to: ['single_sheet', 'labels', 'saddle_stitch_book', 'perfect_bound_book', 'coil_or_wire_o_book', 'loose_leaf'],
        popularity: 999,
        common: false,
        is_other: true,
    },
];
// ── Required attributes (per product type) ─────────────────────────────
//
// Mirrors Nexus's REQUIRED_KEYS but typed per product. Empty array means
// "no hard requirement at the engine level" (the engine will still need
// SOMETHING to price, but no single attribute is mandatory).
//
// NOTE: interior_color_page_count is intentionally ABSENT here even though the
// book families carry it in PRODUCT_TYPE_ATTRIBUTES. It is CONDITIONALLY
// required — only when interior_ink = 'mixed'. That conditionality lives in
// the served schema (the seed field's required + conditional_show, enforced by
// the shared lineConfigIssues validator), not in this coarse unconditional set.
// Nexus mirrors the same conditional rule on its side (require the count only
// when interior_ink resolves to 'mixed').
//
// cover_finish (FB-0074) is absent for the same reason: it became required in
// the served schema, but only WHERE A LAMINATABLE COVER EXISTS — not on a
// self_cover book and not on a cover too thin to hold a laminate film (any text
// stock, or 65# cover at ~9pt). Nexus holds cover_finish optional; an
// unconditional requirement here would reject a self-cover book the portal
// never asks.
export const REQUIRED_ATTRIBUTES = {
    single_sheet: ['finished_size', 'print_ink', 'interior_paper'],
    labels: ['finished_size', 'print_ink'],
    // Book families price on the cover/interior split (PrintQuoteV1 v3).
    saddle_stitch_book: ['cover_finished_size', 'interior_finished_size', 'page_count', 'cover_ink', 'interior_ink', 'interior_paper'],
    perfect_bound_book: ['cover_finished_size', 'interior_finished_size', 'page_count', 'cover_ink', 'interior_ink', 'interior_paper'],
    // Hard-bound mirrors the paperback's required set exactly (same cover/interior
    // split); Nexus prices it as its own Service, differing only in lead time.
    hard_bound_book: ['cover_finished_size', 'interior_finished_size', 'page_count', 'cover_ink', 'interior_ink', 'interior_paper'],
    coil_or_wire_o_book: ['cover_finished_size', 'interior_finished_size', 'page_count', 'cover_ink', 'interior_ink', 'interior_paper', 'binding'],
    loose_leaf: ['interior_finished_size', 'page_count', 'interior_ink', 'interior_paper'],
    media_replication: ['media_type'],
    custom_packaging: ['custom_packaging_source', 'custom_packaging_dimensions'],
    // No print attribute is mandatory for either: an assembly item prices off the
    // kit snapshot, and `something_else` is a hand-off to sales that captures
    // nothing to require.
    assembly: [],
    something_else: [],
};
//# sourceMappingURL=vocabulary.js.map